import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { formatDateTimePreserveTimezone } from '../../utils/dateTimeUtils';
import { Car, X, MapPin, Users, Clock, CheckCircle, AlertCircle, ArrowRight, Loader2, Search, Check, UserPlus } from 'lucide-react';
import {
  initializeUrbanrideApi,
  isConfigured,
  getConfigStatus,
  provisionApiKey,
  isAutoProvisionAvailable,
  searchLocations,
  searchPassengers,
  searchRailroads,
  getRates,
  bookTrip,
  updateTrip,
  createAirportLocation,
  createAddressLocation,
  createAsDirectedLocation,
  createFboLocation,
  createTrainStationLocation,
  createPassenger,
  getDistanceMatrix,
  roundUpToNearest15Minutes,
  calculateSuggestedPickupTime,
  validateFlight,
  TITLES
} from '../../services/urbanrideService';
import { parseFlightNumber } from '../../utils/airlineUtils';

/**
 * UrbanrideQuickBooking - Quick booking modal for Timeline 2.0 component selection
 *
 * This component handles the chauffeur service booking flow when users select
 * multiple components from the timeline and want to book transportation between them.
 */
const UrbanrideQuickBooking = ({
  selectedComponents,
  tripId,
  tripExecutives = [],
  allExecutives = [],
  onSuccess,
  onCancel,
  onUpdateComponents,
  // Edit mode props
  editMode = false,
  existingComponentId = null,   // ID of the chauffeur_service component to update
  existingUrcn = null,          // URCN of the existing Urbanride reservation
  existingComponentData = null, // component_data of the existing chauffeur_service component
  // Side panel mode props (for quickedit split-screen)
  sidePanel = false,
  incomingTimelineComponent = null,  // Timeline component clicked by user
  onClearIncomingComponent = null    // Callback to clear the incoming component after processing
}) => {
  /**
   * Format datetime for display without converting to browser timezone.
   * Extracts date/time components directly from ISO string to preserve original timezone.
   * Example: "2026-02-09T05:45:00" displays as "Mon, Feb 9, 2026 05:45" regardless of browser TZ.
   */
  const formatDisplayDateTime = (isoString) => {
    if (!isoString) return '';
    try {
      // Extract components directly from the ISO string to avoid timezone conversion
      const match = String(isoString).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
      if (!match) {
        // Fallback to date-fns format if not a standard ISO string
        return format(new Date(isoString), 'EEE, MMM d, yyyy HH:mm');
      }

      const [, year, month, day, hours, minutes] = match;

      // Create a date object just to get the day of week (use noon to avoid DST issues)
      const dateForDow = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0);
      const dayOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dateForDow.getDay()];
      const monthName = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][parseInt(month) - 1];

      return `${dayOfWeek}, ${monthName} ${parseInt(day)}, ${year} ${hours}:${minutes}`;
    } catch {
      return isoString;
    }
  };

  // API Configuration state
  const [apiConfigured, setApiConfigured] = useState(false);
  const [apiMode, setApiMode] = useState('test');
  const [configLoading, setConfigLoading] = useState(true);
  const [autoProvisionAvailable, setAutoProvisionAvailable] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [provisionError, setProvisionError] = useState('');

  // Form step state
  const [step, setStep] = useState(1); // 1: Review, 2: Select Vehicle, 3: Confirmation

  // Processed location data
  const [processedLocations, setProcessedLocations] = useState([]);
  const [pickupLocation, setPickupLocation] = useState(null);
  const [dropoffLocation, setDropoffLocation] = useState(null);
  const [stops, setStops] = useState([]);
  const [passengers, setPassengers] = useState([]);

  // Rates and booking state
  const [rates, setRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [addingToTrip, setAddingToTrip] = useState(false);

  // Error state
  const [error, setError] = useState('');

  // Comments
  const [clientComments, setClientComments] = useState('');
  const [pickupComments, setPickupComments] = useState('');
  const [dropoffComments, setDropoffComments] = useState('');

  // Custom pickup time for hotel pickups (user should be prompted to select, not use checkout time)
  const [customPickupTime, setCustomPickupTime] = useState('');
  const [showPickupTimePrompt, setShowPickupTimePrompt] = useState(false);

  // Travel time calculation for smart pickup time suggestion
  const [estimatedTravelTime, setEstimatedTravelTime] = useState(null);
  const [calculatingTravelTime, setCalculatingTravelTime] = useState(false);
  const [travelTimeError, setTravelTimeError] = useState('');

  // Flight info prompt for manual airport/FBO dropoff
  const [showFlightInfoPrompt, setShowFlightInfoPrompt] = useState(false);
  const [isDropoffFbo, setIsDropoffFbo] = useState(false);  // Track if dropoff is FBO
  const [manualAirportFlightInfo, setManualAirportFlightInfo] = useState({
    airlineCode: '',
    flightNumber: '',
    departureDate: '',
    departureTimeOfDay: '',
    otherAirport: '',  // Destination airport (populated from validation)
    tailNumber: ''     // For FBO dropoffs
  });
  // Flight validation state
  const [flightValidating, setFlightValidating] = useState(false);
  const [flightValidated, setFlightValidated] = useState(false);
  const [flightValidationError, setFlightValidationError] = useState('');
  const [departureValidatedFlight, setDepartureValidatedFlight] = useState(null);

  // Arrival flight info prompt for manual airport/FBO pickup
  const [showArrivalFlightPrompt, setShowArrivalFlightPrompt] = useState(false);
  const [isPickupFbo, setIsPickupFbo] = useState(false);  // Track if pickup is FBO
  const [arrivalFlightInfo, setArrivalFlightInfo] = useState({
    airlineCode: '',
    flightNumber: '',
    arrivalDate: '',
    arrivalTimeOfDay: '',
    originAirport: '',  // Origin airport (populated from validation)
    tailNumber: ''      // For FBO pickups
  });
  // Arrival flight validation state
  const [arrivalFlightValidating, setArrivalFlightValidating] = useState(false);
  const [arrivalFlightValidated, setArrivalFlightValidated] = useState(false);
  const [arrivalFlightValidationError, setArrivalFlightValidationError] = useState('');
  const [arrivalValidatedFlight, setArrivalValidatedFlight] = useState(null);
  // Pickup FBO selection state
  const [pickupFboList, setPickupFboList] = useState([]);
  const [pickupFboListLoading, setPickupFboListLoading] = useState(false);
  const [pickupFboListError, setPickupFboListError] = useState('');
  const [selectedPickupFbo, setSelectedPickupFbo] = useState(null);
  const [pickupFboAirportCode, setPickupFboAirportCode] = useState(null);
  // FBO selection state
  const [fboList, setFboList] = useState([]);
  const [fboListLoading, setFboListLoading] = useState(false);
  const [fboListError, setFboListError] = useState('');
  const [selectedFbo, setSelectedFbo] = useState(null);
  const [fboAirportCode, setFboAirportCode] = useState(null);  // Track which airport we loaded FBOs for

  // Train station dropoff state
  const [isDropoffTrainStation, setIsDropoffTrainStation] = useState(false);
  const [trainStationInfo, setTrainStationInfo] = useState({
    railroad: '',
    railroadName: '',
    trainNumber: '',
    departureDate: '',
    departureTime: ''
  });
  const [railroadSearchQuery, setRailroadSearchQuery] = useState('');
  const [railroadSearchResults, setRailroadSearchResults] = useState([]);
  const [railroadSearching, setRailroadSearching] = useState(false);
  const [showRailroadDropdown, setShowRailroadDropdown] = useState(false);

  // Edit-mode location search state
  const [pickupSearchQuery, setPickupSearchQuery] = useState('');
  const [pickupSearchResults, setPickupSearchResults] = useState([]);
  const [pickupSearchLoading, setPickupSearchLoading] = useState(false);
  const [showPickupResults, setShowPickupResults] = useState(false);
  const [dropoffSearchQuery, setDropoffSearchQuery] = useState('');
  const [dropoffSearchResults, setDropoffSearchResults] = useState([]);
  const [dropoffSearchLoading, setDropoffSearchLoading] = useState(false);
  const [showDropoffResults, setShowDropoffResults] = useState(false);
  const pickupSearchTimeout = React.useRef(null);
  const dropoffSearchTimeout = React.useRef(null);

  // Timeline component placement state (for side panel mode)
  const [pendingComponent, setPendingComponent] = useState(null);
  const [linkedComponentIds, setLinkedComponentIds] = useState([]);

  // Add passenger form state
  const [showAddPassengerForm, setShowAddPassengerForm] = useState(false);
  const [newPassenger, setNewPassenger] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    extra: 0
  });
  // Passenger lookup state
  const [passengerLookupState, setPassengerLookupState] = useState('idle'); // idle, searching, found_executive, found_urbanride, not_found
  const [matchedExecutive, setMatchedExecutive] = useState(null);
  const [urbanrideSearchResults, setUrbanrideSearchResults] = useState([]);
  const [selectedUrbanrideProfile, setSelectedUrbanrideProfile] = useState(null);
  const [savingToExecutive, setSavingToExecutive] = useState(false);

  // Initialize API on mount
  useEffect(() => {
    initApi();
  }, []);

  // Process selected components when they change (new booking mode only)
  useEffect(() => {
    if (!editMode && selectedComponents && selectedComponents.length > 0) {
      processSelectedComponents();
    }
  }, [selectedComponents]);

  // In edit mode, pre-populate form state from existing component data
  useEffect(() => {
    if (editMode && existingComponentData) {
      loadExistingBooking(existingComponentData);
    }
  }, [editMode, existingComponentData]);

  // Handle incoming timeline component (side panel mode)
  useEffect(() => {
    if (incomingTimelineComponent) {
      const locData = getLocationFromComponent(incomingTimelineComponent);
      const componentId = incomingTimelineComponent.originalId || incomingTimelineComponent.id;
      setPendingComponent({
        component: incomingTimelineComponent,
        componentId,
        locData,
        name: locData.location?.name || locData.arrivalLocation?.name || locData.departureLocation?.name || incomingTimelineComponent.title || 'Location',
        isAirport: locData.isAirport
      });
      if (onClearIncomingComponent) onClearIncomingComponent();
    }
  }, [incomingTimelineComponent]);

  // Recalculate pickup time when manual airport flight departure time changes
  useEffect(() => {
    const { departureDate, departureTimeOfDay } = manualAirportFlightInfo;
    if (showFlightInfoPrompt && departureDate && departureTimeOfDay && pickupLocation && dropoffLocation) {
      // Combine date and time into datetime string
      const departureDateTime = `${departureDate}T${departureTimeOfDay}`;
      // Create a modified dropoff with the manually entered departure time
      const modifiedDropoff = {
        ...dropoffLocation,
        datetime: departureDateTime
      };
      // Recalculate smart pickup time with the updated departure time
      calculateSmartPickupTime(pickupLocation, modifiedDropoff, stops);
    }
  }, [manualAirportFlightInfo.departureDate, manualAirportFlightInfo.departureTimeOfDay]);

  const initApi = async () => {
    setConfigLoading(true);
    setProvisionError('');
    const result = await initializeUrbanrideApi();
    setApiConfigured(result.configured);
    setApiMode(result.mode);
    setAutoProvisionAvailable(result.autoProvisionAvailable);
    setConfigLoading(false);
  };

  /**
   * Pre-populate form state from an existing chauffeur_service component_data.
   * Used when opening in edit mode to allow changes to an existing reservation.
   */
  const loadExistingBooking = (cd) => {
    if (!cd) return;

    // Build pickup location object from stored data
    const pickup = {
      isAirport: cd.pickup_type === 'airport',
      isHotel: cd.pickup_type === 'hotel',
      isMeeting: cd.pickup_type === 'meeting',
      name: cd.pickup_location || 'Pickup',
      details: cd.pickup_address || cd.pickup_location || '',
      placeId: cd.pickup_place_id || null,
      datetime: cd.pickup_datetime || null,
      // Airport-specific fields
      ...(cd.pickup_type === 'airport' && {
        code: cd.pickup_airport_code || '',
        airlineCode: cd.pickup_airline_code || '',
        flightNumber: cd.pickup_flight_number || '',
        otherAirport: cd.pickup_origin_airport || ''
      })
    };
    setPickupLocation(pickup);
    setPickupSearchQuery(cd.pickup_location || '');

    // Build dropoff location object from stored data
    const dropoff = {
      isAirport: cd.dropoff_type === 'airport',
      isHotel: cd.dropoff_type === 'hotel',
      isMeeting: cd.dropoff_type === 'meeting',
      isAsDirected: cd.dropoff_type === 'as_directed',
      name: cd.dropoff_location || 'Dropoff',
      details: cd.dropoff_address || cd.dropoff_location || '',
      placeId: cd.dropoff_place_id || null,
      datetime: cd.dropoff_datetime || null,
      // Airport-specific fields
      ...(cd.dropoff_type === 'airport' && {
        code: cd.dropoff_airport_code || '',
        airlineCode: cd.dropoff_airline_code || '',
        flightNumber: cd.dropoff_flight_number || '',
        otherAirport: cd.dropoff_destination_airport || ''
      })
    };
    setDropoffLocation(dropoff);
    setDropoffSearchQuery(cd.dropoff_type === 'as_directed' ? 'As Directed' : (cd.dropoff_location || ''));

    // Build stops from stored data
    const existingStops = (cd.stops || []).map(s => ({
      isAirport: s.type === 'airport',
      isHotel: s.type === 'hotel',
      isAsDirected: s.type === 'as_directed',
      comments: s.comments || '',
      location: {
        name: s.location || 'Stop',
        details: s.location_address || s.location || '',
        placeId: s.location_id || null,
        isAirport: s.type === 'airport',
        code: s.airport_code || '',
        airlineCode: s.airline_code || '',
        flightNumber: s.flight_number || ''
      },
      _searchQuery: s.location || '',
      _searchResults: [],
      _showResults: false,
      _searchLoading: false
    }));
    setStops(existingStops);

    // Build passengers from stored data — split name into firstName/lastName
    const existingPassengers = (cd.passengers || []).map(p => {
      const nameParts = (p.name || '').trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      return {
        firstName,
        lastName,
        name: p.name || '',
        email: p.email || '',
        mobile: p.phone || '',
        vip: p.vip || false,
        isExecutive: p.isExecutive || false,
        executiveId: p.executiveId || null,
        extra: p.extra || 0
      };
    });
    setPassengers(existingPassengers);

    // Pre-fill custom pickup time if stored
    if (cd.pickup_datetime) {
      setCustomPickupTime(cd.pickup_datetime);
    }

    // Pre-fill client comments
    if (cd.notes) {
      setClientComments(cd.notes);
    }
    // Pre-fill location comments
    if (cd.pickup_comments) {
      setPickupComments(cd.pickup_comments);
    }
    if (cd.dropoff_comments) {
      setDropoffComments(cd.dropoff_comments);
    }
    // Initialize linked component IDs for reference tracking
    if (cd.linked_component_ids) {
      setLinkedComponentIds(cd.linked_component_ids);
    }
  };

  // Handle auto-provisioning of API key
  const handleProvisionApiKey = async () => {
    setProvisioning(true);
    setProvisionError('');
    try {
      const result = await provisionApiKey();
      if (result.status === 'provisioned' || result.status === 'already_configured') {
        // Reinitialize to update the state
        await initApi();
      } else {
        setProvisionError(result.message || 'Failed to provision API key');
      }
    } catch (err) {
      setProvisionError(err.message || 'Failed to provision API key');
    } finally {
      setProvisioning(false);
    }
  };

  /**
   * Search for FBOs at a given airport using Urbanride API with airport filter
   * @param {string} airportCode - The airport IATA or ICAO code to search FBOs for
   */
  const searchFbosAtAirport = async (airportCode) => {
    console.log('[UrbanrideQuickBooking] searchFbosAtAirport called with:', airportCode);
    if (!airportCode) return;

    // Don't search again if an FBO is already selected
    // The user has already chosen an FBO - no need to reload the list
    if (selectedFbo) {
      console.log('[UrbanrideQuickBooking] FBO already selected, skipping search:', selectedFbo.name);
      return;
    }

    // Validate airport code - must be 3-4 alphabetic characters (IATA or ICAO)
    // FBO IDs are numeric, so reject those
    const isValidAirportCode = /^[A-Z]{3,4}$/i.test(airportCode);
    if (!isValidAirportCode) {
      console.log('[UrbanrideQuickBooking] Invalid airport code (not alphabetic 3-4 chars), skipping:', airportCode);
      return;
    }

    // Don't reload if we already have FBOs for this airport
    if (fboAirportCode === airportCode && fboList.length > 0) {
      console.log('[UrbanrideQuickBooking] Already have FBOs for this airport, skipping reload');
      return;
    }

    setFboListLoading(true);
    setFboListError('');
    setFboList([]);
    setSelectedFbo(null);

    try {
      // Extract IATA code from ICAO (e.g., KTEB -> TEB) for Urbanride API
      // Urbanride expects IATA codes (3 chars) for the airport parameter
      let iataCode = airportCode;
      if (airportCode.length === 4 && airportCode.startsWith('K')) {
        iataCode = airportCode.substring(1); // Remove 'K' prefix for US airports
      }

      // Use Urbanride API with airport filter - always empty input
      // API format: { input: "", type: "fbo", airport: "TEB" }
      const searchQuery = {
        input: '',
        type: 'fbo',
        airport: iataCode
      };

      console.log('[UrbanrideQuickBooking] Calling Urbanride searchLocations with:', searchQuery);
      const result = await searchLocations(searchQuery);
      console.log('[UrbanrideQuickBooking] Urbanride response:', result);

      // Map response to FBO format - preserve the airport code
      const fbos = (result.locations || []).map(fbo => ({
        id: fbo.id,
        name: fbo.name,
        details: fbo.details,
        type: 'fbo',
        airportCode: airportCode  // Preserve the original airport code
      }));

      setFboList(fbos);
      setFboAirportCode(airportCode);  // Track which airport we loaded FBOs for

      if (fbos.length === 0) {
        setFboListError(`No FBOs found at ${airportCode}`);
      }
    } catch (err) {
      console.error('Error searching for FBOs:', err);
      setFboListError(err.message || 'Failed to search for FBOs');
    } finally {
      setFboListLoading(false);
    }
  };

  /**
   * Search for FBOs at a given airport for pickup locations
   * @param {string} airportCode - The airport IATA or ICAO code
   */
  const searchPickupFbosAtAirport = async (airportCode) => {
    console.log('[UrbanrideQuickBooking] searchPickupFbosAtAirport called with:', airportCode);
    if (!airportCode) return;

    // Don't search again if an FBO is already selected
    if (selectedPickupFbo) {
      console.log('[UrbanrideQuickBooking] Pickup FBO already selected, skipping search:', selectedPickupFbo.name);
      return;
    }

    // Validate airport code - must be 3-4 alphabetic characters (IATA or ICAO)
    const isValidAirportCode = /^[A-Z]{3,4}$/i.test(airportCode);
    if (!isValidAirportCode) {
      console.log('[UrbanrideQuickBooking] Invalid airport code (not alphabetic 3-4 chars), skipping:', airportCode);
      return;
    }

    // Don't reload if we already have FBOs for this airport
    if (pickupFboAirportCode === airportCode && pickupFboList.length > 0) {
      console.log('[UrbanrideQuickBooking] Already have pickup FBOs for this airport, skipping reload');
      return;
    }

    setPickupFboListLoading(true);
    setPickupFboListError('');
    setPickupFboList([]);
    setSelectedPickupFbo(null);

    try {
      // Extract IATA code from ICAO (e.g., KTEB -> TEB) for Urbanride API
      let iataCode = airportCode;
      if (airportCode.length === 4 && airportCode.startsWith('K')) {
        iataCode = airportCode.substring(1);
      }

      const searchQuery = {
        input: '',
        type: 'fbo',
        airport: iataCode
      };

      console.log('[UrbanrideQuickBooking] Calling Urbanride searchLocations for pickup FBOs:', searchQuery);
      const result = await searchLocations(searchQuery);
      console.log('[UrbanrideQuickBooking] Urbanride pickup FBO response:', result);

      const fbos = (result.locations || []).map(fbo => ({
        id: fbo.id,
        name: fbo.name,
        details: fbo.details,
        type: 'fbo',
        airportCode: airportCode
      }));

      setPickupFboList(fbos);
      setPickupFboAirportCode(airportCode);

      if (fbos.length === 0) {
        setPickupFboListError(`No FBOs found at ${airportCode}`);
      }
    } catch (err) {
      console.error('Error searching for pickup FBOs:', err);
      setPickupFboListError(err.message || 'Failed to search for FBOs');
    } finally {
      setPickupFboListLoading(false);
    }
  };

  // Edit-mode location search handlers
  const handleEditPickupSearch = useCallback((query) => {
    setPickupSearchQuery(query);
    if (pickupSearchTimeout.current) clearTimeout(pickupSearchTimeout.current);
    if (!query || query.length < 2) {
      setPickupSearchResults([]);
      setShowPickupResults(false);
      return;
    }
    setPickupSearchLoading(true);
    pickupSearchTimeout.current = setTimeout(async () => {
      try {
        const result = await searchLocations({ input: query });
        setPickupSearchResults(result.locations || []);
        setShowPickupResults(true);
      } catch (err) {
        console.error('Pickup search error:', err);
      } finally {
        setPickupSearchLoading(false);
      }
    }, 300);
  }, []);

  const selectEditPickupLocation = (loc) => {
    setPickupLocation({
      name: loc.name,
      details: loc.details || loc.name,
      placeId: loc.placeId || loc.id || '',
      isAirport: loc.type === 'airport',
      code: loc.type === 'airport' ? loc.id : '',
      type: loc.type
    });
    setPickupSearchQuery(`${loc.type === 'airport' && loc.id ? loc.id + ' - ' : ''}${loc.name}`);
    setShowPickupResults(false);
    setPickupSearchResults([]);
  };

  const handleEditDropoffSearch = useCallback((query) => {
    setDropoffSearchQuery(query);
    if (dropoffSearchTimeout.current) clearTimeout(dropoffSearchTimeout.current);
    if (!query || query.length < 2) {
      setDropoffSearchResults([]);
      setShowDropoffResults(false);
      return;
    }
    setDropoffSearchLoading(true);
    dropoffSearchTimeout.current = setTimeout(async () => {
      try {
        const result = await searchLocations({ input: query });
        setDropoffSearchResults(result.locations || []);
        setShowDropoffResults(true);
      } catch (err) {
        console.error('Dropoff search error:', err);
      } finally {
        setDropoffSearchLoading(false);
      }
    }, 300);
  }, []);

  const selectEditDropoffLocation = (loc) => {
    const isAsDirected = loc.type === 'as_directed' || loc.id === 'as_directed';
    setDropoffLocation({
      name: isAsDirected ? 'As Directed' : loc.name,
      details: isAsDirected ? 'Passenger/s will direct to final destination.' : (loc.details || loc.name),
      placeId: isAsDirected ? '' : (loc.placeId || loc.id || ''),
      isAirport: loc.type === 'airport',
      isAsDirected: isAsDirected,
      code: loc.type === 'airport' ? loc.id : '',
      type: loc.type
    });
    setDropoffSearchQuery(isAsDirected ? 'As Directed' : `${loc.type === 'airport' && loc.id ? loc.id + ' - ' : ''}${loc.name}`);
    setShowDropoffResults(false);
    setDropoffSearchResults([]);
  };

  const handleEditStopSearch = useCallback((index, query) => {
    const updated = [...stops];
    if (!query || query.length < 2) {
      updated[index] = { ...updated[index], _searchQuery: query, _searchResults: [], _showResults: false, _searchLoading: false };
      setStops(updated);
      return;
    }
    updated[index] = { ...updated[index], _searchQuery: query, _searchLoading: true };
    setStops(updated);
    setTimeout(async () => {
      try {
        const result = await searchLocations({ input: query });
        setStops(prev => {
          const u = [...prev];
          if (u[index]) {
            u[index] = { ...u[index], _searchResults: result.locations || [], _showResults: true, _searchLoading: false };
          }
          return u;
        });
      } catch (err) {
        setStops(prev => {
          const u = [...prev];
          if (u[index]) u[index] = { ...u[index], _searchLoading: false };
          return u;
        });
      }
    }, 300);
  }, [stops]);

  const selectEditStopLocation = (index, loc) => {
    const isAsDirected = loc.type === 'as_directed' || loc.id === 'as_directed';
    const updated = [...stops];
    updated[index] = {
      ...updated[index],
      isAirport: loc.type === 'airport',
      isAsDirected: isAsDirected,
      location: {
        name: isAsDirected ? 'As Directed' : loc.name,
        details: isAsDirected ? 'Passenger/s will direct to one or more stops.' : (loc.details || loc.name),
        placeId: isAsDirected ? '' : (loc.placeId || loc.id || ''),
        isAirport: loc.type === 'airport',
        code: loc.type === 'airport' ? loc.id : '',
        type: loc.type
      },
      _searchQuery: isAsDirected ? 'As Directed' : `${loc.type === 'airport' && loc.id ? loc.id + ' - ' : ''}${loc.name}`,
      _showResults: false,
      _searchResults: []
    };
    setStops(updated);
  };

  const addEditStop = () => {
    setStops([...stops, {
      isAirport: false,
      isAsDirected: false,
      comments: '',
      location: null,
      _searchQuery: '',
      _searchResults: [],
      _showResults: false,
      _searchLoading: false
    }]);
  };

  const removeEditStop = (index) => {
    setStops(stops.filter((_, i) => i !== index));
  };

  // Timeline component placement handlers (for side panel mode)
  const placePendingAsPickup = () => {
    if (!pendingComponent) return;
    const { locData, componentId } = pendingComponent;
    // For airports, use arrivalLocation (passenger being picked up from arrival)
    const loc = locData.isAirport ? locData.arrivalLocation : locData.location;
    if (loc) {
      setPickupLocation({
        ...loc,
        isAirport: locData.isAirport,
        isHotel: locData.isHotel,
        isMeeting: locData.isMeeting,
        isEvent: locData.isEvent,
        isRestaurant: locData.isRestaurant,
        datetime: loc.datetime || locData.datetime
      });
      setPickupSearchQuery(loc.code ? `${loc.code} - ${loc.name}` : loc.name);
    }
    setLinkedComponentIds(prev => [...new Set([...prev, componentId])]);
    setPendingComponent(null);
  };

  const placePendingAsStop = () => {
    if (!pendingComponent) return;
    const { locData, componentId } = pendingComponent;
    const loc = locData.isAirport ? (locData.arrivalLocation || locData.departureLocation) : locData.location;
    if (loc) {
      setStops(prev => [...prev, {
        isAirport: locData.isAirport,
        isAsDirected: false,
        comments: '',
        location: {
          name: loc.name,
          details: loc.details || loc.name,
          placeId: loc.placeId || '',
          isAirport: locData.isAirport,
          code: loc.code || ''
        },
        _searchQuery: loc.code ? `${loc.code} - ${loc.name}` : loc.name,
        _searchResults: [],
        _showResults: false,
        _searchLoading: false
      }]);
    }
    setLinkedComponentIds(prev => [...new Set([...prev, componentId])]);
    setPendingComponent(null);
  };

  const placePendingAsDropoff = () => {
    if (!pendingComponent) return;
    const { locData, componentId } = pendingComponent;
    // For airports, use departureLocation (passenger departing from this airport)
    const loc = locData.isAirport ? locData.departureLocation : locData.location;
    if (loc) {
      setDropoffLocation({
        ...loc,
        isAirport: locData.isAirport,
        isAsDirected: false,
        isHotel: locData.isHotel,
        isMeeting: locData.isMeeting,
        isEvent: locData.isEvent,
        isRestaurant: locData.isRestaurant,
        datetime: loc.datetime || locData.datetime
      });
      setDropoffSearchQuery(loc.code ? `${loc.code} - ${loc.name}` : loc.name);
    }
    setLinkedComponentIds(prev => [...new Set([...prev, componentId])]);
    setPendingComponent(null);
  };

  const dismissPendingComponent = () => {
    setPendingComponent(null);
  };

  /**
   * Search for railroads using Urbanride API
   * @param {string} query - Search query
   */
  const handleRailroadSearch = async (query) => {
    setRailroadSearchQuery(query);

    if (!query || query.length < 1) {
      setRailroadSearchResults([]);
      setShowRailroadDropdown(false);
      return;
    }

    setRailroadSearching(true);
    setShowRailroadDropdown(true);

    try {
      const result = await searchRailroads({ input: query });
      setRailroadSearchResults(result.railroads || []);
    } catch (err) {
      console.error('Error searching for railroads:', err);
      setRailroadSearchResults([]);
    } finally {
      setRailroadSearching(false);
    }
  };

  /**
   * Select a railroad from search results
   */
  const handleSelectRailroad = (railroad) => {
    setTrainStationInfo(prev => ({
      ...prev,
      railroad: railroad.code,
      railroadName: railroad.name
    }));
    setRailroadSearchQuery(railroad.name);
    setShowRailroadDropdown(false);
    setRailroadSearchResults([]);
  };

  /**
   * Clear railroad selection
   */
  const handleClearRailroad = () => {
    setTrainStationInfo(prev => ({
      ...prev,
      railroad: '',
      railroadName: ''
    }));
    setRailroadSearchQuery('');
    setRailroadSearchResults([]);
  };

  /**
   * Calculate suggested pickup time for non-airport pickups when the destination
   * (dropoff or first stop) is a meeting, event, restaurant, or flight with a specific start/departure time.
   *
   * Applies to any pickup that doesn't have a fixed arrival time: hotels, addresses, meetings,
   * events, restaurants, train stations, etc.
   *
   * Uses Google Distance Matrix API to calculate drive time with traffic-aware routing.
   * Two-pass approach:
   * 1. Initial estimate using rough departure time
   * 2. Refined calculation using the calculated pickup time for more accurate traffic data
   *
   * For flights, additional airport processing time is added:
   * - Commercial domestic: 90 minutes
   * - Commercial international: 120 minutes
   * - Private flight: 15 minutes (FBO processing)
   *
   * @param {Object} pickupLocation - The pickup location with placeId/address
   * @param {Object} destination - The destination location (meeting/event/restaurant/airport)
   * @param {Array} stopsArray - Array of intermediate stops (to check first stop)
   */
  const calculateSmartPickupTime = async (pickupLocation, destination, stopsArray = []) => {
    // Determine the target destination - prefer first stop if it has a start time
    let targetDestination = null;
    let targetStartTime = null;
    let isAirportDestination = false;
    let airportProcessingMinutes = 0;

    // Check if first stop is a meeting/event/restaurant/airport with a start time
    if (stopsArray.length > 0) {
      const firstStop = stopsArray[0];
      if ((firstStop.isMeeting || firstStop.isEvent || firstStop.isRestaurant) && firstStop.datetime) {
        targetDestination = firstStop;
        targetStartTime = firstStop.datetime;
      } else if (firstStop.isAirport && firstStop.datetime) {
        targetDestination = firstStop;
        targetStartTime = firstStop.datetime;
        isAirportDestination = true;
        airportProcessingMinutes = calculateAirportProcessingTime(firstStop);
      }
    }

    // If no qualifying first stop, check the dropoff
    if (!targetDestination && destination) {
      if ((destination.isMeeting || destination.isEvent || destination.isRestaurant) && destination.datetime) {
        targetDestination = destination;
        targetStartTime = destination.datetime;
      } else if (destination.isAirport && destination.datetime) {
        targetDestination = destination;
        targetStartTime = destination.datetime;
        isAirportDestination = true;
        airportProcessingMinutes = calculateAirportProcessingTime(destination);
      }
    }

    // If no destination with a start time, we can't calculate suggested pickup time
    if (!targetDestination || !targetStartTime) {
      return;
    }

    // Get origin (pickup) location info
    const originPlaceId = pickupLocation.placeId;
    const originAddress = pickupLocation.details || pickupLocation.name;

    // Get destination location info - for airports, use airport name/code as address
    let destPlaceId = null;
    let destAddress = null;

    if (isAirportDestination) {
      // For airports, construct address from airport code and name
      const airportCode = targetDestination.code || '';
      const airportName = targetDestination.name || targetDestination.details || '';
      destAddress = airportName || `${airportCode} Airport`;
    } else {
      destPlaceId = targetDestination.placeId || targetDestination.location?.placeId;
      destAddress = targetDestination.details || targetDestination.location?.details ||
                   targetDestination.name || targetDestination.location?.name;
    }

    // Need at least one identifier for both origin and destination
    if (!originPlaceId && !originAddress) {
      console.warn('Cannot calculate travel time: no pickup location identifier');
      return;
    }
    if (!destPlaceId && !destAddress) {
      console.warn('Cannot calculate travel time: no destination location identifier');
      return;
    }

    setCalculatingTravelTime(true);
    setTravelTimeError('');

    try {
      const destTime = new Date(targetStartTime);
      const now = new Date();

      // Calculate an initial estimated departure time for the first API call
      // Use a rough buffer: 2.5 hours for airports (travel + processing), 1.5 hours for other destinations
      const initialBufferMinutes = isAirportDestination
        ? (airportProcessingMinutes + 90) // Processing time + ~1.5 hour travel estimate
        : 90; // ~1.5 hour travel estimate for meetings/events
      const initialEstimatedDeparture = new Date(destTime.getTime() - (initialBufferMinutes * 60 * 1000));

      // Determine if we should use departure_time for traffic-aware routing
      // Only use if the estimated departure is in the future
      const useTrafficData = initialEstimatedDeparture > now;
      const initialDepartureTime = useTrafficData ? initialEstimatedDeparture.toISOString() : null;

      // FIRST PASS: Get initial travel time estimate with traffic data
      const initialResult = await getDistanceMatrix({
        originPlaceId: originPlaceId,
        originAddress: originPlaceId ? null : originAddress,
        destinationPlaceId: destPlaceId,
        destinationAddress: destPlaceId ? null : destAddress,
        departureTime: initialDepartureTime
      });

      if (!initialResult.success || !initialResult.durationSeconds) {
        console.warn('Distance Matrix API did not return duration:', initialResult);
        setTravelTimeError(initialResult.error || 'Could not calculate travel time');
        return;
      }

      // Calculate initial suggested pickup time
      let suggestedPickup;
      const airportProcessingSeconds = airportProcessingMinutes * 60;

      if (isAirportDestination) {
        const rawPickupTime = new Date(destTime.getTime() - (initialResult.durationSeconds * 1000) - (airportProcessingSeconds * 1000));
        suggestedPickup = roundPickupTimeToNearest15Minutes(rawPickupTime);
      } else {
        const roundedDurationSeconds = roundUpToNearest15Minutes(initialResult.durationSeconds);
        suggestedPickup = new Date(destTime.getTime() - (roundedDurationSeconds * 1000));
      }

      // SECOND PASS: Refine with more accurate departure time if it differs significantly
      // and the pickup time is still in the future
      let finalResult = initialResult;
      const pickupIsInFuture = suggestedPickup > now;
      const timeDifferenceMinutes = Math.abs(suggestedPickup.getTime() - initialEstimatedDeparture.getTime()) / (60 * 1000);

      // Only do a second pass if:
      // 1. The pickup time is in the future (so we can use traffic data)
      // 2. The calculated pickup time differs by more than 15 minutes from our initial estimate
      if (pickupIsInFuture && timeDifferenceMinutes > 15) {
        const refinedResult = await getDistanceMatrix({
          originPlaceId: originPlaceId,
          originAddress: originPlaceId ? null : originAddress,
          destinationPlaceId: destPlaceId,
          destinationAddress: destPlaceId ? null : destAddress,
          departureTime: suggestedPickup.toISOString()
        });

        if (refinedResult.success && refinedResult.durationSeconds) {
          finalResult = refinedResult;

          // Recalculate pickup time with refined travel duration
          if (isAirportDestination) {
            const rawPickupTime = new Date(destTime.getTime() - (finalResult.durationSeconds * 1000) - (airportProcessingSeconds * 1000));
            suggestedPickup = roundPickupTimeToNearest15Minutes(rawPickupTime);
          } else {
            const roundedDurationSeconds = roundUpToNearest15Minutes(finalResult.durationSeconds);
            suggestedPickup = new Date(destTime.getTime() - (roundedDurationSeconds * 1000));
          }
        }
      }

      // Build display info
      let displayInfo = {};
      if (isAirportDestination) {
        displayInfo = {
          durationSeconds: finalResult.durationSeconds,
          durationText: finalResult.durationText,
          roundedDurationSeconds: finalResult.durationSeconds,
          roundedDurationMinutes: Math.ceil(finalResult.durationSeconds / 60),
          airportProcessingMinutes: airportProcessingMinutes
        };
      } else {
        const roundedDurationSeconds = roundUpToNearest15Minutes(finalResult.durationSeconds);
        displayInfo = {
          durationSeconds: finalResult.durationSeconds,
          durationText: finalResult.durationText,
          roundedDurationSeconds: roundedDurationSeconds,
          roundedDurationMinutes: roundedDurationSeconds / 60,
          airportProcessingMinutes: 0
        };
      }

      // Format for datetime-local input
      const year = suggestedPickup.getFullYear();
      const month = String(suggestedPickup.getMonth() + 1).padStart(2, '0');
      const day = String(suggestedPickup.getDate()).padStart(2, '0');
      const hours = String(suggestedPickup.getHours()).padStart(2, '0');
      const minutes = String(suggestedPickup.getMinutes()).padStart(2, '0');
      const formattedTime = `${year}-${month}-${day}T${hours}:${minutes}`;

      setCustomPickupTime(formattedTime);

      // Determine destination type for display
      let destinationType;
      if (isAirportDestination) {
        destinationType = targetDestination.isPrivateFlight ? 'flight (private)' : 'flight';
      } else if (targetDestination.isMeeting) {
        destinationType = 'meeting';
      } else if (targetDestination.isEvent) {
        destinationType = 'event';
      } else {
        destinationType = 'restaurant';
      }

      setEstimatedTravelTime({
        ...displayInfo,
        distanceText: finalResult.distanceText,
        destinationType: destinationType,
        destinationStartTime: targetStartTime,
        suggestedPickupTime: formattedTime,
        // Airport-specific info
        isAirport: isAirportDestination,
        airportCode: isAirportDestination ? (targetDestination.code || '') : null,
        flightNumber: isAirportDestination ? (targetDestination.flightNumber || '') : null,
        // Traffic info
        usedTrafficData: pickupIsInFuture,
        refinedCalculation: pickupIsInFuture && timeDifferenceMinutes > 15
      });
    } catch (err) {
      console.error('Error calculating travel time:', err);
      setTravelTimeError('Error calculating travel time');
    } finally {
      setCalculatingTravelTime(false);
    }
  };

  /**
   * Calculate airport processing time based on flight type and destination.
   * - Commercial domestic: 90 minutes
   * - Commercial international: 120 minutes
   * - Private flight: 15 minutes (FBO processing)
   *
   * @param {Object} airportLocation - Airport destination with flight details
   * @returns {number} Processing time in minutes
   */
  const calculateAirportProcessingTime = (airportLocation) => {
    // Check if it's a private flight
    if (airportLocation.isPrivateFlight) {
      return 15; // FBO processing for private flights
    }

    // For commercial flights, determine if domestic or international
    // We check the otherAirport (destination airport) to determine if international
    const departureAirport = airportLocation.code || '';
    const arrivalAirport = airportLocation.otherAirport || '';

    // Simple heuristic: if both airports are in the same country, it's domestic
    // We'll use common country prefixes for airport codes
    const isInternational = isInternationalFlight(departureAirport, arrivalAirport);

    return isInternational ? 120 : 90;
  };

  /**
   * Determine if a flight is international based on departure and arrival airport codes.
   * Uses country-based airport code patterns.
   *
   * @param {string} departureCode - Departure airport IATA code
   * @param {string} arrivalCode - Arrival airport IATA code
   * @returns {boolean} True if international flight
   */
  const isInternationalFlight = (departureCode, arrivalCode) => {
    if (!departureCode || !arrivalCode) {
      // If we can't determine, assume international for safety
      return true;
    }

    // Get the country for each airport
    const departureCountry = getAirportCountry(departureCode);
    const arrivalCountry = getAirportCountry(arrivalCode);

    // If countries differ, it's international
    // If we can't determine country, assume international
    if (!departureCountry || !arrivalCountry) {
      return true;
    }

    return departureCountry !== arrivalCountry;
  };

  /**
   * Get the country code for an airport based on its IATA code.
   * Uses common patterns and a lookup table for major airports.
   *
   * @param {string} airportCode - IATA airport code
   * @returns {string|null} Country code or null if unknown
   */
  const getAirportCountry = (airportCode) => {
    if (!airportCode) return null;

    const code = airportCode.toUpperCase();

    // Major airports lookup table
    const airportCountries = {
      // United States
      'JFK': 'US', 'LAX': 'US', 'ORD': 'US', 'DFW': 'US', 'DEN': 'US',
      'SFO': 'US', 'SEA': 'US', 'LAS': 'US', 'MCO': 'US', 'EWR': 'US',
      'MIA': 'US', 'PHX': 'US', 'IAH': 'US', 'BOS': 'US', 'ATL': 'US',
      'LGA': 'US', 'FLL': 'US', 'MSP': 'US', 'DTW': 'US', 'PHL': 'US',
      'DCA': 'US', 'IAD': 'US', 'BWI': 'US', 'SAN': 'US', 'TPA': 'US',
      'PDX': 'US', 'HNL': 'US', 'SLC': 'US', 'AUS': 'US', 'BNA': 'US',
      // Canada
      'YYZ': 'CA', 'YVR': 'CA', 'YUL': 'CA', 'YYC': 'CA', 'YOW': 'CA',
      'YEG': 'CA', 'YHZ': 'CA', 'YWG': 'CA',
      // United Kingdom
      'LHR': 'GB', 'LGW': 'GB', 'STN': 'GB', 'LTN': 'GB', 'MAN': 'GB',
      'EDI': 'GB', 'BHX': 'GB', 'GLA': 'GB', 'LCY': 'GB',
      // European airports
      'CDG': 'FR', 'ORY': 'FR', 'NCE': 'FR', 'LYS': 'FR',
      'FRA': 'DE', 'MUC': 'DE', 'TXL': 'DE', 'DUS': 'DE', 'HAM': 'DE', 'BER': 'DE',
      'AMS': 'NL', 'MAD': 'ES', 'BCN': 'ES', 'FCO': 'IT', 'MXP': 'IT',
      'ZRH': 'CH', 'GVA': 'CH', 'VIE': 'AT', 'BRU': 'BE', 'CPH': 'DK',
      'OSL': 'NO', 'ARN': 'SE', 'HEL': 'FI', 'DUB': 'IE', 'LIS': 'PT',
      'ATH': 'GR', 'WAW': 'PL', 'PRG': 'CZ',
      // Middle East
      'DXB': 'AE', 'AUH': 'AE', 'DOH': 'QA', 'JED': 'SA', 'RUH': 'SA',
      'TLV': 'IL', 'AMM': 'JO', 'KWI': 'KW', 'BAH': 'BH', 'MCT': 'OM',
      // Asia Pacific
      'HKG': 'HK', 'SIN': 'SG', 'NRT': 'JP', 'HND': 'JP', 'KIX': 'JP',
      'ICN': 'KR', 'GMP': 'KR', 'PEK': 'CN', 'PVG': 'CN', 'CAN': 'CN',
      'TPE': 'TW', 'BKK': 'TH', 'KUL': 'MY', 'CGK': 'ID', 'MNL': 'PH',
      'DEL': 'IN', 'BOM': 'IN', 'SYD': 'AU', 'MEL': 'AU', 'BNE': 'AU',
      'AKL': 'NZ', 'WLG': 'NZ',
      // Latin America
      'MEX': 'MX', 'CUN': 'MX', 'GDL': 'MX', 'GRU': 'BR', 'GIG': 'BR',
      'EZE': 'AR', 'SCL': 'CL', 'BOG': 'CO', 'LIM': 'PE', 'PTY': 'PA',
      // Africa
      'JNB': 'ZA', 'CPT': 'ZA', 'CAI': 'EG', 'CMN': 'MA', 'NBO': 'KE',
      'ADD': 'ET', 'LOS': 'NG', 'ACC': 'GH'
    };

    return airportCountries[code] || null;
  };

  /**
   * Round a pickup time back (earlier) to the nearest 15-minute increment.
   * This ensures pickup times are on clean boundaries like :00, :15, :30, :45.
   *
   * Examples:
   * - 3:17 PM → 3:15 PM
   * - 3:14 PM → 3:00 PM
   * - 3:01 PM → 3:00 PM
   * - 3:00 PM → 3:00 PM (no change)
   *
   * @param {Date} pickupTime - The calculated pickup time
   * @returns {Date} The pickup time rounded back to nearest 15 minutes
   */
  const roundPickupTimeToNearest15Minutes = (pickupTime) => {
    const date = new Date(pickupTime);
    const minutes = date.getMinutes();

    // Round down to nearest 15-minute increment
    const roundedMinutes = Math.floor(minutes / 15) * 15;

    date.setMinutes(roundedMinutes);
    date.setSeconds(0);
    date.setMilliseconds(0);

    return date;
  };

  /**
   * Extract location information from a component
   * For flights, extracts full flight details needed for Urbanride API
   */
  const getLocationFromComponent = (component) => {
    const cd = component.component_data || {};
    const componentType = component.component_type;

    // Helper to build proper datetime by combining date and time if needed
    // This ensures the datetime is in ISO format for proper parsing
    const buildDateTime = (dateField, timeField, fallback) => {
      // If we have a full datetime already (with T separator), use it
      if (dateField && dateField.includes('T')) return dateField;
      // Combine date and time
      if (dateField && timeField) {
        const datePart = dateField.split('T')[0];
        const timePart = timeField.includes('T') ? timeField.split('T')[1] : timeField;
        return `${datePart}T${timePart}`;
      }
      // Fall back to just date or time
      if (dateField) return dateField;
      if (timeField) return timeField;
      return fallback;
    };

    // Helper to build arrival datetime - checks arrival_datetime first, then date+time
    // IMPORTANT: Does NOT fall back to departure time (start_datetime) for arrivals
    const buildArrivalDateTime = (leg, componentEndDateTime) => {
      // Check for combined arrival_datetime field first
      if (leg.arrival_datetime) return leg.arrival_datetime;
      // Then try date + time combination
      if (leg.arrival_date && leg.arrival_time) {
        const datePart = leg.arrival_date.split('T')[0];
        const timePart = leg.arrival_time.includes('T') ? leg.arrival_time.split('T')[1] : leg.arrival_time;
        return `${datePart}T${timePart}`;
      }
      // Fall back to component end_datetime (which should be arrival time), NOT start_datetime
      return componentEndDateTime || null;
    };

    // Helper to build departure datetime - checks departure_datetime first, then date+time
    const buildDepartureDateTime = (leg, componentStartDateTime) => {
      // Check for combined departure_datetime field first
      if (leg.departure_datetime) return leg.departure_datetime;
      // Then try date + time combination
      if (leg.departure_date && leg.departure_time) {
        const datePart = leg.departure_date.split('T')[0];
        const timePart = leg.departure_time.includes('T') ? leg.departure_time.split('T')[1] : leg.departure_time;
        return `${datePart}T${timePart}`;
      }
      // Fall back to component start_datetime
      return componentStartDateTime || null;
    };

    // Commercial/Private Flight - extract full flight details for Urbanride API
    if (componentType === 'commercial_flight' || componentType === 'private_flight') {
      const isFlightLeg = component.isFlightLeg;
      const isPrivateFlight = componentType === 'private_flight';

      if (isFlightLeg && component.flightLeg) {
        const leg = component.flightLeg;
        // Extract airport codes
        const arrivalAirport = leg.arrival_airport || leg.arrival_airport_code || '';
        const arrivalName = leg.arrival_airport_name || `${arrivalAirport} Airport`;
        const departureAirport = leg.departure_airport || leg.departure_airport_code || '';
        const departureName = leg.departure_airport_name || `${departureAirport} Airport`;

        // Extract flight details
        // The flight_number field may contain both airline code and number (e.g., "J2 22")
        // We need to parse it to extract the IATA code for the airline field
        const rawFlightNumber = leg.flight_number || cd.flight_number || cd.tail_number || '';
        const parsed = parseFlightNumber(rawFlightNumber);

        // Use parsed airline code if available, otherwise fall back to airline fields
        // Note: leg.airline often contains the full name (e.g., "AZERBAIJAN AIRLINES") not the IATA code
        const airlineCode = parsed.airlineCode || leg.airline_code || leg.airline_name || '';
        const flightNumber = parsed.flightNumber || rawFlightNumber;

        // Use specialized helpers that check for combined datetime fields
        // IMPORTANT: arrivalTime should NOT fall back to start_datetime (which is departure time)
        const arrivalTime = buildArrivalDateTime(leg, component.end_datetime);
        const departureTime = buildDepartureDateTime(leg, component.start_datetime);

        return {
          type: 'airport',
          isAirport: true,
          isPrivateFlight: isPrivateFlight,
          // For pickup (arrival): passenger arrives at this airport
          arrivalLocation: {
            name: arrivalName,
            code: arrivalAirport,
            details: arrivalName,
            airlineCode: airlineCode,
            flightNumber: flightNumber,
            otherAirport: departureAirport, // Origin airport
            datetime: arrivalTime,
            isPrivateFlight: isPrivateFlight
          },
          // For dropoff (departure): passenger departs from this airport
          departureLocation: {
            name: departureName,
            code: departureAirport,
            details: departureName,
            airlineCode: airlineCode,
            flightNumber: flightNumber,
            otherAirport: arrivalAirport, // Destination airport
            datetime: departureTime,
            isPrivateFlight: isPrivateFlight
          },
          datetime: arrivalTime
        };
      }

      // Full flight component (not individual leg)
      const legs = cd.flight_legs || cd.private_flight_legs || [];
      if (legs.length > 0) {
        const lastLeg = legs[legs.length - 1];
        const firstLeg = legs[0];
        const arrivalAirport = lastLeg.arrival_airport || lastLeg.arrival_airport_code || '';
        const arrivalName = lastLeg.arrival_airport_name || `${arrivalAirport} Airport`;
        const departureAirport = firstLeg.departure_airport || firstLeg.departure_airport_code || '';
        const departureName = firstLeg.departure_airport_name || `${departureAirport} Airport`;

        // Extract flight details - parse flight number to get airline code
        const lastLegRawFlight = lastLeg.flight_number || cd.flight_number || cd.tail_number || '';
        const firstLegRawFlight = firstLeg.flight_number || cd.flight_number || cd.tail_number || '';
        const lastLegParsed = parseFlightNumber(lastLegRawFlight);
        const firstLegParsed = parseFlightNumber(firstLegRawFlight);

        // Use parsed airline code if available, otherwise fall back to airline fields
        const lastLegAirline = lastLegParsed.airlineCode || lastLeg.airline_code || lastLeg.airline_name || '';
        const firstLegAirline = firstLegParsed.airlineCode || firstLeg.airline_code || firstLeg.airline_name || '';

        return {
          type: 'airport',
          isAirport: true,
          isPrivateFlight: isPrivateFlight,
          arrivalLocation: {
            name: arrivalName,
            code: arrivalAirport,
            details: arrivalName,
            airlineCode: lastLegAirline,
            flightNumber: lastLegParsed.flightNumber || lastLegRawFlight,
            otherAirport: departureAirport,
            datetime: buildArrivalDateTime(lastLeg, component.end_datetime),
            isPrivateFlight: isPrivateFlight
          },
          departureLocation: {
            name: departureName,
            code: departureAirport,
            details: departureName,
            airlineCode: firstLegAirline,
            flightNumber: firstLegParsed.flightNumber || firstLegRawFlight,
            otherAirport: arrivalAirport,
            datetime: buildDepartureDateTime(firstLeg, component.start_datetime),
            isPrivateFlight: isPrivateFlight
          },
          datetime: buildArrivalDateTime(lastLeg, component.end_datetime)
        };
      }

      // Private flight without legs (isPrivateFlight is already set above)
      const arrAirport = cd.arrival_airport_iata || cd.arrival_airport || '';
      const depAirport = cd.departure_airport_iata || cd.departure_airport || '';

      return {
        type: 'airport',
        isAirport: true,
        isPrivateFlight: isPrivateFlight,
        arrivalLocation: {
          name: cd.arrival_airport_name || `${arrAirport} Airport`,
          code: arrAirport,
          details: cd.arrival_airport_name || `${arrAirport} Airport`,
          airlineCode: '',
          flightNumber: cd.tail_number || '',
          otherAirport: depAirport,
          datetime: component.end_datetime || component.start_datetime,
          isPrivateFlight: isPrivateFlight
        },
        departureLocation: {
          name: cd.departure_airport_name || `${depAirport} Airport`,
          code: depAirport,
          details: cd.departure_airport_name || `${depAirport} Airport`,
          airlineCode: '',
          flightNumber: cd.tail_number || '',
          otherAirport: arrAirport,
          datetime: component.start_datetime,
          isPrivateFlight: isPrivateFlight
        },
        datetime: component.start_datetime
      };
    }

    // Hotel - use hotel address with Google Place ID if available
    if (componentType === 'hotel') {
      const address = cd.hotel_address || cd.address || component.location || '';
      const hotelName = cd.hotel_name || component.title || 'Hotel';
      // Look for place_id in multiple possible fields
      const placeId = cd.hotel_place_id || cd.place_id || cd.google_place_id || '';

      return {
        type: 'address',
        isAirport: false,
        location: {
          name: hotelName,
          details: address || hotelName,
          placeId: placeId
        },
        datetime: component.start_datetime,
        isHotel: true
      };
    }

    // Restaurant - use restaurant address with Google Place ID if available
    if (componentType === 'restaurant') {
      const address = cd.address || component.location || '';
      const restaurantName = cd.restaurant_name || component.title || 'Restaurant';
      // Look for place_id in multiple possible fields
      const placeId = cd.restaurant_place_id || cd.place_id || cd.google_place_id || '';

      return {
        type: 'address',
        isAirport: false,
        location: {
          name: restaurantName,
          details: address || restaurantName,
          placeId: placeId
        },
        datetime: component.start_datetime,
        endDatetime: component.end_datetime,
        isRestaurant: true
      };
    }

    // Meeting / Speaker Meeting - use meeting/venue location
    // Speaker meetings store their venue via the shared meeting location handler
    // (meeting_location + place_id), and also keep venue_name/venue_address.
    if (componentType === 'meeting' || componentType === 'speaker_meeting') {
      const location = cd.meeting_location || cd.venue_address || cd.location || component.location || '';
      const meetingName = cd.venue_name || component.title || 'Meeting';
      // Look for place_id in multiple possible fields
      const placeId = cd.meeting_place_id || cd.place_id || cd.google_place_id || '';

      return {
        type: 'address',
        isAirport: false,
        location: {
          name: meetingName,
          details: location || meetingName,
          placeId: placeId
        },
        datetime: component.start_datetime,
        endDatetime: component.end_datetime,
        isMeeting: true
      };
    }

    // Event - use event location
    if (componentType === 'event') {
      const location = cd.event_location || cd.location || component.location || '';
      const eventName = component.title || 'Event';
      // Look for place_id in multiple possible fields
      const placeId = cd.event_place_id || cd.place_id || cd.google_place_id || '';

      return {
        type: 'address',
        isAirport: false,
        location: {
          name: eventName,
          details: location || eventName,
          placeId: placeId
        },
        datetime: component.start_datetime,
        endDatetime: component.end_datetime,
        isEvent: true
      };
    }

    // Manual location - check for as_directed type
    if (componentType === 'manual_location' || component.isManualLocation) {
      const locationType = component.manualLocationType || cd.location_type;

      // As Directed - chauffeur service where passenger directs
      if (locationType === 'as_directed') {
        return {
          type: 'as_directed',
          isAirport: false,
          isAsDirected: true,
          location: {
            name: 'As Directed',
            details: 'Passenger/s will direct to final destination.',
            placeId: '',
            type: 'as_directed'
          },
          datetime: component.start_datetime
        };
      }

      // Other manual location types (airport, fbo, train_station, address)
      const locationData = component.manualLocationData || {};
      const isAirportType = locationType === 'airport' || locationType === 'fbo';
      const isFbo = locationType === 'fbo';
      const isTrainStation = locationType === 'train_station';
      // For FBOs, use the airportCode field (airport ID), not the id field (FBO ID)
      // For regular airports, use id or code as before
      // For train stations, use the station code from id
      const airportCode = isFbo
        ? (locationData.airportCode || cd.airport_code || '')
        : (locationData.id || locationData.code || '');
      const stationCode = isTrainStation ? (locationData.id || cd.location_id || '') : '';

      return {
        type: isAirportType ? 'airport' : (isTrainStation ? 'train-station' : 'address'),
        isAirport: isAirportType,
        isFbo: isFbo,
        isTrainStation: isTrainStation,
        // For airports/FBOs, put code at top level for buildBookingCmd compatibility
        ...(isAirportType && { code: airportCode }),
        // For train stations, put station code at top level
        ...(isTrainStation && { stationCode: stationCode }),
        // For FBOs, include the selected FBO data to pre-populate selectedFbo
        ...(isFbo && {
          fboData: {
            id: locationData.id || cd.location_id,
            name: locationData.name || cd.location_name || component.title,
            details: locationData.details || cd.location_details || ''
          }
        }),
        location: {
          name: locationData.name || cd.location_name || component.title || 'Location',
          details: locationData.details || cd.location_details || component.location || '',
          placeId: locationData.placeId || locationData.id || cd.location_id || '',
          code: airportCode // For airports/FBOs
        },
        // Also put name and details at top level for display
        name: locationData.name || cd.location_name || component.title || 'Location',
        details: locationData.details || cd.location_details || component.location || '',
        datetime: component.start_datetime
      };
    }

    // Default fallback
    return {
      type: 'address',
      isAirport: false,
      location: {
        name: component.title || 'Location',
        details: component.location || component.title || 'Location',
        placeId: cd.place_id || cd.google_place_id || ''
      },
      datetime: component.start_datetime
    };
  };

  /**
   * Parse airline passenger name format (e.g., "LASTNAME/FIRSTNAMEMR" or "SMITH/JOHNMR")
   * Returns { firstName, lastName }
   */
  const parseAirlinePassengerName = (rawName) => {
    if (!rawName) return { firstName: '', lastName: '' };

    // Check if it's in airline format: LASTNAME/FIRSTNAMEMR or LASTNAME/FIRSTNAME
    if (rawName.includes('/')) {
      const parts = rawName.split('/');
      const lastName = parts[0] || '';
      let firstName = parts[1] || '';

      // Remove common suffixes like MR, MRS, MS, MISS from first name
      firstName = firstName.replace(/(MR|MRS|MS|MISS|DR|PROF|SIR)$/i, '').trim();

      // Capitalize properly
      const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

      return {
        firstName: capitalize(firstName),
        lastName: capitalize(lastName)
      };
    }

    // Not in airline format, try to split by space
    const parts = rawName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return {
        firstName: parts[0],
        lastName: parts.slice(1).join(' ')
      };
    }

    // Single name, use as first name
    return {
      firstName: rawName,
      lastName: ''
    };
  };

  /**
   * Find an executive by ID or by name matching
   * Returns the executive and their Urbanride pax ID based on current mode
   */
  const findExecutiveAndUrbanrideId = (executiveId, firstName, lastName) => {
    let exec = null;

    // First try to find by executiveId
    if (executiveId) {
      exec = allExecutives.find(e => e.id === executiveId) ||
             tripExecutives.find(e => e.id === executiveId);
    }

    // If not found by ID, try to match by name
    if (!exec && firstName && lastName) {
      const firstNameLower = firstName.toLowerCase().trim();
      const lastNameLower = lastName.toLowerCase().trim();

      exec = allExecutives.find(e =>
        (e.first_name?.toLowerCase().trim() === firstNameLower &&
         e.last_name?.toLowerCase().trim() === lastNameLower)
      ) || tripExecutives.find(e =>
        (e.first_name?.toLowerCase().trim() === firstNameLower &&
         e.last_name?.toLowerCase().trim() === lastNameLower)
      );
    }

    if (!exec) {
      return { exec: null, urbanridePaxId: null };
    }

    // Get the appropriate profile ID based on current API mode
    const { mode } = getConfigStatus();
    const urbanridePaxId = mode === 'production'
      ? (exec.urbanride_pax_id_production || exec.urbanride_pax_id || null)
      : (exec.urbanride_pax_id_test || exec.urbanride_pax_id || null);

    return { exec, urbanridePaxId };
  };

  /**
   * Extract passengers from the pickup component
   */
  const getPassengersFromComponent = (component) => {
    const cd = component.component_data || {};
    const componentType = component.component_type;
    const extractedPassengers = [];

    // Flight passengers
    if (componentType === 'commercial_flight' || componentType === 'private_flight') {
      const passengers = cd.passengers || [];

      if (component.isFlightLeg && component.flightLeg) {
        const leg = component.flightLeg;
        const legPassengers = leg.leg_passengers || leg.passengers || leg.seat_assignments || [];

        legPassengers.forEach(p => {
          const rawName = p.passenger_name || p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim();
          if (rawName) {
            // Parse airline format names
            const parsed = parseAirlinePassengerName(rawName);
            const firstName = p.firstName || parsed.firstName;
            const lastName = p.lastName || parsed.lastName;

            // Look up executive and urbanride_pax_id by ID or by name matching
            const { exec, urbanridePaxId } = findExecutiveAndUrbanrideId(
              p.executiveId,
              firstName,
              lastName
            );

            extractedPassengers.push({
              name: `${firstName} ${lastName}`.trim(),
              firstName: firstName,
              lastName: lastName,
              email: p.email || exec?.email || '',
              mobile: p.mobile || p.phone || exec?.mobile || exec?.phone || '',
              isExecutive: p.isExecutive || !!exec,
              executiveId: p.executiveId || exec?.id || null,
              urbanride_pax_id: urbanridePaxId
            });
          }
        });
      }

      if (extractedPassengers.length === 0) {
        passengers.forEach(p => {
          const rawName = p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim();
          if (rawName) {
            // Parse airline format names
            const parsed = parseAirlinePassengerName(rawName);
            const firstName = p.firstName || parsed.firstName;
            const lastName = p.lastName || parsed.lastName;

            // Look up executive and urbanride_pax_id by ID or by name matching
            const { exec, urbanridePaxId } = findExecutiveAndUrbanrideId(
              p.executiveId,
              firstName,
              lastName
            );

            extractedPassengers.push({
              name: `${firstName} ${lastName}`.trim(),
              firstName: firstName,
              lastName: lastName,
              email: p.email || exec?.email || '',
              mobile: p.mobile || p.phone || exec?.mobile || exec?.phone || '',
              isExecutive: p.isExecutive || !!exec,
              executiveId: p.executiveId || exec?.id || null,
              urbanride_pax_id: urbanridePaxId
            });
          }
        });
      }
    }

    // Meeting attendees
    if (componentType === 'meeting') {
      const primaryExecId = cd.primary_executive_id;
      const attendees = cd.meeting_attendees || cd.attendees || [];

      if (primaryExecId) {
        const { exec, urbanridePaxId } = findExecutiveAndUrbanrideId(primaryExecId, null, null);
        if (exec) {
          extractedPassengers.push({
            name: exec.full_name || `${exec.first_name} ${exec.last_name}`,
            firstName: exec.first_name || '',
            lastName: exec.last_name || '',
            email: exec.email || '',
            mobile: exec.mobile || exec.phone || '',
            isExecutive: true,
            executiveId: exec.id,
            urbanride_pax_id: urbanridePaxId
          });
        }
      }

      attendees.forEach(a => {
        if (a.name && !extractedPassengers.some(p => p.name === a.name)) {
          const firstName = a.name.split(' ')[0] || '';
          const lastName = a.name.split(' ').slice(1).join(' ') || '';
          // Try to match attendee to an executive by name
          const { exec, urbanridePaxId } = findExecutiveAndUrbanrideId(null, firstName, lastName);

          extractedPassengers.push({
            name: a.name,
            firstName: firstName,
            lastName: lastName,
            email: a.email || exec?.email || '',
            mobile: a.mobile || a.phone || exec?.mobile || exec?.phone || '',
            isExecutive: !!exec,
            executiveId: exec?.id || null,
            urbanride_pax_id: urbanridePaxId
          });
        }
      });
    }

    // Speaker meeting - the speaker is the traveler
    if (componentType === 'speaker_meeting') {
      const speakerName = cd.speaker_name || '';
      if (speakerName) {
        const firstName = speakerName.split(' ')[0] || '';
        const lastName = speakerName.split(' ').slice(1).join(' ') || '';
        // Try to match the speaker to an executive/contact by name
        const { exec, urbanridePaxId } = findExecutiveAndUrbanrideId(null, firstName, lastName);

        extractedPassengers.push({
          name: speakerName,
          firstName: firstName,
          lastName: lastName,
          email: cd.speaker_email || exec?.email || '',
          mobile: cd.speaker_phone || cd.speaker_mobile || exec?.mobile || exec?.phone || '',
          isExecutive: !!exec,
          executiveId: exec?.id || null,
          urbanride_pax_id: urbanridePaxId
        });
      }
    }

    // Restaurant attendees
    if (componentType === 'restaurant') {
      const attendees = cd.restaurant_attendees || [];

      attendees.forEach(a => {
        if (a.name) {
          const firstName = a.name.split(' ')[0] || '';
          const lastName = a.name.split(' ').slice(1).join(' ') || '';
          // Try to match attendee to an executive by name
          const { exec, urbanridePaxId } = findExecutiveAndUrbanrideId(null, firstName, lastName);

          extractedPassengers.push({
            name: a.name,
            firstName: firstName,
            lastName: lastName,
            email: a.email || exec?.email || '',
            mobile: a.mobile || a.phone || exec?.mobile || exec?.phone || '',
            isExecutive: !!exec,
            executiveId: exec?.id || null,
            urbanride_pax_id: urbanridePaxId
          });
        }
      });
    }

    // Hotel assigned executives
    if (componentType === 'hotel') {
      const assignedExecs = cd.assigned_executives || [];

      assignedExecs.forEach(execId => {
        const { exec, urbanridePaxId } = findExecutiveAndUrbanrideId(execId, null, null);
        if (exec) {
          extractedPassengers.push({
            name: exec.full_name || `${exec.first_name} ${exec.last_name}`,
            firstName: exec.first_name || '',
            lastName: exec.last_name || '',
            email: exec.email || '',
            mobile: exec.mobile || exec.phone || '',
            isExecutive: true,
            executiveId: exec.id,
            urbanride_pax_id: urbanridePaxId
          });
        }
      });
    }

    return extractedPassengers;
  };

  /**
   * Check if a location (hotel, address) is likely in the same area as an airport
   * Uses city name matching from airport names and addresses
   */
  const isLocationNearAirport = (locationDetails, airportName, airportCode) => {
    if (!locationDetails) return false;

    const locationLower = locationDetails.toLowerCase();
    const airportLower = (airportName || '').toLowerCase();

    // Common city-to-airport mappings
    const cityAirportMappings = {
      'new york': ['jfk', 'lga', 'ewr', 'john f kennedy', 'laguardia', 'newark'],
      'nyc': ['jfk', 'lga', 'ewr'],
      'manhattan': ['jfk', 'lga', 'ewr'],
      'brooklyn': ['jfk', 'lga', 'ewr'],
      'los angeles': ['lax', 'los angeles'],
      'la': ['lax'],
      'san francisco': ['sfo', 'san francisco'],
      'sf': ['sfo'],
      'chicago': ['ord', 'mdw', 'ohare', 'midway'],
      'london': ['lhr', 'lgw', 'stn', 'lcy', 'heathrow', 'gatwick', 'stansted'],
      'paris': ['cdg', 'ory', 'charles de gaulle', 'orly'],
      'dubai': ['dxb', 'dwc', 'dubai'],
      'tokyo': ['nrt', 'hnd', 'narita', 'haneda'],
      'miami': ['mia', 'fll', 'miami'],
      'boston': ['bos', 'boston', 'logan'],
      'washington': ['dca', 'iad', 'bwi', 'reagan', 'dulles'],
      'dc': ['dca', 'iad', 'bwi'],
      'atlanta': ['atl', 'hartsfield', 'atlanta'],
      'dallas': ['dfw', 'dal', 'dallas', 'fort worth'],
      'denver': ['den', 'denver'],
      'seattle': ['sea', 'seattle', 'tacoma'],
      'hong kong': ['hkg', 'hong kong'],
      'singapore': ['sin', 'changi', 'singapore'],
      'amsterdam': ['ams', 'schiphol', 'amsterdam'],
      'frankfurt': ['fra', 'frankfurt'],
      'munich': ['muc', 'munich'],
      'rome': ['fco', 'fiumicino', 'rome', 'roma'],
      'milan': ['mxp', 'lin', 'malpensa', 'linate', 'milan', 'milano'],
      'sydney': ['syd', 'sydney', 'kingsford'],
      'melbourne': ['mel', 'melbourne', 'tullamarine'],
      'toronto': ['yyz', 'ytz', 'pearson', 'toronto'],
      'vancouver': ['yvr', 'vancouver'],
      'montreal': ['yul', 'montreal', 'trudeau'],
    };

    const airportCodeLower = (airportCode || '').toLowerCase();

    // Check if location contains any city that maps to this airport
    for (const [city, airports] of Object.entries(cityAirportMappings)) {
      if (locationLower.includes(city)) {
        if (airports.includes(airportCodeLower)) {
          return true;
        }
        // Also check if airport name contains any of the airport identifiers
        for (const apt of airports) {
          if (airportLower.includes(apt)) {
            return true;
          }
        }
      }
    }

    // Direct match: check if airport name contains location city or vice versa
    // Extract potential city names from location (first part before comma often is city or venue)
    const locationParts = locationLower.split(',').map(p => p.trim());
    for (const part of locationParts) {
      if (part.length > 2 && airportLower.includes(part)) {
        return true;
      }
    }

    // Check if location contains the airport code
    if (airportCodeLower && locationLower.includes(airportCodeLower)) {
      return true;
    }

    return false;
  };

  /**
   * Process selected components to extract locations and passengers
   *
   * Airport Context Inference Logic:
   * ---------------------------------
   * When a flight is selected along with a hotel/address, the system must determine
   * which airport (departure or arrival) to use for the transfer. This is done using
   * GEOGRAPHIC CONTEXT rather than just chronological ordering.
   *
   * Key principle: The transfer must occur at the airport in the same city as the hotel.
   *
   * Examples:
   * 1. Hotel in New York + Flight JFK→Dubai
   *    → Hotel is in NYC, JFK is in NYC → Transfer at JFK (departure)
   *    → Result: Hotel (pickup) → JFK (dropoff) - departure transfer
   *
   * 2. Flight Dubai→JFK + Hotel in New York
   *    → Hotel is in NYC, JFK is in NYC → Transfer at JFK (arrival)
   *    → Result: JFK (pickup) → Hotel (dropoff) - arrival transfer
   *
   * 3. If no geographic match is found, fall back to chronological ordering.
   *
   * Selection Order Logic for Ambiguous Cases:
   * ------------------------------------------
   * When the relationship between locations is ambiguous (e.g., meeting + hotel,
   * event + restaurant), the FIRST ITEM SELECTED is assumed to be the PICKUP.
   *
   * For meetings, events, and restaurants used as PICKUP, the END TIME is used
   * (since the passenger leaves after the event concludes).
   *
   * Examples:
   * 1. User selects Meeting first, then Hotel
   *    → Meeting is pickup (at meeting end time), Hotel is dropoff
   *
   * 2. User selects Hotel first, then Meeting
   *    → Hotel is pickup, Meeting is dropoff
   */
  const processSelectedComponents = () => {
    // Extract location from each component (don't sort yet)
    // The originalIndex preserves the user's selection order
    const locations = selectedComponents.map((comp, index) => ({
      ...getLocationFromComponent(comp),
      component: comp,
      originalIndex: index
    }));

    setProcessedLocations(locations);

    // Separate airports from non-airports
    const airportLocations = locations.filter(loc => loc.isAirport);
    const nonAirportLocations = locations.filter(loc => !loc.isAirport);

    let finalPickup = null;
    let finalDropoff = null;
    let finalStops = [];
    let isHotelPickup = false;
    let pickupUsesEndTime = false;

    /**
     * Helper to determine the appropriate pickup datetime for a location.
     * For meetings, events, and restaurants used as pickup, use end time
     * (passenger leaves after the event ends).
     */
    const getPickupDatetime = (loc) => {
      if ((loc.isMeeting || loc.isEvent || loc.isRestaurant) && loc.endDatetime) {
        return loc.endDatetime;
      }
      return loc.datetime;
    };

    // If we have exactly one flight and one non-airport location, use geographic inference
    // BUT only if it's a real flight (with departureLocation/arrivalLocation), not a manual airport
    if (airportLocations.length === 1 && nonAirportLocations.length === 1) {
      const flight = airportLocations[0];
      const other = nonAirportLocations[0];

      // Check if this is a real flight with departure/arrival locations, or a manual airport
      const isRealFlight = flight.departureLocation && flight.arrivalLocation;

      if (isRealFlight) {
        // Real flight - use geographic inference
        const otherDetails = other.location?.details || other.location?.name || '';

        // Check which airport (departure or arrival) is near the hotel/address
        const departureNear = isLocationNearAirport(
          otherDetails,
          flight.departureLocation?.name,
          flight.departureLocation?.code
        );
        const arrivalNear = isLocationNearAirport(
          otherDetails,
          flight.arrivalLocation?.name,
          flight.arrivalLocation?.code
        );

        if (departureNear && !arrivalNear) {
          // Hotel is near departure airport → Departure transfer (hotel to airport)
          // Use end time for meetings/events/restaurants as pickup
          pickupUsesEndTime = (other.isMeeting || other.isEvent || other.isRestaurant) && other.endDatetime;
          finalPickup = {
            ...other.location,
            isAirport: false,
            isHotel: other.isHotel,
            isMeeting: other.isMeeting,
            isRestaurant: other.isRestaurant,
            isEvent: other.isEvent,
            datetime: pickupUsesEndTime ? other.endDatetime : other.datetime,
            endDatetime: other.endDatetime
          };
          finalDropoff = {
            ...flight.departureLocation,
            isAirport: true,
            datetime: flight.departureLocation?.datetime
          };
          isHotelPickup = other.isHotel;
        } else if (arrivalNear && !departureNear) {
          // Hotel is near arrival airport → Arrival transfer (airport to hotel)
          finalPickup = {
            ...flight.arrivalLocation,
            isAirport: true,
            datetime: flight.arrivalLocation?.datetime
          };
          finalDropoff = {
            ...other.location,
            isAirport: false,
            isHotel: other.isHotel,
            isMeeting: other.isMeeting,
            isRestaurant: other.isRestaurant,
            isEvent: other.isEvent,
            datetime: other.datetime
          };
          isHotelPickup = false;
        } else {
          // No clear geographic match or both match - USE SELECTION ORDER
          const addressIndex = locations.findIndex(loc => !loc.isAirport);
          const flightIndex = locations.findIndex(loc => loc.isAirport);

          if (addressIndex < flightIndex) {
            // Address selected first → departure transfer (address to departure airport)
            pickupUsesEndTime = (other.isMeeting || other.isEvent || other.isRestaurant) && other.endDatetime;
            finalPickup = {
              ...other.location,
              isAirport: false,
              isHotel: other.isHotel,
              isMeeting: other.isMeeting,
              isRestaurant: other.isRestaurant,
              isEvent: other.isEvent,
              datetime: pickupUsesEndTime ? other.endDatetime : other.datetime,
              endDatetime: other.endDatetime
            };
            finalDropoff = {
              ...flight.departureLocation,
              isAirport: true,
              datetime: flight.departureLocation?.datetime
            };
            isHotelPickup = other.isHotel;
          } else {
            // Flight selected first → arrival transfer (arrival airport to address)
            finalPickup = {
              ...flight.arrivalLocation,
              isAirport: true,
              datetime: flight.arrivalLocation?.datetime
            };
            finalDropoff = {
              ...other.location,
              isAirport: false,
              isHotel: other.isHotel,
              isMeeting: other.isMeeting,
              isRestaurant: other.isRestaurant,
              isEvent: other.isEvent,
              datetime: other.datetime
            };
            isHotelPickup = false;
          }
        }
      } else {
        // Manual airport/FBO - use simple selection order
        const addressIndex = locations.findIndex(loc => !loc.isAirport);
        const airportIndex = locations.findIndex(loc => loc.isAirport);

        if (addressIndex < airportIndex) {
          // Address selected first → address is pickup, manual airport/FBO is dropoff
          pickupUsesEndTime = (other.isMeeting || other.isEvent || other.isRestaurant) && other.endDatetime;
          finalPickup = {
            ...other.location,
            isAirport: false,
            isHotel: other.isHotel,
            isMeeting: other.isMeeting,
            isRestaurant: other.isRestaurant,
            isEvent: other.isEvent,
            datetime: pickupUsesEndTime ? other.endDatetime : other.datetime,
            endDatetime: other.endDatetime
          };
          // Use top-level properties for manual airport/FBO
          finalDropoff = {
            name: flight.name,
            details: flight.details,
            code: flight.code,
            isAirport: true,
            isFbo: flight.isFbo,
            fboData: flight.fboData,  // Include FBO data if available
            datetime: flight.datetime
          };
          isHotelPickup = other.isHotel;
        } else {
          // Manual airport/FBO selected first → manual airport/FBO is pickup, address is dropoff
          finalPickup = {
            name: flight.name,
            details: flight.details,
            code: flight.code,
            isAirport: true,
            isFbo: flight.isFbo,
            datetime: flight.datetime
          };
          finalDropoff = {
            ...other.location,
            isAirport: false,
            isHotel: other.isHotel,
            isMeeting: other.isMeeting,
            isRestaurant: other.isRestaurant,
            isEvent: other.isEvent,
            datetime: other.datetime
          };
          isHotelPickup = false;
        }
      }
    } else {
      // Multiple components or no flight
      // Always use SELECTION ORDER: the user has explicitly specified the route order
      // in the booking summary by the order they clicked "Add to Itinerary"
      const ordered = [...locations]; // Already in selection order

      const pickup = ordered[0];
      const dropoff = ordered[ordered.length - 1];
      const intermediateStops = ordered.slice(1, -1);

      // Use end time for meetings/events/restaurants as pickup
      pickupUsesEndTime = (pickup.isMeeting || pickup.isEvent || pickup.isRestaurant) && pickup.endDatetime;
      const pickupDatetime = pickupUsesEndTime ? pickup.endDatetime : pickup.datetime;

      // For airports from flights, use arrivalLocation. For manual airports/train stations, use top-level properties.
      if (pickup.isAirport) {
        finalPickup = pickup.arrivalLocation
          ? { ...pickup.arrivalLocation, isAirport: true }
          : { name: pickup.name, details: pickup.details, code: pickup.code, isAirport: true, datetime: pickup.datetime };
      } else if (pickup.isTrainStation) {
        // Train station - use top-level properties
        finalPickup = { name: pickup.name, details: pickup.details, stationCode: pickup.stationCode, isAirport: false, isTrainStation: true, datetime: pickupDatetime };
      } else {
        finalPickup = { ...pickup.location, isAirport: false, isHotel: pickup.isHotel, isMeeting: pickup.isMeeting, isRestaurant: pickup.isRestaurant, isEvent: pickup.isEvent, datetime: pickupDatetime, endDatetime: pickup.endDatetime };
      }

      if (ordered.length > 1) {
        // For airports from flights, use departureLocation. For manual airports/FBOs/train stations, use top-level properties.
        if (dropoff.isAirport) {
          finalDropoff = dropoff.departureLocation
            ? { ...dropoff.departureLocation, isAirport: true }
            : { name: dropoff.name, details: dropoff.details, code: dropoff.code, isAirport: true, isFbo: dropoff.isFbo, fboData: dropoff.fboData, datetime: dropoff.datetime };
        } else if (dropoff.isTrainStation) {
          // Train station - use top-level properties
          finalDropoff = { name: dropoff.name, details: dropoff.details, stationCode: dropoff.stationCode, isAirport: false, isTrainStation: true, datetime: dropoff.datetime };
        } else {
          finalDropoff = { ...dropoff.location, isAirport: false, isHotel: dropoff.isHotel, isMeeting: dropoff.isMeeting, isRestaurant: dropoff.isRestaurant, isEvent: dropoff.isEvent, datetime: dropoff.datetime };
        }
      }

      // Process intermediate stops
      if (intermediateStops.length > 0) {
        finalStops = intermediateStops.map(stop => {
          let stopLocation;
          if (stop.isAirport) {
            // For airports from flights, use arrivalLocation. For manual airports, use top-level properties.
            stopLocation = stop.arrivalLocation
              ? { ...stop.arrivalLocation, isAirport: true }
              : { name: stop.name, details: stop.details, code: stop.code, isAirport: true };
          } else if (stop.isTrainStation) {
            // Train station - use top-level properties
            stopLocation = { name: stop.name, details: stop.details, stationCode: stop.stationCode, isAirport: false, isTrainStation: true };
          } else {
            stopLocation = { ...stop.location, isAirport: false };
          }
          return {
            location: stopLocation,
            datetime: stop.datetime,
            endDatetime: stop.endDatetime,
            component: stop.component,
            isAirport: stop.isAirport,
            isTrainStation: stop.isTrainStation,
            isHotel: stop.isHotel,
            isMeeting: stop.isMeeting,
            isRestaurant: stop.isRestaurant,
            isEvent: stop.isEvent
          };
        });
      }

      isHotelPickup = pickup.isHotel;
    }

    // Set the final locations
    if (finalPickup) {
      setPickupLocation(finalPickup);
    }
    if (finalDropoff) {
      setDropoffLocation(finalDropoff);
    }
    setStops(finalStops);

    // Show pickup time prompt for any pickup that's NOT from an arriving flight
    // Arriving flights have fixed arrival times; all other pickup types need user to specify/confirm when to leave
    // This includes: hotels, addresses, meetings (end time), events (end time), restaurants (end time), train stations, etc.
    const hasFixedArrivalTime = finalPickup?.isAirport; // Only flight arrivals have truly fixed times
    const needsPickupTimePrompt = !hasFixedArrivalTime;

    if (needsPickupTimePrompt) {
      setShowPickupTimePrompt(true);
      setEstimatedTravelTime(null);
      setTravelTimeError('');

      // Try to calculate smart pickup time based on destination
      // If destination (dropoff or first stop) is a meeting/event/restaurant/flight with a start time,
      // calculate travel time and suggest pickup time = destination time - rounded travel time - airport processing (for flights)
      const hasTimedDestination = (finalDropoff?.isMeeting || finalDropoff?.isEvent || finalDropoff?.isRestaurant || finalDropoff?.isAirport) ||
        (finalStops.length > 0 && (finalStops[0].isMeeting || finalStops[0].isEvent || finalStops[0].isRestaurant || finalStops[0].isAirport));

      if (hasTimedDestination && finalPickup) {
        // Calculate smart pickup time asynchronously
        // Set a temporary default while calculating
        // Use the pickup datetime if available (for hotels/meetings), otherwise use dropoff datetime as reference
        const referenceDate = finalPickup.datetime || finalDropoff?.datetime;
        if (referenceDate) {
          const refDate = new Date(referenceDate);
          const dateOnly = `${refDate.getFullYear()}-${String(refDate.getMonth() + 1).padStart(2, '0')}-${String(refDate.getDate()).padStart(2, '0')}`;
          setCustomPickupTime(`${dateOnly}T09:00`); // Temporary default while calculating
        }
        // Call the async calculation function
        calculateSmartPickupTime(finalPickup, finalDropoff, finalStops);
      } else {
        // No timed destination - use default 9 AM on pickup/dropoff date
        const referenceDate = finalPickup?.datetime || finalDropoff?.datetime;
        if (referenceDate) {
          const refDate = new Date(referenceDate);
          const dateOnly = `${refDate.getFullYear()}-${String(refDate.getMonth() + 1).padStart(2, '0')}-${String(refDate.getDate()).padStart(2, '0')}`;
          setCustomPickupTime(`${dateOnly}T09:00`); // Default to 9 AM, user should change
        }
      }
    } else {
      setShowPickupTimePrompt(false);
      setCustomPickupTime('');
      setEstimatedTravelTime(null);
      setTravelTimeError('');
    }

    // Check if dropoff is a manual airport/FBO (no flight info) - prompt user for flight details
    // Manual airports have isAirport=true but no airlineCode or flightNumber
    const isManualAirportDropoff = finalDropoff?.isAirport && !finalDropoff?.airlineCode && !finalDropoff?.flightNumber;
    const isFboDropoff = finalDropoff?.isFbo || false;
    if (isManualAirportDropoff) {
      setShowFlightInfoPrompt(true);
      setIsDropoffFbo(isFboDropoff);
      // Reset validation state
      setFlightValidated(false);
      setFlightValidationError('');

      // Check if the dropoff already has FBO data (selected during component selection)
      // If so, pre-populate selectedFbo and skip the FBO search
      if (isFboDropoff && finalDropoff?.fboData?.id) {
        console.log('[UrbanrideQuickBooking] FBO already selected from component:', finalDropoff.fboData);
        setSelectedFbo(finalDropoff.fboData);
        setFboList([]);  // No need for list since FBO is already selected
        setFboListError('');
        setFboAirportCode(finalDropoff.code);
      } else {
        // Only reset FBO selection if the airport has changed
        // This preserves the user's FBO selection when re-processing doesn't change the dropoff
        // Also check that the airport code is valid (not a numeric FBO ID)
        const isValidAirportCode = finalDropoff?.code && /^[A-Z]{3,4}$/i.test(finalDropoff.code);
        const dropoffCodeChanged = isValidAirportCode && dropoffLocation?.code !== finalDropoff?.code;

        if (dropoffCodeChanged) {
          // Reset FBO selection state only when airport changes
          setFboList([]);
          setFboListError('');
          setSelectedFbo(null);
          setFboAirportCode(null);  // Reset tracked airport so we reload FBOs
          // If it's an FBO, search for FBOs at this airport
          if (isFboDropoff && finalDropoff?.code) {
            searchFbosAtAirport(finalDropoff.code);
          }
        }
      }

      // Initialize departure date only (leave time blank to be filled by validation)
      const referenceDate = finalPickup?.datetime || finalDropoff?.datetime;
      if (referenceDate) {
        const refDate = new Date(referenceDate);
        const year = refDate.getFullYear();
        const month = String(refDate.getMonth() + 1).padStart(2, '0');
        const day = String(refDate.getDate()).padStart(2, '0');
        setManualAirportFlightInfo({
          airlineCode: '',
          flightNumber: '',
          departureDate: `${year}-${month}-${day}`,
          departureTimeOfDay: '',  // Leave blank - will be populated by validation
          otherAirport: '',
          tailNumber: ''
        });
      } else {
        setManualAirportFlightInfo({
          airlineCode: '',
          flightNumber: '',
          departureDate: '',
          departureTimeOfDay: '',
          otherAirport: '',
          tailNumber: ''
        });
      }
      // If airport hasn't changed, preserve existing FBO selection and flight info
    } else {
      setShowFlightInfoPrompt(false);
      setIsDropoffFbo(false);
      setFlightValidated(false);
      setFlightValidationError('');
      setFboList([]);
      setFboListError('');
      setSelectedFbo(null);
      setFboAirportCode(null);
      setManualAirportFlightInfo({ airlineCode: '', flightNumber: '', departureDate: '', departureTimeOfDay: '', otherAirport: '', tailNumber: '' });
    }

    // Check if dropoff is a train station - prompt for railroad, train number, and departure time
    const isTrainStationDropoff = finalDropoff?.isTrainStation || false;
    if (isTrainStationDropoff) {
      setIsDropoffTrainStation(true);
      // Initialize departure date from reference date
      const referenceDate = finalPickup?.datetime || finalDropoff?.datetime;
      if (referenceDate) {
        const refDate = new Date(referenceDate);
        const year = refDate.getFullYear();
        const month = String(refDate.getMonth() + 1).padStart(2, '0');
        const day = String(refDate.getDate()).padStart(2, '0');
        setTrainStationInfo(prev => ({
          ...prev,
          departureDate: `${year}-${month}-${day}`,
          departureTime: ''
        }));
      }
    } else {
      setIsDropoffTrainStation(false);
      setTrainStationInfo({ railroad: '', railroadName: '', trainNumber: '', departureDate: '', departureTime: '' });
      setRailroadSearchQuery('');
      setRailroadSearchResults([]);
    }

    // Check if pickup is a manual airport/FBO (no flight info) - prompt user for arrival flight details
    // Manual airports have isAirport=true but no airlineCode or flightNumber
    const isManualAirportPickup = finalPickup?.isAirport && !finalPickup?.airlineCode && !finalPickup?.flightNumber;
    const isFboPickup = finalPickup?.isFbo || false;
    if (isManualAirportPickup) {
      setShowArrivalFlightPrompt(true);
      setIsPickupFbo(isFboPickup);
      // Reset validation state
      setArrivalFlightValidated(false);
      setArrivalFlightValidationError('');

      // Check if the pickup already has FBO data (selected during component selection)
      if (isFboPickup && finalPickup?.fboData?.id) {
        console.log('[UrbanrideQuickBooking] Pickup FBO already selected from component:', finalPickup.fboData);
        setSelectedPickupFbo(finalPickup.fboData);
        setPickupFboList([]);
        setPickupFboListError('');
        setPickupFboAirportCode(finalPickup.code);
      } else {
        // Only reset FBO selection if the airport has changed
        const isValidAirportCode = finalPickup?.code && /^[A-Z]{3,4}$/i.test(finalPickup.code);
        const pickupCodeChanged = isValidAirportCode && pickupLocation?.code !== finalPickup?.code;

        if (pickupCodeChanged) {
          // Reset FBO selection state only when airport changes
          setPickupFboList([]);
          setPickupFboListError('');
          setSelectedPickupFbo(null);
          setPickupFboAirportCode(null);
          // If it's an FBO, search for FBOs at this airport
          if (isFboPickup && finalPickup?.code) {
            searchPickupFbosAtAirport(finalPickup.code);
          }
        }
      }

      // Initialize arrival date from reference date
      const referenceDate = finalPickup?.datetime || finalDropoff?.datetime;
      if (referenceDate) {
        const refDate = new Date(referenceDate);
        const year = refDate.getFullYear();
        const month = String(refDate.getMonth() + 1).padStart(2, '0');
        const day = String(refDate.getDate()).padStart(2, '0');
        setArrivalFlightInfo({
          airlineCode: '',
          flightNumber: '',
          arrivalDate: `${year}-${month}-${day}`,
          arrivalTimeOfDay: '',  // Leave blank - will be populated by validation
          originAirport: '',
          tailNumber: ''
        });
      } else {
        setArrivalFlightInfo({
          airlineCode: '',
          flightNumber: '',
          arrivalDate: '',
          arrivalTimeOfDay: '',
          originAirport: '',
          tailNumber: ''
        });
      }
    } else {
      setShowArrivalFlightPrompt(false);
      setIsPickupFbo(false);
      setArrivalFlightValidated(false);
      setArrivalFlightValidationError('');
      setPickupFboList([]);
      setPickupFboListError('');
      setSelectedPickupFbo(null);
      setPickupFboAirportCode(null);
      setArrivalFlightInfo({ airlineCode: '', flightNumber: '', arrivalDate: '', arrivalTimeOfDay: '', originAirport: '', tailNumber: '' });
    }

    // Extract passengers from the pickup component
    // Preserve any manually added passengers when re-extracting
    const pickupComponent = locations.find(loc =>
      (finalPickup?.isAirport && loc.isAirport) ||
      (!finalPickup?.isAirport && !loc.isAirport && loc.location?.name === finalPickup?.name)
    );

    let extractedPassengers = [];
    if (pickupComponent) {
      extractedPassengers = getPassengersFromComponent(pickupComponent.component);
    } else if (locations.length > 0) {
      // Fallback to first component
      extractedPassengers = getPassengersFromComponent(locations[0].component);
    }

    // Mark extracted passengers and merge with manually added ones
    const extractedWithFlag = extractedPassengers.map(p => ({ ...p, isExtracted: true }));

    setPassengers(prev => {
      // Keep manually added passengers (those without isExtracted flag or with manuallyAdded flag)
      const manuallyAdded = prev.filter(p => p.manuallyAdded === true);
      // Combine extracted passengers with manually added ones
      return [...extractedWithFlag, ...manuallyAdded];
    });
  };

  /**
   * Format datetime for Urbanride API (yyyy-MM-dd'T'HH:mm without seconds)
   * IMPORTANT: Extracts directly from ISO string to preserve original timezone
   */
  const formatDateTimeForApi = (datetime) => {
    if (!datetime) return '';
    try {
      const isoString = String(datetime);

      // Try to extract directly from ISO format to preserve timezone
      const match = isoString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
      if (match) {
        const [, year, month, day, hours, minutes] = match;
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      }

      // Fallback: if not ISO format, try parsing with Date
      // This case should be rare - most datetimes should be ISO format
      const date = new Date(datetime);
      if (isNaN(date.getTime())) return '';

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (e) {
      console.error('Error formatting datetime:', e);
      return '';
    }
  };

  /**
   * Generate stop comment based on component type and end time
   * For meetings, events, restaurants - generates "Chauffeur will wait until approximately HH:MM"
   */
  const generateStopComment = (stop) => {
    // Only generate wait-until comments for meetings, events, and restaurants with end times
    if ((stop.isMeeting || stop.isEvent || stop.isRestaurant) && stop.endDatetime) {
      try {
        // Extract time directly from ISO string to preserve timezone
        const match = String(stop.endDatetime).match(/T(\d{2}):(\d{2})/);
        if (match) {
          const hours = match[1];
          const minutes = match[2];
          return `Chauffeur will wait until approximately ${hours}:${minutes}`;
        }
        // Fallback to Date parsing if not ISO format
        const endDate = new Date(stop.endDatetime);
        if (!isNaN(endDate.getTime())) {
          const hours = String(endDate.getHours()).padStart(2, '0');
          const minutes = String(endDate.getMinutes()).padStart(2, '0');
          return `Chauffeur will wait until approximately ${hours}:${minutes}`;
        }
      } catch (e) {
        console.error('Error generating stop comment:', e);
      }
    }
    return '';
  };

  /**
   * Geocode an address to get a place ID using the Urbanride location search API
   * @param {string} address - The address to geocode
   * @returns {Promise<string>} The place ID or empty string if not found
   */
  const geocodeAddress = async (address) => {
    if (!address) return '';

    try {
      const result = await searchLocations({ input: address });
      if (result.locations && result.locations.length > 0) {
        // Return the ID of the first matching location
        return result.locations[0].id || '';
      }
    } catch (err) {
      console.error('Failed to geocode address:', address, err);
    }
    return '';
  };

  /**
   * Resolve place IDs for all locations that have addresses but missing place IDs
   * This ensures the Urbanride API can properly calculate rates
   */
  const resolveLocationPlaceIds = async () => {
    let updatedPickup = pickupLocation;
    let updatedDropoff = dropoffLocation;
    let updatedStops = [...stops];
    let hasUpdates = false;

    // Resolve pickup place ID if missing (for non-airport locations)
    if (pickupLocation && !pickupLocation.isAirport && !pickupLocation.placeId && pickupLocation.details) {
      const placeId = await geocodeAddress(pickupLocation.details);
      if (placeId) {
        updatedPickup = { ...pickupLocation, placeId };
        hasUpdates = true;
      }
    }

    // Resolve dropoff place ID if missing (for non-airport locations)
    if (dropoffLocation && !dropoffLocation.isAirport && !dropoffLocation.placeId && dropoffLocation.details) {
      const placeId = await geocodeAddress(dropoffLocation.details);
      if (placeId) {
        updatedDropoff = { ...dropoffLocation, placeId };
        hasUpdates = true;
      }
    }

    // Resolve stop place IDs if missing (for non-airport stops)
    for (let i = 0; i < stops.length; i++) {
      const stop = stops[i];
      if (stop.location && !stop.location.isAirport && !stop.location.placeId && stop.location.details) {
        const placeId = await geocodeAddress(stop.location.details);
        if (placeId) {
          updatedStops[i] = {
            ...stop,
            location: { ...stop.location, placeId }
          };
          hasUpdates = true;
        }
      }
    }

    // Update state if we resolved any place IDs
    if (hasUpdates) {
      if (updatedPickup !== pickupLocation) setPickupLocation(updatedPickup);
      if (updatedDropoff !== dropoffLocation) setDropoffLocation(updatedDropoff);
      if (updatedStops !== stops) setStops(updatedStops);
    }

    return { pickup: updatedPickup, dropoff: updatedDropoff, stops: updatedStops };
  };

  /**
   * Build the booking command with proper airport/address formatting
   */
  const buildBookingCmd = (resolvedLocations = null, validatedFlights = null) => {
    // Use resolved locations if provided, otherwise use current state
    const pickup_loc = resolvedLocations?.pickup || pickupLocation;
    const dropoff_loc = resolvedLocations?.dropoff || dropoffLocation;
    const stops_list = resolvedLocations?.stops || stops;

    // Use provided validated flights (from autoValidateFlights) or fall back to state
    const pickupFlightData = validatedFlights?.arrivalFlight || arrivalValidatedFlight;
    const dropoffFlightData = validatedFlights?.departureFlight || departureValidatedFlight;

    // For hotel pickups, use the custom pickup time selected by the user
    // Do NOT use hotel checkout time unless explicitly set by user
    const pickupDateTime = showPickupTimePrompt && customPickupTime
      ? formatDateTimeForApi(customPickupTime)
      : formatDateTimeForApi(pickup_loc?.datetime) || formatDateTimeForApi(new Date());

    // Build pickup location - use airport format if it's an airport
    // Check if pickup is a manual airport (no flight info) - use arrival flight info if available
    const isManualAirportPickup = showArrivalFlightPrompt && pickup_loc?.isAirport;

    // For manual airport pickups, calculate pickup datetime based on arrival time
    // If user entered arrival date/time, use that for pickup
    let actualPickupDateTime = pickupDateTime;
    if (isManualAirportPickup && arrivalFlightInfo.arrivalDate && arrivalFlightInfo.arrivalTimeOfDay) {
      // Use the validated arrival time as the pickup time
      actualPickupDateTime = formatDateTimeForApi(`${arrivalFlightInfo.arrivalDate}T${arrivalFlightInfo.arrivalTimeOfDay}`);
    }

    let pickup;
    if (pickup_loc?.isFbo && selectedPickupFbo) {
      // FBO pickup - use selected FBO's ID and tail number
      pickup = createFboLocation({
        id: selectedPickupFbo.id,
        dateTime: actualPickupDateTime,
        tailNumber: arrivalFlightInfo.tailNumber || '',
        comments: pickupComments
      });
    } else if (pickup_loc?.isAirport && pickup_loc?.code) {
      // For manual airport pickups, use user-entered arrival flight values or defaults
      const airlineCode = isManualAirportPickup
        ? (arrivalFlightInfo.airlineCode || 'XX')
        : (pickup_loc.airlineCode || '');
      const flightNumber = isManualAirportPickup
        ? (arrivalFlightInfo.flightNumber || '1')
        : (pickup_loc.flightNumber || '');

      pickup = createAirportLocation({
        airport: pickup_loc.code,
        airline: airlineCode,
        flightNumber: flightNumber,
        dateTime: actualPickupDateTime,
        departure: false,
        comments: pickupComments,
        flight: pickupFlightData || null
      });
    } else if (pickup_loc?.isTrainStation && pickup_loc?.stationCode) {
      // Train station pickup
      pickup = createTrainStationLocation({
        station: pickup_loc.stationCode,
        dateTime: pickupDateTime,
        railroad: '',
        trainNumber: '',
        comments: pickupComments
      });
    } else {
      // Address location - both id and placeId should be the Google Place ID
      const pickupPlaceId = pickup_loc?.placeId || '';
      // If no place_id, include address in comments as fallback for chauffeur
      const pickupAddrFallback = !pickupPlaceId && pickup_loc?.details
        ? `Address: ${pickup_loc.details}`
        : '';
      const pickupComment = [pickupAddrFallback, pickupComments].filter(Boolean).join('\n');
      pickup = createAddressLocation({
        id: pickupPlaceId,
        placeId: pickupPlaceId,
        dateTime: pickupDateTime,
        comments: pickupComment
      });
    }

    // Build dropoff location - use airport format if it's an airport, as_directed if specified
    const isManualAirportDropoff = showFlightInfoPrompt && dropoff_loc?.isAirport;
    // For manual airport dropoffs, use user-entered values or apply defaults
    let manualDepartureDateTime = null;
    if (isManualAirportDropoff) {
      if (manualAirportFlightInfo.departureDate && manualAirportFlightInfo.departureTimeOfDay) {
        // User entered both date and time
        manualDepartureDateTime = `${manualAirportFlightInfo.departureDate}T${manualAirportFlightInfo.departureTimeOfDay}`;
      } else {
        // Default: pickup time + 2 hours
        const pickupDate = new Date(pickupDateTime);
        pickupDate.setHours(pickupDate.getHours() + 2);
        manualDepartureDateTime = formatDateTimeForApi(pickupDate);
      }
    }
    const dropoffDateTime = isManualAirportDropoff && manualDepartureDateTime
      ? formatDateTimeForApi(manualDepartureDateTime)
      : (formatDateTimeForApi(dropoff_loc?.datetime) || pickupDateTime);
    let dropoff;
    if (dropoff_loc?.isAsDirected || dropoff_loc?.type === 'as_directed') {
      // As Directed dropoff - passenger will direct driver to final destination
      const asDirectedComment = ['Passenger/s will direct to final destination.', dropoffComments].filter(Boolean).join('\n');
      dropoff = createAsDirectedLocation({
        dateTime: dropoffDateTime,
        comments: asDirectedComment
      });
    } else if (dropoff_loc?.isFbo && selectedFbo) {
      // FBO dropoff - use selected FBO's ID and tail number
      dropoff = createFboLocation({
        id: selectedFbo.id,
        dateTime: dropoffDateTime,
        tailNumber: manualAirportFlightInfo.tailNumber || '',
        comments: dropoffComments
      });
    } else if (dropoff_loc?.isAirport && dropoff_loc?.code) {
      // For manual airport dropoffs, use user-entered values or defaults
      // Defaults: airline "XX", flight number "1"
      const airlineCode = isManualAirportDropoff
        ? (manualAirportFlightInfo.airlineCode || 'XX')
        : (dropoff_loc.airlineCode || '');
      const flightNumber = isManualAirportDropoff
        ? (manualAirportFlightInfo.flightNumber || '1')
        : (dropoff_loc.flightNumber || '');
      dropoff = createAirportLocation({
        airport: dropoff_loc.code,
        airline: airlineCode,
        flightNumber: flightNumber,
        dateTime: dropoffDateTime,
        departure: true,
        comments: dropoffComments,
        flight: dropoffFlightData || null
      });
    } else if (dropoff_loc?.isTrainStation && dropoff_loc?.stationCode) {
      // Train station dropoff - use manually entered departure time if available
      let trainDropoffDateTime = dropoffDateTime;
      if (trainStationInfo.departureDate) {
        if (trainStationInfo.departureTime) {
          trainDropoffDateTime = `${trainStationInfo.departureDate}T${trainStationInfo.departureTime}`;
        } else {
          // Default: pickup time + 2 hours if no time specified
          const pickupDate = new Date(pickupDateTime);
          pickupDate.setHours(pickupDate.getHours() + 2);
          trainDropoffDateTime = formatDateTimeForApi(pickupDate);
        }
      }
      dropoff = createTrainStationLocation({
        station: dropoff_loc.stationCode,
        dateTime: trainDropoffDateTime,
        railroad: trainStationInfo.railroad || '',
        trainNumber: trainStationInfo.trainNumber || '',
        comments: dropoffComments
      });
    } else {
      // Address location - both id and placeId should be the Google Place ID
      const dropoffPlaceId = dropoff_loc?.placeId || '';
      // If no place_id, include address in comments as fallback for chauffeur
      const dropoffAddrFallback = !dropoffPlaceId && dropoff_loc?.details
        ? `Address: ${dropoff_loc.details}`
        : '';
      const dropoffComment = [dropoffAddrFallback, dropoffComments].filter(Boolean).join('\n');
      dropoff = createAddressLocation({
        id: dropoffPlaceId,
        placeId: dropoffPlaceId,
        dateTime: dropoffDateTime,
        comments: dropoffComment
      });
    }

    // Build stops - support airport, address, and as_directed types
    // Generate stop comments based on end times for meetings, events, restaurants
    // Merge auto-generated comments with user-entered stop comments
    const bookingStops = stops_list.map(stop => {
      const stopDateTime = formatDateTimeForApi(stop.datetime) || pickupDateTime;
      const autoComment = generateStopComment(stop);
      const userComment = stop.comments || '';
      const stopComment = [autoComment, userComment].filter(Boolean).join('\n');

      // Check for as_directed stop type
      if (stop.isAsDirected || stop.location?.isAsDirected || stop.location?.type === 'as_directed') {
        return createAsDirectedLocation({
          dateTime: stopDateTime,
          comments: userComment
        });
      }

      if (stop.location?.isAirport && stop.location?.code) {
        return createAirportLocation({
          airport: stop.location.code,
          airline: stop.location.airlineCode || '',
          flightNumber: stop.location.flightNumber || '',
          dateTime: stopDateTime,
          departure: false,
          comments: stopComment
        });
      } else if (stop.location?.isTrainStation && stop.location?.stationCode) {
        // Train station stop
        return createTrainStationLocation({
          station: stop.location.stationCode,
          dateTime: stopDateTime,
          railroad: '',
          trainNumber: '',
          comments: stopComment
        });
      } else {
        // Address location - both id and placeId should be the Google Place ID
        const stopPlaceId = stop.location?.placeId || '';
        // If no place_id, include address in comments as fallback for chauffeur
        let finalStopComment = stopComment;
        if (!stopPlaceId && stop.location?.details) {
          const addressComment = `Address: ${stop.location.details}`;
          finalStopComment = finalStopComment ? `${finalStopComment}\n${addressComment}` : addressComment;
        }
        return createAddressLocation({
          id: stopPlaceId,
          placeId: stopPlaceId,
          dateTime: stopDateTime,
          comments: finalStopComment
        });
      }
    });

    // Build passengers with proper name parsing
    // Include urbanride_pax_id if available to reuse existing Urbanride profiles
    // Include extra guests count for each passenger
    const bookingPassengers = passengers.map(p =>
      createPassenger({
        id: p.urbanride_pax_id || undefined, // Urbanride pax ID for profile reuse
        firstName: p.firstName || 'Guest',
        lastName: p.lastName || '',
        mobile: p.mobile || '',
        email: p.email || '',
        title: '',
        vip: false,
        extra: p.extra || 0 // Additional unnamed guests traveling with this passenger
      })
    );

    // If no passengers, add a placeholder
    if (bookingPassengers.length === 0) {
      bookingPassengers.push(
        createPassenger({
          firstName: 'Guest',
          lastName: '',
          mobile: '',
          email: '',
          title: '',
          vip: false
        })
      );
    }

    return {
      pickup,
      dropoff,
      stops: bookingStops,
      passengers: bookingPassengers,
      clientComments,
      asQuote: false
    };
  };

  /**
   * Validate flight info for manual airport dropoff via Urbanride API
   * Populates the destination airport (otherAirport) and verifies the flight exists
   */
  const handleValidateFlight = async () => {
    const { airlineCode, flightNumber, departureDate, departureTimeOfDay } = manualAirportFlightInfo;

    // Require airline and flight number for validation
    if (!airlineCode || !flightNumber) {
      setFlightValidationError('Please enter both airline code and flight number to validate');
      return;
    }

    if (!departureDate) {
      setFlightValidationError('Please enter the departure date');
      return;
    }

    // Get the airport code from dropoff location
    const airportCode = dropoffLocation?.code;
    if (!airportCode) {
      setFlightValidationError('Airport code not found');
      return;
    }

    setFlightValidating(true);
    setFlightValidationError('');
    setFlightValidated(false);
    setDepartureValidatedFlight(null);

    try {
      // Combine date and time for the API call (use noon if no time specified)
      const dateTime = departureTimeOfDay
        ? `${departureDate}T${departureTimeOfDay}`
        : `${departureDate}T12:00`;

      const result = await validateFlight({
        airport: airportCode.toUpperCase(),
        airline: airlineCode.toUpperCase(),
        flightNumber: flightNumber,
        dateTime: dateTime,
        departure: true  // true for departure (dropoff at airport)
      });

      // Extract the flight data from the nested response
      const flightData = result?.flight || result;

      // Store the full validated flight data for inclusion in the booking payload
      setDepartureValidatedFlight(flightData);

      // Get the scheduled departure time
      const scheduledTime = flightData?.departureTimeScheduled ||
                           flightData?.departureTime ||
                           flightData?.scheduledTime ||
                           flightData?.time;

      // Get the destination airport
      const destinationAirport = flightData?.destinationAirport || flightData?.arrivalAirport || '';

      // Update the flight info with validated data
      if (scheduledTime) {
        const scheduledDateTime = new Date(scheduledTime);
        const hours = String(scheduledDateTime.getHours()).padStart(2, '0');
        const minutes = String(scheduledDateTime.getMinutes()).padStart(2, '0');

        setManualAirportFlightInfo(prev => ({
          ...prev,
          departureTimeOfDay: `${hours}:${minutes}`,
          otherAirport: destinationAirport
        }));
      } else if (destinationAirport) {
        // Even without time, set the destination
        setManualAirportFlightInfo(prev => ({
          ...prev,
          otherAirport: destinationAirport
        }));
      }

      setFlightValidated(true);
    } catch (err) {
      console.error('Flight validation error:', err);
      setFlightValidationError(err.message || 'Flight not found or validation failed');
      setFlightValidated(false);
      setDepartureValidatedFlight(null);
    } finally {
      setFlightValidating(false);
    }
  };

  /**
   * Validate arrival flight info for manual airport pickup via Urbanride API
   * Populates the origin airport and verifies the flight exists
   */
  const handleValidateArrivalFlight = async () => {
    const { airlineCode, flightNumber, arrivalDate, arrivalTimeOfDay } = arrivalFlightInfo;

    // Require airline and flight number for validation
    if (!airlineCode || !flightNumber) {
      setArrivalFlightValidationError('Please enter both airline code and flight number to validate');
      return;
    }

    if (!arrivalDate) {
      setArrivalFlightValidationError('Please enter the arrival date');
      return;
    }

    // Get the airport code from pickup location
    const airportCode = pickupLocation?.code;
    if (!airportCode) {
      setArrivalFlightValidationError('Airport code not found');
      return;
    }

    setArrivalFlightValidating(true);
    setArrivalFlightValidationError('');
    setArrivalFlightValidated(false);
    setArrivalValidatedFlight(null);

    try {
      // Combine date and time for the API call (use noon if no time specified)
      const dateTime = arrivalTimeOfDay
        ? `${arrivalDate}T${arrivalTimeOfDay}`
        : `${arrivalDate}T12:00`;

      const result = await validateFlight({
        airport: airportCode.toUpperCase(),
        airline: airlineCode.toUpperCase(),
        flightNumber: flightNumber,
        dateTime: dateTime,
        departure: false  // false for arrival (pickup at airport)
      });

      // Extract the flight data from the nested response
      const flightData = result?.flight || result;

      // Store the full validated flight data for inclusion in the booking payload
      setArrivalValidatedFlight(flightData);

      // Get the scheduled arrival time
      const scheduledTime = flightData?.arrivalTimeScheduled ||
                           flightData?.arrivalTime ||
                           flightData?.scheduledTime ||
                           flightData?.time;

      // Get the origin airport (where the flight departed from)
      const originAirport = flightData?.departureAirport || flightData?.originAirport || '';

      // Update the flight info with validated data
      if (scheduledTime) {
        const scheduledDateTime = new Date(scheduledTime);
        const hours = String(scheduledDateTime.getHours()).padStart(2, '0');
        const minutes = String(scheduledDateTime.getMinutes()).padStart(2, '0');

        setArrivalFlightInfo(prev => ({
          ...prev,
          arrivalTimeOfDay: `${hours}:${minutes}`,
          originAirport: originAirport
        }));
      } else if (originAirport) {
        // Even without time, set the origin
        setArrivalFlightInfo(prev => ({
          ...prev,
          originAirport: originAirport
        }));
      }

      setArrivalFlightValidated(true);
    } catch (err) {
      console.error('Arrival flight validation error:', err);
      setArrivalFlightValidationError(err.message || 'Flight not found or validation failed');
      setArrivalFlightValidated(false);
      setArrivalValidatedFlight(null);
    } finally {
      setArrivalFlightValidating(false);
    }
  };

  /**
   * Auto-validate flights before booking if not already validated.
   * Handles both cases:
   *   1. Manual entry: flight info in arrivalFlightInfo/manualAirportFlightInfo state
   *   2. Pre-filled from timeline: flight info on pickupLocation/dropoffLocation directly
   * Returns the validated flight data directly for immediate use (since setState is async).
   */
  const autoValidateFlights = async () => {
    let arrivalFlight = arrivalValidatedFlight;
    let departureFlight = departureValidatedFlight;

    const promises = [];

    // --- Arrival flight (airport pickup) ---
    if (!arrivalValidatedFlight && pickupLocation?.isAirport && pickupLocation?.code) {
      // Determine flight info source: manual entry or pre-filled from timeline
      let airlineCode, flightNumber, dateTime;

      if (showArrivalFlightPrompt && arrivalFlightInfo.airlineCode && arrivalFlightInfo.flightNumber && arrivalFlightInfo.arrivalDate) {
        // Manual entry path
        airlineCode = arrivalFlightInfo.airlineCode;
        flightNumber = arrivalFlightInfo.flightNumber;
        dateTime = arrivalFlightInfo.arrivalTimeOfDay
          ? `${arrivalFlightInfo.arrivalDate}T${arrivalFlightInfo.arrivalTimeOfDay}`
          : `${arrivalFlightInfo.arrivalDate}T12:00`;
      } else if (pickupLocation.airlineCode && pickupLocation.flightNumber && pickupLocation.datetime) {
        // Pre-filled from timeline path
        airlineCode = pickupLocation.airlineCode;
        flightNumber = pickupLocation.flightNumber;
        dateTime = formatDateTimeForApi(pickupLocation.datetime);
      }

      if (airlineCode && flightNumber && dateTime) {
        promises.push(
          validateFlight({
            airport: pickupLocation.code.toUpperCase(),
            airline: airlineCode.toUpperCase(),
            flightNumber: String(flightNumber),
            dateTime,
            departure: false
          }).then(result => {
            const flightData = result?.flight || result;
            arrivalFlight = flightData;
            setArrivalValidatedFlight(flightData);
            setArrivalFlightValidated(true);
          }).catch(err => {
            console.warn('Auto-validation of arrival flight failed:', err.message);
          })
        );
      }
    }

    // --- Departure flight (airport dropoff) ---
    if (!departureValidatedFlight && dropoffLocation?.isAirport && dropoffLocation?.code) {
      let airlineCode, flightNumber, dateTime;

      if (showFlightInfoPrompt && manualAirportFlightInfo.airlineCode && manualAirportFlightInfo.flightNumber && manualAirportFlightInfo.departureDate) {
        // Manual entry path
        airlineCode = manualAirportFlightInfo.airlineCode;
        flightNumber = manualAirportFlightInfo.flightNumber;
        dateTime = manualAirportFlightInfo.departureTimeOfDay
          ? `${manualAirportFlightInfo.departureDate}T${manualAirportFlightInfo.departureTimeOfDay}`
          : `${manualAirportFlightInfo.departureDate}T12:00`;
      } else if (dropoffLocation.airlineCode && dropoffLocation.flightNumber && dropoffLocation.datetime) {
        // Pre-filled from timeline path
        airlineCode = dropoffLocation.airlineCode;
        flightNumber = dropoffLocation.flightNumber;
        dateTime = formatDateTimeForApi(dropoffLocation.datetime);
      }

      if (airlineCode && flightNumber && dateTime) {
        promises.push(
          validateFlight({
            airport: dropoffLocation.code.toUpperCase(),
            airline: airlineCode.toUpperCase(),
            flightNumber: String(flightNumber),
            dateTime,
            departure: true
          }).then(result => {
            const flightData = result?.flight || result;
            departureFlight = flightData;
            setDepartureValidatedFlight(flightData);
            setFlightValidated(true);
          }).catch(err => {
            console.warn('Auto-validation of departure flight failed:', err.message);
          })
        );
      }
    }

    if (promises.length > 0) {
      await Promise.all(promises);
    }

    return { arrivalFlight, departureFlight };
  };

  /**
   * Get rates from Urbanride
   */
  const handleGetRates = async () => {
    setError('');

    if (!pickupLocation || !dropoffLocation) {
      setError('Please ensure both pickup and dropoff locations are set');
      return;
    }

    // When pickup time prompt is shown, require user to confirm/set a pickup time
    if (showPickupTimePrompt && !customPickupTime) {
      setError('Please select a pickup time');
      return;
    }

    // For FBO dropoffs, require FBO selection
    if (isDropoffFbo && !selectedFbo) {
      setError('Please select an FBO from the list');
      return;
    }

    // For FBO pickups, require FBO selection
    if (isPickupFbo && !selectedPickupFbo) {
      setError('Please select an FBO for pickup from the list');
      return;
    }

    // Note: For manual airport dropoffs, if user doesn't enter flight info,
    // defaults will be applied: airline "XX", flight "1", departure = pickup + 2 hours
    // Same applies for manual airport pickups: airline "XX", flight "1"

    try {
      setRatesLoading(true);
      setSelectedRate(null); // Clear previous selection when fetching new rates

      // Auto-validate flights before building booking command
      // This ensures flight data is included even if user didn't manually click Verify
      const validatedFlights = await autoValidateFlights();

      // First, resolve any missing place IDs by geocoding addresses
      const resolvedLocations = await resolveLocationPlaceIds();

      const bookingCmd = buildBookingCmd(resolvedLocations, validatedFlights);
      const result = await getRates(bookingCmd);
      setRates(result.rates || []);
      setStep(2);
    } catch (err) {
      setError(`Failed to get rates: ${err.message}`);
    } finally {
      setRatesLoading(false);
    }
  };

  /**
   * Book the trip
   */
  const handleBookTrip = async () => {
    if (!selectedRate) {
      setError('Please select a vehicle');
      return;
    }

    try {
      setError('');
      setBookingLoading(true);
      // Re-validate flights to ensure latest data is in the booking payload
      const validatedFlights = await autoValidateFlights();
      const bookingCmd = buildBookingCmd(null, validatedFlights);
      bookingCmd.vehicleId = selectedRate.vehicleId;
      bookingCmd.confirmationId = selectedRate.confirmationId;

      const result = await bookTrip(bookingCmd);
      setBookingResult(result);

      // Add booking as trip component and update selected components
      await addBookingToTrip(result);

      setStep(3);

      // Auto-close after showing confirmation briefly
      setTimeout(() => {
        if (onSuccess) {
          onSuccess(result);
        }
      }, 2000);
    } catch (err) {
      setError(`Booking failed: ${err.message}`);
    } finally {
      setBookingLoading(false);
    }
  };

  /**
   * Add booking to trip and update selected components with reference
   */
  const addBookingToTrip = async (booking) => {
    if (!tripId || !booking) return;

    setAddingToTrip(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      // Build component title
      // If there are stops, use first stop as destination in title (for wait-and-return or multi-stop trips)
      // This avoids confusing titles like "Hotel to Hotel" when the actual trip is "Hotel to Meeting (and back)"
      const pickupName = pickupLocation?.name || 'Pickup';
      const firstStopName = stops.length > 0 ? (stops[0]?.location?.name || stops[0]?.name) : null;
      const dropoffName = dropoffLocation?.name || 'Dropoff';
      const destinationName = firstStopName || dropoffName;
      const title = `Urbanride: ${pickupName} to ${destinationName}`;

      // Build location string (same logic - use first stop if available)
      const location = `${pickupName} to ${destinationName}`;

      // Build passenger names for description (with +X for extra guests)
      const passengerNames = passengers.map(p => {
        const name = p.name || `${p.firstName} ${p.lastName}`;
        const extra = p.extra || 0;
        return extra > 0 ? `${name} +${extra}` : name;
      }).join(', ');

      // Calculate total passenger count for description
      const totalPassengerCount = passengers.reduce((sum, p) => sum + 1 + (p.extra || 0), 0);

      // Helper function to format date as local ISO string (preserves local time, no UTC conversion)
      // IMPORTANT: Extracts time directly from ISO string to avoid browser timezone conversion
      const formatLocalDateTime = (dateInput) => {
        // If it's already an ISO string, extract directly to preserve the original time
        if (typeof dateInput === 'string') {
          const match = dateInput.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
          if (match) {
            const [, year, month, day, hours, minutes, seconds = '00'] = match;
            return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
          }
        }

        // Fallback for Date objects or non-ISO strings
        // WARNING: This uses browser local time, so only use for user-entered times
        const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
        if (isNaN(d.getTime())) return new Date().toISOString().slice(0, 19);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const seconds = String(d.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      };

      // Build start datetime - use arrival flight info for manual airport pickups,
      // custom pickup time for hotel pickups, or fallback to pickupLocation datetime
      // IMPORTANT: Use formatLocalDateTime instead of toISOString() to preserve local timezone
      const isManualAirportPickup = showArrivalFlightPrompt && pickupLocation?.isAirport;
      let startDateTime;
      if (isManualAirportPickup && arrivalFlightInfo.arrivalDate && arrivalFlightInfo.arrivalTimeOfDay) {
        // For manual airport pickups, use the validated arrival time (already local)
        startDateTime = `${arrivalFlightInfo.arrivalDate}T${arrivalFlightInfo.arrivalTimeOfDay}:00`;
      } else if (showPickupTimePrompt && customPickupTime) {
        // For hotel pickups, use the custom pickup time (pass string directly to preserve TZ)
        startDateTime = formatLocalDateTime(customPickupTime);
      } else {
        // Fallback to pickupLocation datetime or current time
        // Pass string directly to preserve original timezone
        startDateTime = pickupLocation?.datetime
          ? formatLocalDateTime(pickupLocation.datetime)
          : formatLocalDateTime(new Date());
      }

      // Determine pickup type and details based on location data
      // For airports, isAirport is directly on pickupLocation; for others, flags were copied during initialization
      const pickupIsAirport = pickupLocation?.isAirport;
      const pickupIsHotel = pickupLocation?.isHotel;
      const pickupIsMeeting = pickupLocation?.isMeeting;
      const pickupType = pickupIsAirport ? 'airport' : (pickupIsHotel ? 'hotel' : 'address');

      // Determine dropoff type and details based on location data
      const dropoffIsAirport = dropoffLocation?.isAirport;
      const dropoffIsHotel = dropoffLocation?.isHotel;
      const dropoffIsMeeting = dropoffLocation?.isMeeting;
      const dropoffIsAsDirected = dropoffLocation?.isAsDirected || dropoffLocation?.location?.type === 'as_directed';
      const dropoffType = dropoffIsAsDirected ? 'as_directed' : (dropoffIsAirport ? 'airport' : (dropoffIsHotel ? 'hotel' : 'address'));

      // Create the chauffeur service component
      const componentData = {
        component_type: 'chauffeur_service',
        title: title,
        description: `${selectedRate?.vehicle || 'Vehicle'} - ${passengerNames || 'Guests'}${totalPassengerCount > 1 ? ` (${totalPassengerCount} passengers)` : ''}`,
        start_datetime: startDateTime,
        end_datetime: startDateTime,
        location: location,
        status: 'confirmed',
        confirmation_number: booking.urcn,
        notes: clientComments || '',
        component_data: {
          service_type: 'transfer',
          vehicle_class: selectedRate?.vehicle || '',
          vehicle_image_url: selectedRate?.vehicleImageUrl || '',
          pickup_location: pickupName,
          // For airports, details is directly on pickupLocation; for others it's in location.details
          pickup_address: pickupIsAirport
            ? (pickupLocation?.details || pickupLocation?.name || pickupName)
            : (pickupLocation?.location?.details || pickupLocation?.location?.name || pickupLocation?.details || pickupName),
          dropoff_location: dropoffName,
          dropoff_address: dropoffIsAirport
            ? (dropoffLocation?.details || dropoffLocation?.name || dropoffName)
            : (dropoffLocation?.location?.details || dropoffLocation?.location?.name || dropoffLocation?.details || dropoffName),
          pickup_type: pickupType,
          dropoff_type: dropoffType,
          pickup_place_id: pickupLocation?.placeId || '',
          dropoff_place_id: dropoffIsAsDirected ? '' : (dropoffLocation?.placeId || ''),
          pickup_datetime: startDateTime,
          pickup_comments: pickupComments || '',
          dropoff_comments: dropoffComments || '',
          // Airport pickup details - for manual airport pickups use arrivalFlightInfo
          ...(pickupIsAirport && {
            pickup_airport_code: pickupLocation?.code || '',
            pickup_airline_code: isManualAirportPickup
              ? (arrivalFlightInfo.airlineCode || '')
              : (pickupLocation?.airlineCode || ''),
            pickup_flight_number: isManualAirportPickup
              ? (arrivalFlightInfo.flightNumber || '')
              : (pickupLocation?.flightNumber || ''),
            pickup_origin_airport: isManualAirportPickup
              ? (arrivalFlightInfo.originAirport || '')
              : ''
          }),
          // Airport dropoff details - for manual airport dropoffs use manualAirportFlightInfo
          ...(dropoffIsAirport && {
            dropoff_airport_code: dropoffLocation?.code || '',
            dropoff_airline_code: showFlightInfoPrompt
              ? (manualAirportFlightInfo.airlineCode || '')
              : (dropoffLocation?.airlineCode || ''),
            dropoff_flight_number: showFlightInfoPrompt
              ? (manualAirportFlightInfo.flightNumber || '')
              : (dropoffLocation?.flightNumber || ''),
            dropoff_destination_airport: showFlightInfoPrompt
              ? (manualAirportFlightInfo.otherAirport || '')
              : ''
          }),
          // Additional stops with full details
          stops: stops.map(s => {
            const stopIsAirport = s.location?.isAirport || s.isAirport;
            const stopIsHotel = s.isHotel;
            const stopIsMeeting = s.isMeeting;
            const stopIsAsDirected = s.isAsDirected || s.location?.isAsDirected || s.location?.type === 'as_directed';
            const stopType = stopIsAsDirected ? 'as_directed' : (stopIsAirport ? 'airport' : (stopIsHotel ? 'hotel' : 'address'));
            const stopComment = generateStopComment(s);
            return {
              type: stopType,
              location: stopIsAsDirected ? 'As Directed' : (s.location?.name || 'Stop'),
              // For airport stops, properties are directly on s.location
              location_address: stopIsAsDirected ? 'Passenger/s will direct to one or more stops.' : (s.location?.details || s.location?.name || 'Stop'),
              location_id: stopIsAsDirected ? '' : (s.location?.placeId || ''),
              // Include auto-generated comment and user-entered comment
              comments: [stopComment, s.comments || ''].filter(Boolean).join('\n') || '',
              // Airport stop details - properties are directly on s.location for airports
              ...(stopIsAirport && {
                airport_code: s.location?.code || '',
                airline_code: s.location?.airlineCode || '',
                flight_number: s.location?.flightNumber || ''
              })
            };
          }),
          // Passengers (include extra guests count)
          passengers: passengers.map(p => ({
            name: p.name || `${p.firstName} ${p.lastName}`,
            email: p.email || '',
            phone: p.mobile || '',
            vip: false,
            isExecutive: p.isExecutive || false,
            executiveId: p.executiveId || null,
            extra: p.extra || 0
          })),
          // Rate details
          rate: selectedRate?.rate || '',
          rate_ext: selectedRate?.rateExt || '',
          capacity: selectedRate?.capacity || 0,
          luggage: selectedRate?.luggage || 0,
          cancellation_policy: selectedRate?.cancellationPolicy || '',
          // Booking reference
          urbanride_urcn: booking.urcn,
          urbanride_reservation_id: booking.reservationId || null,
          booking_mode: apiMode,
          // Link to selected components
          linked_component_ids: selectedComponents.map(c => c.originalId || c.id)
        }
      };

      // Create the chauffeur service component
      await axios.post(`${apiBase}/trips/${tripId}/components`, componentData, { headers });

      // Update each selected component with the Urbanride booking reference
      // Group selected components by their originalId (parent component) to track which specific legs were booked
      const componentGroups = {};
      selectedComponents.forEach(c => {
        const parentId = c.originalId || c.id;
        if (!componentGroups[parentId]) {
          componentGroups[parentId] = {
            originalComp: c,
            legIndices: []
          };
        }
        // Track which specific leg indices were selected for this booking
        if (c.isFlightLeg && c.legIndex !== undefined) {
          componentGroups[parentId].legIndices.push(c.legIndex);
        }
      });

      for (const [componentId, group] of Object.entries(componentGroups)) {
        try {
          const existingData = group.originalComp.originalComponent?.component_data || group.originalComp.component_data || {};

          // Merge with any existing booked leg indices (in case of multiple bookings)
          const existingLegIndices = existingData.urbanride_booking_leg_indices || [];
          const mergedLegIndices = [...new Set([...existingLegIndices, ...group.legIndices])];

          // Create a booking object with full itinerary and passenger data
          const bookingObject = {
            urcn: booking.urcn,
            booking_mode: apiMode,
            vehicle_class: selectedRate?.vehicle || '',
            // Pickup details
            pickup_type: pickupType,
            pickup_location: pickupName,
            pickup_address: pickupIsAirport
              ? (pickupLocation?.details || pickupLocation?.name || pickupName)
              : (pickupLocation?.location?.details || pickupLocation?.location?.name || pickupLocation?.details || pickupName),
            pickup_datetime: startDateTime,
            pickup_comments: pickupComments || '',
            ...(pickupIsAirport && {
              pickup_airport_code: pickupLocation?.code || '',
              pickup_airline_code: pickupLocation?.airlineCode || '',
              pickup_flight_number: pickupLocation?.flightNumber || ''
            }),
            // Dropoff details
            dropoff_type: dropoffType,
            dropoff_location: dropoffName,
            dropoff_address: dropoffIsAirport
              ? (dropoffLocation?.details || dropoffLocation?.name || dropoffName)
              : (dropoffLocation?.location?.details || dropoffLocation?.location?.name || dropoffLocation?.details || dropoffName),
            dropoff_comments: dropoffComments || '',
            ...(dropoffIsAirport && {
              dropoff_airport_code: dropoffLocation?.code || '',
              dropoff_airline_code: dropoffLocation?.airlineCode || '',
              dropoff_flight_number: dropoffLocation?.flightNumber || ''
            }),
            // Stops
            stops: stops.map(s => {
              const stopIsAirport = s.location?.isAirport || s.isAirport;
              const stopIsHotel = s.isHotel;
              const stopType = stopIsAirport ? 'airport' : (stopIsHotel ? 'hotel' : 'address');
              const stopComment = generateStopComment(s);
              return {
                type: stopType,
                location: s.location?.name || 'Stop',
                location_address: s.location?.details || s.location?.name || 'Stop',
                comments: [stopComment, s.comments || ''].filter(Boolean).join('\n') || '',
                ...(stopIsAirport && {
                  airport_code: s.location?.code || '',
                  airline_code: s.location?.airlineCode || '',
                  flight_number: s.location?.flightNumber || ''
                })
              };
            }),
            // Passengers
            passengers: passengers.map(p => ({
              name: p.name || `${p.firstName} ${p.lastName}`,
              email: p.email || '',
              phone: p.mobile || '',
              isExecutive: p.isExecutive || false
            })),
            // Leg indices for this booking (which flight legs are covered)
            ...(group.legIndices.length > 0 && { leg_indices: group.legIndices })
          };

          // Append to existing urbanride_bookings array, or create new array
          const existingBookings = existingData.urbanride_bookings || [];
          const updatedBookings = [...existingBookings, bookingObject];

          // Update component with Urbanride booking reference
          // Store in urbanride_bookings array for multiple booking support
          // Also keep legacy urbanride_booking_urcn for backward compatibility
          await axios.put(`${apiBase}/trips/${tripId}/components/${componentId}`, {
            component_data: {
              ...existingData,
              urbanride_bookings: updatedBookings,
              // Legacy field - keep for backward compatibility, stores latest URCN
              urbanride_booking_urcn: booking.urcn,
              urbanride_booking_mode: apiMode,
              // Only include leg indices if this is a flight with legs
              ...(mergedLegIndices.length > 0 && { urbanride_booking_leg_indices: mergedLegIndices })
            }
          }, { headers });
        } catch (updateErr) {
          console.warn(`Failed to update component ${componentId}:`, updateErr);
        }
      }

      // Notify parent to refresh components
      if (onUpdateComponents) {
        onUpdateComponents();
      }

    } catch (err) {
      console.error('Failed to add booking to trip:', err);
      setError(`Booking confirmed (${booking.urcn}), but failed to add to trip: ${err.message}`);
    } finally {
      setAddingToTrip(false);
    }
  };

  /**
   * Handle done button
   */
  const handleDone = () => {
    if (onSuccess) {
      onSuccess(bookingResult);
    }
  };

  /**
   * Submit an update to an existing Urbanride reservation (edit mode).
   * Calls /urbanride/trip/update with the URCN and updated BookingCmd,
   * then PATCHes the existing chauffeur_service component with new data.
   */
  const handleUpdateTripSubmit = async () => {
    if (!selectedRate) {
      setError('Please select a vehicle');
      return;
    }
    if (!existingUrcn) {
      setError('Missing reservation reference (URCN). Cannot update.');
      return;
    }

    try {
      setError('');
      setBookingLoading(true);
      const validatedFlights = await autoValidateFlights();
      const bookingCmd = buildBookingCmd(null, validatedFlights);
      bookingCmd.vehicleId = selectedRate.vehicleId;
      bookingCmd.confirmationId = selectedRate.confirmationId;

      const result = await updateTrip(existingUrcn, bookingCmd);
      // The update API returns the same urcn (reservation number stays the same)
      const updateResult = { urcn: existingUrcn, ...result };
      setBookingResult(updateResult);

      // Update the existing component with new booking details
      await updateBookingInTrip(updateResult);

      setStep(3);

      setTimeout(() => {
        if (onSuccess) {
          onSuccess(updateResult);
        }
      }, 2000);
    } catch (err) {
      setError(`Update failed: ${err.message}`);
    } finally {
      setBookingLoading(false);
    }
  };

  /**
   * Update the existing chauffeur_service trip component after a successful reservation update.
   */
  const updateBookingInTrip = async (booking) => {
    if (!tripId || !booking || !existingComponentId) return;

    setAddingToTrip(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const pickupName = pickupLocation?.name || 'Pickup';
      const firstStopName = stops.length > 0 ? (stops[0]?.location?.name || stops[0]?.name) : null;
      const dropoffName = dropoffLocation?.name || 'Dropoff';
      const destinationName = firstStopName || dropoffName;
      const title = `Urbanride: ${pickupName} to ${destinationName}`;
      const location = `${pickupName} to ${destinationName}`;

      const passengerNames = passengers.map(p => {
        const name = p.name || `${p.firstName} ${p.lastName}`;
        const extra = p.extra || 0;
        return extra > 0 ? `${name} +${extra}` : name;
      }).join(', ');

      const totalPassengerCount = passengers.reduce((sum, p) => sum + 1 + (p.extra || 0), 0);

      const formatLocalDateTime = (dateInput) => {
        if (typeof dateInput === 'string') {
          const match = dateInput.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
          if (match) {
            const [, year, month, day, hours, minutes, seconds = '00'] = match;
            return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
          }
        }
        const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
        if (isNaN(d.getTime())) return new Date().toISOString().slice(0, 19);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const seconds = String(d.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      };

      const isManualAirportPickup = showArrivalFlightPrompt && pickupLocation?.isAirport;
      let startDateTime;
      if (isManualAirportPickup && arrivalFlightInfo.arrivalDate && arrivalFlightInfo.arrivalTimeOfDay) {
        startDateTime = `${arrivalFlightInfo.arrivalDate}T${arrivalFlightInfo.arrivalTimeOfDay}:00`;
      } else if (customPickupTime) {
        startDateTime = formatLocalDateTime(customPickupTime);
      } else {
        startDateTime = pickupLocation?.datetime
          ? formatLocalDateTime(pickupLocation.datetime)
          : formatLocalDateTime(new Date());
      }

      const pickupIsAirport = pickupLocation?.isAirport;
      const pickupIsHotel = pickupLocation?.isHotel;
      const pickupType = pickupIsAirport ? 'airport' : (pickupIsHotel ? 'hotel' : 'address');

      const dropoffIsAirport = dropoffLocation?.isAirport;
      const dropoffIsHotel = dropoffLocation?.isHotel;
      const dropoffIsAsDirected = dropoffLocation?.isAsDirected;
      const dropoffType = dropoffIsAsDirected ? 'as_directed' : (dropoffIsAirport ? 'airport' : (dropoffIsHotel ? 'hotel' : 'address'));

      // PUT the existing chauffeur_service component with updated details
      await axios.put(`${apiBase}/trips/${tripId}/components/${existingComponentId}`, {
        title,
        description: `${selectedRate?.vehicle || 'Vehicle'} - ${passengerNames || 'Guests'}${totalPassengerCount > 1 ? ` (${totalPassengerCount} passengers)` : ''}`,
        start_datetime: startDateTime,
        end_datetime: startDateTime,
        location,
        component_data: {
          ...(existingComponentData || {}),
          service_type: 'transfer',
          vehicle_class: selectedRate?.vehicle || '',
          vehicle_image_url: selectedRate?.vehicleImageUrl || '',
          pickup_location: pickupName,
          pickup_address: pickupIsAirport
            ? (pickupLocation?.details || pickupLocation?.name || pickupName)
            : (pickupLocation?.location?.details || pickupLocation?.location?.name || pickupLocation?.details || pickupName),
          dropoff_location: dropoffName,
          dropoff_address: dropoffIsAirport
            ? (dropoffLocation?.details || dropoffLocation?.name || dropoffName)
            : (dropoffLocation?.location?.details || dropoffLocation?.location?.name || dropoffLocation?.details || dropoffName),
          pickup_type: pickupType,
          dropoff_type: dropoffType,
          pickup_place_id: pickupLocation?.placeId || '',
          dropoff_place_id: dropoffIsAsDirected ? '' : (dropoffLocation?.placeId || ''),
          pickup_datetime: startDateTime,
          pickup_comments: pickupComments || '',
          dropoff_comments: dropoffComments || '',
          ...(pickupIsAirport && {
            pickup_airport_code: pickupLocation?.code || '',
            pickup_airline_code: isManualAirportPickup
              ? (arrivalFlightInfo.airlineCode || '')
              : (pickupLocation?.airlineCode || ''),
            pickup_flight_number: isManualAirportPickup
              ? (arrivalFlightInfo.flightNumber || '')
              : (pickupLocation?.flightNumber || ''),
            pickup_origin_airport: isManualAirportPickup
              ? (arrivalFlightInfo.originAirport || '')
              : (pickupLocation?.otherAirport || '')
          }),
          ...(dropoffIsAirport && {
            dropoff_airport_code: dropoffLocation?.code || '',
            dropoff_airline_code: showFlightInfoPrompt
              ? (manualAirportFlightInfo.airlineCode || '')
              : (dropoffLocation?.airlineCode || ''),
            dropoff_flight_number: showFlightInfoPrompt
              ? (manualAirportFlightInfo.flightNumber || '')
              : (dropoffLocation?.flightNumber || ''),
            dropoff_destination_airport: showFlightInfoPrompt
              ? (manualAirportFlightInfo.otherAirport || '')
              : (dropoffLocation?.otherAirport || '')
          }),
          stops: stops.map(s => {
            const stopIsAirport = s.location?.isAirport || s.isAirport;
            const stopIsHotel = s.isHotel;
            const stopIsAsDirected = s.isAsDirected || s.location?.isAsDirected;
            const stopType = stopIsAsDirected ? 'as_directed' : (stopIsAirport ? 'airport' : (stopIsHotel ? 'hotel' : 'address'));
            const stopComment = generateStopComment(s);
            return {
              type: stopType,
              location: stopIsAsDirected ? 'As Directed' : (s.location?.name || 'Stop'),
              location_address: stopIsAsDirected ? 'Passenger/s will direct to one or more stops.' : (s.location?.details || s.location?.name || 'Stop'),
              location_id: stopIsAsDirected ? '' : (s.location?.placeId || ''),
              comments: [stopComment, s.comments || ''].filter(Boolean).join('\n') || '',
              ...(stopIsAirport && {
                airport_code: s.location?.code || '',
                airline_code: s.location?.airlineCode || '',
                flight_number: s.location?.flightNumber || ''
              })
            };
          }),
          passengers: passengers.map(p => ({
            name: p.name || `${p.firstName} ${p.lastName}`,
            email: p.email || '',
            phone: p.mobile || '',
            vip: p.vip || false,
            isExecutive: p.isExecutive || false,
            executiveId: p.executiveId || null,
            extra: p.extra || 0
          })),
          rate: selectedRate?.rate || '',
          rate_ext: selectedRate?.rateExt || '',
          capacity: selectedRate?.capacity || 0,
          luggage: selectedRate?.luggage || 0,
          cancellation_policy: selectedRate?.cancellationPolicy || '',
          urbanride_urcn: booking.urcn,
          booking_mode: apiMode,
          linked_component_ids: linkedComponentIds,
          notes: clientComments || ''
        }
      }, { headers });

      // Update references on linked timeline components
      await updateLinkedComponentReferences(booking.urcn, headers, apiBase);

      if (onUpdateComponents) {
        onUpdateComponents();
      }
    } catch (err) {
      console.error('Failed to update booking in trip:', err);
      setError(`Reservation updated (${booking.urcn}), but failed to save changes to trip: ${err.message}`);
    } finally {
      setAddingToTrip(false);
    }
  };

  /**
   * Update urbanride_bookings references on linked timeline components.
   * Adds references to newly linked components and removes from unlinked ones.
   */
  const updateLinkedComponentReferences = async (urcn, headers, apiBase) => {
    const oldLinkedIds = existingComponentData?.linked_component_ids || [];
    const newLinkedIds = linkedComponentIds;

    // Build booking reference object for newly linked components
    const bookingRef = {
      urcn: urcn,
      booking_mode: apiMode,
      vehicle_class: selectedRate?.vehicle || '',
      pickup: pickupLocation?.name || '',
      dropoff: dropoffLocation?.name || '',
      passengers: passengers.map(p => p.name || `${p.firstName} ${p.lastName}`).join(', ')
    };

    // Remove references from components no longer in the itinerary
    const removedIds = oldLinkedIds.filter(id => !newLinkedIds.includes(id));
    for (const compId of removedIds) {
      try {
        const resp = await axios.get(`${apiBase}/trips/${tripId}/components/${compId}`, { headers });
        const compData = resp.data?.component_data || {};
        const updatedBookings = (compData.urbanride_bookings || []).filter(b => b.urcn !== urcn);
        await axios.put(`${apiBase}/trips/${tripId}/components/${compId}`, {
          component_data: {
            ...compData,
            urbanride_bookings: updatedBookings,
            ...(compData.urbanride_booking_urcn === urcn && { urbanride_booking_urcn: '' })
          }
        }, { headers });
      } catch (err) {
        console.warn(`Failed to remove reference from component ${compId}:`, err);
      }
    }

    // Add references to newly linked components
    const addedIds = newLinkedIds.filter(id => !oldLinkedIds.includes(id));
    for (const compId of addedIds) {
      try {
        const resp = await axios.get(`${apiBase}/trips/${tripId}/components/${compId}`, { headers });
        const compData = resp.data?.component_data || {};
        const existingBookings = compData.urbanride_bookings || [];
        // Don't add duplicate
        if (existingBookings.some(b => b.urcn === urcn)) continue;
        await axios.put(`${apiBase}/trips/${tripId}/components/${compId}`, {
          component_data: {
            ...compData,
            urbanride_bookings: [...existingBookings, bookingRef],
            urbanride_booking_urcn: urcn
          }
        }, { headers });
      } catch (err) {
        console.warn(`Failed to add reference to component ${compId}:`, err);
      }
    }
  };

  /**
   * Remove a passenger
   */
  const removePassenger = (index) => {
    setPassengers(passengers.filter((_, i) => i !== index));
  };

  /**
   * Look up passenger by name - checks executive first, then Urbanride API
   */
  const lookupPassenger = async (firstName, lastName) => {
    if (!firstName?.trim() || !lastName?.trim()) {
      setPassengerLookupState('idle');
      setMatchedExecutive(null);
      setUrbanrideSearchResults([]);
      setSelectedUrbanrideProfile(null);
      return;
    }

    setPassengerLookupState('searching');
    setSelectedUrbanrideProfile(null);

    // First, check if there's a matching executive
    const { exec, urbanridePaxId } = findExecutiveAndUrbanrideId(
      null,
      firstName.trim(),
      lastName.trim()
    );

    setMatchedExecutive(exec);

    // If executive has Urbanride profile, we're done
    if (exec && urbanridePaxId) {
      setPassengerLookupState('found_executive');
      setSelectedUrbanrideProfile({
        id: urbanridePaxId,
        firstName: exec.first_name,
        lastName: exec.last_name,
        email: exec.email,
        mobile: exec.mobile || exec.phone
      });
      return;
    }

    // Search Urbanride API for existing passenger profiles
    try {
      const searchQuery = `${firstName.trim()} ${lastName.trim()}`;
      const result = await searchPassengers({ input: searchQuery });
      const passengers = result?.data?.passengers || result?.passengers || [];
      const passengerList = Array.isArray(passengers) ? passengers : [];

      setUrbanrideSearchResults(passengerList);

      if (passengerList.length > 0) {
        setPassengerLookupState('found_urbanride');
      } else {
        setPassengerLookupState('not_found');
      }
    } catch (e) {
      console.error('Urbanride passenger search failed:', e);
      setUrbanrideSearchResults([]);
      setPassengerLookupState('not_found');
    }
  };

  /**
   * Select an Urbanride profile from search results
   */
  const selectUrbanrideProfile = (profile) => {
    setSelectedUrbanrideProfile(profile);
    // Auto-fill email and mobile from the profile if not already set
    if (profile) {
      setNewPassenger(prev => ({
        ...prev,
        email: prev.email || profile.email || '',
        mobile: prev.mobile || profile.mobile || ''
      }));
    }
  };

  /**
   * Save selected Urbanride profile to the matched executive
   */
  const saveUrbanrideProfileToExecutive = async () => {
    if (!matchedExecutive || !selectedUrbanrideProfile) return;

    setSavingToExecutive(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const { mode } = getConfigStatus();

      // Update the executive with the Urbanride profile ID
      const updateData = mode === 'production'
        ? { urbanride_pax_id_production: selectedUrbanrideProfile.id }
        : { urbanride_pax_id_test: selectedUrbanrideProfile.id };

      await axios.put(`${apiBase}/executives/${matchedExecutive.id}`, updateData, { headers });

      // Update the local executive data in our arrays
      const updateExecInArray = (arr) => arr.map(e =>
        e.id === matchedExecutive.id
          ? { ...e, ...updateData }
          : e
      );

      // Note: This updates the local state but the parent component may need to refresh
      // The change will persist in the database for future bookings
    } catch (e) {
      console.error('Failed to save Urbanride profile to executive:', e);
    } finally {
      setSavingToExecutive(false);
    }
  };

  /**
   * Add a new passenger with the selected profile (or create new)
   */
  const addNewPassenger = async () => {
    if (!newPassenger.firstName.trim() || !newPassenger.lastName.trim()) {
      return; // Require at least first and last name
    }

    // If there's a matched executive and we selected an Urbanride profile, save it
    if (matchedExecutive && selectedUrbanrideProfile && !matchedExecutive.urbanride_pax_id_test && !matchedExecutive.urbanride_pax_id_production) {
      await saveUrbanrideProfileToExecutive();
    }

    const passengerToAdd = {
      name: `${newPassenger.firstName.trim()} ${newPassenger.lastName.trim()}`,
      firstName: newPassenger.firstName.trim(),
      lastName: newPassenger.lastName.trim(),
      email: newPassenger.email.trim() || selectedUrbanrideProfile?.email || matchedExecutive?.email || '',
      mobile: newPassenger.mobile.trim() || selectedUrbanrideProfile?.mobile || matchedExecutive?.mobile || matchedExecutive?.phone || '',
      isExecutive: !!matchedExecutive,
      executiveId: matchedExecutive?.id || null,
      urbanride_pax_id: selectedUrbanrideProfile?.id || null,
      extra: newPassenger.extra || 0,
      manuallyAdded: true // Flag to preserve this passenger when re-extracting
    };

    setPassengers([...passengers, passengerToAdd]);
    resetAddPassengerForm();
  };

  /**
   * Reset the add passenger form state
   */
  const resetAddPassengerForm = () => {
    setNewPassenger({ firstName: '', lastName: '', email: '', mobile: '', extra: 0 });
    setPassengerLookupState('idle');
    setMatchedExecutive(null);
    setUrbanrideSearchResults([]);
    setSelectedUrbanrideProfile(null);
    setShowAddPassengerForm(false);
  };

  /**
   * Update a passenger's extra guests count
   */
  const updatePassengerExtra = (index, extra) => {
    const updated = [...passengers];
    updated[index] = { ...updated[index], extra: Math.max(0, parseInt(extra) || 0) };
    setPassengers(updated);
  };

  // Calculate total passenger count (passengers + their extra guests)
  const totalPassengerCount = passengers.reduce((total, p) => total + 1 + (p.extra || 0), 0);

  // Filter rates by vehicle capacity
  const availableRates = rates.filter(rate => rate.capacity >= totalPassengerCount);
  const filteredOutCount = rates.length - availableRates.length;

  // Clear selected rate if it's no longer available due to capacity changes
  useEffect(() => {
    if (selectedRate && !availableRates.some(r => r.vehicleId === selectedRate.vehicleId)) {
      setSelectedRate(null);
    }
  }, [availableRates, selectedRate]);

  // Loading state
  if (configLoading) {
    return (
      <div className={sidePanel ? "h-full flex items-center justify-center" : "fixed inset-0 bg-ui-overlay bg-opacity-50 flex items-center justify-center z-[70]"}>
        <div className={sidePanel ? "p-8" : "bg-ui-surface rounded-xl p-8 max-w-md w-full mx-4"}>
          <div className="flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-ui-link animate-spin" />
            <span className="ml-3 text-ui-ink-3">Initializing Urbanride...</span>
          </div>
        </div>
      </div>
    );
  }

  // Not configured state
  if (!apiConfigured) {
    return (
      <div className={sidePanel ? "h-full flex items-center justify-center" : "fixed inset-0 bg-ui-overlay bg-opacity-50 flex items-center justify-center z-[70]"}>
        <div className={sidePanel ? "p-6" : "bg-ui-surface rounded-xl p-6 max-w-md w-full mx-4"}>
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-ui-amber-bg rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-ui-amber-fg" />
            </div>
            <h3 className="text-lg font-semibold text-ui-ink mb-2">Urbanride API Not Configured</h3>
            {autoProvisionAvailable ? (
              <>
                <p className="text-ui-ink-3 mb-4">
                  Your organization is set up for Urbanride. Click below to activate your booking access.
                </p>
                {provisionError && (
                  <div className="mb-4 p-3 bg-ui-red-bg border border-ui-red-line text-ui-red-fg rounded-md text-sm">
                    {provisionError}
                  </div>
                )}
                <div className="flex justify-center gap-3">
                  <button
                    onClick={onCancel}
                    className="px-4 py-2 border border-ui-control rounded-md hover:bg-ui-hover"
                    disabled={provisioning}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleProvisionApiKey}
                    disabled={provisioning}
                    className="inline-flex items-center px-4 py-2 bg-ui-primary text-ui-on-primary rounded-md hover:bg-ui-primary-hover disabled:opacity-50"
                  >
                    {provisioning ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Setting Up...
                      </>
                    ) : (
                      <>
                        <Car className="w-4 h-4 mr-2" />
                        Activate Urbanride
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-ui-ink-3 mb-4">
                  To book chauffeur services, please contact your administrator to set up Urbanride access for your organization.
                </p>
                <div className="flex justify-center gap-3">
                  <button
                    onClick={onCancel}
                    className="px-4 py-2 border border-ui-control rounded-md hover:bg-ui-hover"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={sidePanel ? "h-full" : "fixed inset-0 bg-ui-overlay bg-opacity-50 flex items-center justify-center z-[70]"}>
      <div className={sidePanel ? "bg-ui-surface h-full overflow-hidden flex flex-col border-l border-ui-line" : "bg-ui-surface rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col"}>
        {/* Header */}
        <div className={`${sidePanel ? 'px-4 py-3' : 'px-6 py-4'} border-b border-ui-line flex items-center justify-between bg-gradient-to-r from-ui-blue-strong to-ui-indigo-strong`}>
          <div className="flex items-center gap-3">
            <Car className="w-6 h-6 text-ui-on-primary" />
            <h2 className="text-lg font-semibold text-ui-on-primary">{editMode ? 'Urbanride Quick Edit' : 'Urbanride Quick Booking'}</h2>
          </div>
          <button
            onClick={onCancel}
            className="text-ui-on-primary hover:bg-ui-on-primary/20 p-1 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error display */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-ui-red-bg border border-ui-red-line text-ui-red-fg rounded-md text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* API Mode Badge */}
        {apiMode === 'test' && (
          <div className="mx-6 mt-4 p-2 bg-ui-amber-bg border border-ui-amber-line text-ui-amber-fg rounded-md text-xs text-center">
            TEST MODE - Bookings will not be charged
          </div>
        )}

        {/* Content */}
        <div className={`flex-1 min-h-0 overflow-y-auto ${sidePanel ? 'p-4' : 'p-6'}`}>
          {/* Step 1: Review */}
          {step === 1 && (
            <div className="space-y-6">
              {/* Pending timeline component action bar */}
              {pendingComponent && (
                <div className="bg-ui-blue-bg border border-ui-blue-line rounded-lg p-3 animate-in fade-in slide-in-from-top-2">
                  <p className="text-sm font-medium text-ui-blue-fg mb-2">
                    Add <span className="font-semibold">"{pendingComponent.name}"</span> as:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={placePendingAsPickup}
                      className="px-3 py-1.5 bg-ui-green-strong text-ui-on-primary rounded-md text-xs font-medium hover:bg-ui-green-strong/90"
                    >
                      Pickup
                    </button>
                    <button
                      type="button"
                      onClick={placePendingAsStop}
                      className="px-3 py-1.5 bg-ui-primary text-ui-on-primary rounded-md text-xs font-medium hover:bg-ui-primary-hover"
                    >
                      Stop
                    </button>
                    <button
                      type="button"
                      onClick={placePendingAsDropoff}
                      className="px-3 py-1.5 bg-ui-red-strong text-ui-on-primary rounded-md text-xs font-medium hover:bg-ui-red-strong/90"
                    >
                      Dropoff
                    </button>
                    <button
                      type="button"
                      onClick={dismissPendingComponent}
                      className="px-3 py-1.5 border border-ui-control text-ui-ink-3 rounded-md text-xs font-medium hover:bg-ui-hover"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Tip for side panel mode */}
              {sidePanel && editMode && step === 1 && !pendingComponent && (
                <div className="text-xs text-ui-muted bg-ui-sunk rounded p-2 flex items-center gap-1.5">
                  <MapPin className="w-3 h-3 flex-shrink-0" />
                  Click timeline cards on the left to add locations, or search below.
                </div>
              )}

              {/* Route Summary */}
              <div className="bg-ui-sunk rounded-lg p-4">
                <h4 className="text-sm font-semibold text-ui-ink mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Route
                </h4>

                {editMode ? (
                  <div className="space-y-4">
                    {/* Editable Pickup */}
                    <div className="relative">
                      <label className="block text-xs font-medium text-ui-ink-3 mb-1">Pickup Location</label>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-ui-green-strong flex items-center justify-center flex-shrink-0">
                          <span className="text-ui-on-primary text-xs font-bold">P</span>
                        </div>
                        <div className="flex-1 relative">
                          <input
                            type="text"
                            value={pickupSearchQuery}
                            onChange={(e) => handleEditPickupSearch(e.target.value)}
                            onFocus={() => pickupSearchResults.length > 0 && setShowPickupResults(true)}
                            onBlur={() => setTimeout(() => setShowPickupResults(false), 200)}
                            placeholder="Search pickup location..."
                            className="w-full px-3 py-2 border border-ui-control rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ui-primary"
                          />
                          {pickupSearchLoading && <Loader2 className="absolute right-3 top-2.5 w-4 h-4 animate-spin text-ui-muted" />}
                          {showPickupResults && pickupSearchResults.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-ui-surface border border-ui-line rounded-md shadow-lg max-h-48 overflow-y-auto">
                              {pickupSearchResults.map((loc, i) => (
                                <button
                                  key={i}
                                  type="button"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => selectEditPickupLocation(loc)}
                                  className="w-full text-left px-3 py-2 hover:bg-ui-blue-bg text-sm border-b border-ui-divider last:border-0"
                                >
                                  <div className="font-medium text-ui-ink">{loc.type === 'airport' && loc.id ? `${loc.id} - ` : ''}{loc.name}</div>
                                  {loc.details && <div className="text-xs text-ui-muted">{loc.details}</div>}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      {pickupLocation && (
                        <div className="ml-8 mt-1 text-xs text-ui-green-fg flex items-center gap-1">
                          <Check className="w-3 h-3" /> {pickupLocation.name}
                        </div>
                      )}
                      <input
                        type="text"
                        value={pickupComments}
                        onChange={(e) => setPickupComments(e.target.value)}
                        placeholder="Add pickup comment..."
                        className="ml-8 mt-2 w-[calc(100%-2rem)] px-2 py-1 border border-ui-line rounded text-xs text-ui-ink-3 focus:outline-none focus:ring-1 focus:ring-ui-primary"
                      />
                    </div>

                    {/* Editable Stops */}
                    {stops.map((stop, idx) => (
                      <div key={idx} className="relative">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-ui-primary flex items-center justify-center flex-shrink-0">
                            <span className="text-ui-on-primary text-xs font-bold">{idx + 1}</span>
                          </div>
                          <div className="flex-1 relative">
                            <input
                              type="text"
                              value={stop._searchQuery || stop.location?.name || ''}
                              onChange={(e) => handleEditStopSearch(idx, e.target.value)}
                              placeholder="Search stop location..."
                              className="w-full px-3 py-2 border border-ui-control rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ui-primary"
                            />
                            {stop._searchLoading && <Loader2 className="absolute right-3 top-2.5 w-4 h-4 animate-spin text-ui-muted" />}
                            {stop._showResults && stop._searchResults?.length > 0 && (
                              <div className="absolute z-50 w-full mt-1 bg-ui-surface border border-ui-line rounded-md shadow-lg max-h-48 overflow-y-auto">
                                {stop._searchResults.map((loc, i) => (
                                  <button
                                    key={i}
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => selectEditStopLocation(idx, loc)}
                                    className="w-full text-left px-3 py-2 hover:bg-ui-blue-bg text-sm border-b border-ui-divider last:border-0"
                                  >
                                    <div className="font-medium text-ui-ink">{loc.type === 'airport' && loc.id ? `${loc.id} - ` : ''}{loc.name}</div>
                                    {loc.details && <div className="text-xs text-ui-muted">{loc.details}</div>}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeEditStop(idx)}
                            className="text-ui-red-fg hover:text-ui-red-fg p-1"
                            title="Remove stop"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        {stop.location && (
                          <div className="ml-8 mt-1 text-xs text-ui-link flex items-center gap-1">
                            <Check className="w-3 h-3" /> {stop.location.name}
                          </div>
                        )}
                        <input
                          type="text"
                          value={stop.comments || ''}
                          onChange={(e) => {
                            const updated = [...stops];
                            updated[idx] = { ...updated[idx], comments: e.target.value };
                            setStops(updated);
                          }}
                          placeholder="Add stop comment..."
                          className="ml-8 mt-1 w-[calc(100%-2rem)] px-2 py-1 border border-ui-line rounded text-xs text-ui-ink-3 focus:outline-none focus:ring-1 focus:ring-ui-primary"
                        />
                      </div>
                    ))}

                    {/* Add Stop */}
                    <button
                      type="button"
                      onClick={addEditStop}
                      className="ml-8 flex items-center gap-1.5 text-xs text-ui-link hover:text-ui-blue-fg font-medium"
                    >
                      <span className="w-5 h-5 rounded-full border-2 border-ui-primary flex items-center justify-center text-ui-link font-bold text-xs">+</span>
                      Add Stop
                    </button>

                    {/* Editable Dropoff */}
                    <div className="relative">
                      <label className="block text-xs font-medium text-ui-ink-3 mb-1">Dropoff Location</label>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-ui-red-strong flex items-center justify-center flex-shrink-0">
                          <span className="text-ui-on-primary text-xs font-bold">D</span>
                        </div>
                        <div className="flex-1 relative">
                          <input
                            type="text"
                            value={dropoffSearchQuery}
                            onChange={(e) => handleEditDropoffSearch(e.target.value)}
                            onFocus={() => dropoffSearchResults.length > 0 && setShowDropoffResults(true)}
                            onBlur={() => setTimeout(() => setShowDropoffResults(false), 200)}
                            placeholder="Search dropoff location..."
                            className="w-full px-3 py-2 border border-ui-control rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ui-primary"
                          />
                          {dropoffSearchLoading && <Loader2 className="absolute right-3 top-2.5 w-4 h-4 animate-spin text-ui-muted" />}
                          {showDropoffResults && dropoffSearchResults.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-ui-surface border border-ui-line rounded-md shadow-lg max-h-48 overflow-y-auto">
                              {dropoffSearchResults.map((loc, i) => (
                                <button
                                  key={i}
                                  type="button"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => selectEditDropoffLocation(loc)}
                                  className="w-full text-left px-3 py-2 hover:bg-ui-blue-bg text-sm border-b border-ui-divider last:border-0"
                                >
                                  <div className="font-medium text-ui-ink">{loc.type === 'airport' && loc.id ? `${loc.id} - ` : ''}{loc.name}</div>
                                  {loc.details && <div className="text-xs text-ui-muted">{loc.details}</div>}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => selectEditDropoffLocation({ type: 'as_directed', id: 'as_directed', name: 'As Directed' })}
                        className="ml-8 mt-1 text-xs text-ui-violet-fg hover:text-ui-violet-fg hover:underline"
                      >
                        Set as "As Directed"
                      </button>
                      {dropoffLocation && (
                        <div className="ml-8 mt-1 text-xs text-ui-red-fg flex items-center gap-1">
                          <Check className="w-3 h-3" /> {dropoffLocation.isAsDirected ? 'As Directed' : dropoffLocation.name}
                        </div>
                      )}
                      <input
                        type="text"
                        value={dropoffComments}
                        onChange={(e) => setDropoffComments(e.target.value)}
                        placeholder="Add dropoff comment..."
                        className="ml-8 mt-2 w-[calc(100%-2rem)] px-2 py-1 border border-ui-line rounded text-xs text-ui-ink-3 focus:outline-none focus:ring-1 focus:ring-ui-primary"
                      />
                    </div>
                  </div>
                ) : (
                <div className="space-y-3">
                  {/* Pickup */}
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-ui-green-strong flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-ui-on-primary text-xs font-bold">P</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-ui-ink">
                        {pickupLocation?.name || 'Pickup location'}
                      </div>
                      {pickupLocation?.details && pickupLocation.details !== pickupLocation.name && (
                        <div className="text-xs text-ui-muted">{pickupLocation.details}</div>
                      )}
                      {pickupLocation?.datetime && !showPickupTimePrompt && (
                        <div className="text-xs text-ui-muted">
                          {formatDisplayDateTime(pickupLocation.datetime)}
                          {/* Show indicator when pickup uses meeting/event/restaurant end time */}
                          {(pickupLocation?.isMeeting || pickupLocation?.isEvent || pickupLocation?.isRestaurant) &&
                            pickupLocation?.endDatetime && (
                            <span className="ml-1 text-ui-green-fg">(after {pickupLocation.isMeeting ? 'meeting' : pickupLocation.isEvent ? 'event' : 'dining'} ends)</span>
                          )}
                        </div>
                      )}
                      {showPickupTimePrompt && customPickupTime && (
                        <div className="text-xs text-ui-link font-medium">
                          Custom pickup: {formatDisplayDateTime(customPickupTime)}
                        </div>
                      )}
                      {/* Show arrival flight/FBO info for manual airport/FBO pickups when entered */}
                      {showArrivalFlightPrompt && arrivalFlightInfo.arrivalDate && arrivalFlightInfo.arrivalTimeOfDay && (
                        <div className="text-xs text-ui-link font-medium mt-1">
                          {isPickupFbo ? (
                            <>
                              Arrival: {formatDisplayDateTime(`${arrivalFlightInfo.arrivalDate}T${arrivalFlightInfo.arrivalTimeOfDay}`)}
                              {arrivalFlightInfo.tailNumber && (
                                <span className="ml-1">(Tail: {arrivalFlightInfo.tailNumber})</span>
                              )}
                            </>
                          ) : (
                            <>
                              Flight: {arrivalFlightInfo.airlineCode || '---'} {arrivalFlightInfo.flightNumber || '---'} at {formatDisplayDateTime(`${arrivalFlightInfo.arrivalDate}T${arrivalFlightInfo.arrivalTimeOfDay}`)}
                              {arrivalFlightInfo.originAirport && (
                                <span className="ml-1">from {arrivalFlightInfo.originAirport}</span>
                              )}
                              {arrivalFlightValidated && (
                                <CheckCircle className="w-3 h-3 inline ml-1 text-ui-green-fg" />
                              )}
                            </>
                          )}
                        </div>
                      )}
                      <input
                        type="text"
                        value={pickupComments}
                        onChange={(e) => setPickupComments(e.target.value)}
                        placeholder="Add pickup comment..."
                        className="mt-1 w-full px-2 py-1 border border-ui-line rounded text-xs text-ui-ink-3 focus:outline-none focus:ring-1 focus:ring-ui-primary"
                      />
                    </div>
                  </div>

                  {/* Stops */}
                  {stops.map((stop, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-ui-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-ui-on-primary text-xs font-bold">{idx + 1}</span>
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-ui-ink">
                          {stop.location?.name || 'Stop'}
                        </div>
                        {stop.location?.details && stop.location.details !== stop.location.name && (
                          <div className="text-xs text-ui-muted">{stop.location.details}</div>
                        )}
                        <input
                          type="text"
                          value={stop.comments || ''}
                          onChange={(e) => {
                            const updated = [...stops];
                            updated[idx] = { ...updated[idx], comments: e.target.value };
                            setStops(updated);
                          }}
                          placeholder="Add stop comment..."
                          className="mt-1 w-full px-2 py-1 border border-ui-line rounded text-xs text-ui-ink-3 focus:outline-none focus:ring-1 focus:ring-ui-primary"
                        />
                      </div>
                    </div>
                  ))}

                  {/* Dropoff */}
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-ui-red-strong flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-ui-on-primary text-xs font-bold">D</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-ui-ink">
                        {/* For FBOs, show selected FBO name if available */}
                        {isDropoffFbo && selectedFbo
                          ? selectedFbo.name
                          : (dropoffLocation?.name || 'Dropoff location')
                        }
                      </div>
                      {/* For FBOs, show selected FBO details; otherwise show dropoff details */}
                      {isDropoffFbo && selectedFbo ? (
                        selectedFbo.details && (
                          <div className="text-xs text-ui-muted">{selectedFbo.details}</div>
                        )
                      ) : (
                        dropoffLocation?.details && dropoffLocation.details !== dropoffLocation.name && (
                          <div className="text-xs text-ui-muted">{dropoffLocation.details}</div>
                        )
                      )}
                      {/* Show flight/FBO info for manual airport/FBO dropoffs when entered */}
                      {showFlightInfoPrompt && manualAirportFlightInfo.departureDate && manualAirportFlightInfo.departureTimeOfDay && (
                        <div className="text-xs text-ui-link font-medium mt-1">
                          {isDropoffFbo ? (
                            <>
                              Departure: {formatDisplayDateTime(`${manualAirportFlightInfo.departureDate}T${manualAirportFlightInfo.departureTimeOfDay}`)}
                              {manualAirportFlightInfo.tailNumber && (
                                <span className="ml-1">(Tail: {manualAirportFlightInfo.tailNumber})</span>
                              )}
                            </>
                          ) : (
                            <>
                              Flight: {manualAirportFlightInfo.airlineCode || '---'} {manualAirportFlightInfo.flightNumber || '---'} at {formatDisplayDateTime(`${manualAirportFlightInfo.departureDate}T${manualAirportFlightInfo.departureTimeOfDay}`)}
                              {manualAirportFlightInfo.otherAirport && (
                                <span className="ml-1">to {manualAirportFlightInfo.otherAirport}</span>
                              )}
                              {flightValidated && (
                                <CheckCircle className="w-3 h-3 inline ml-1 text-ui-green-fg" />
                              )}
                            </>
                          )}
                        </div>
                      )}
                      <input
                        type="text"
                        value={dropoffComments}
                        onChange={(e) => setDropoffComments(e.target.value)}
                        placeholder="Add dropoff comment..."
                        className="mt-1 w-full px-2 py-1 border border-ui-line rounded text-xs text-ui-ink-3 focus:outline-none focus:ring-1 focus:ring-ui-primary"
                      />
                    </div>
                  </div>
                </div>
                )}
              </div>

              {/* Arrival Flight/FBO Info for Manual Airport/FBO Pickup */}
              {showArrivalFlightPrompt && (
                <div className="bg-ui-blue-bg border border-ui-blue-line rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-ui-blue-fg mb-2 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/>
                    </svg>
                    {isPickupFbo ? 'FBO Arrival Information' : 'Arrival Flight Information'}
                    {!isPickupFbo && arrivalFlightValidated && (
                      <span className="ml-auto flex items-center gap-1 text-ui-green-fg text-xs">
                        <CheckCircle className="w-3 h-3" />
                        Validated
                      </span>
                    )}
                  </h4>
                  <p className="text-xs text-ui-link mb-3">
                    {isPickupFbo
                      ? (selectedPickupFbo
                          ? <>Enter arrival details for FBO pickup at <span className="font-semibold">{pickupLocation?.name || pickupLocation?.code}</span>.</>
                          : <>Please select an FBO and enter arrival details for <span className="font-semibold">{pickupLocation?.name || pickupLocation?.code}</span>.</>
                        )
                      : <>Please enter your arrival flight details for the airport pickup at <span className="font-semibold">{pickupLocation?.name || pickupLocation?.code}</span>.</>
                    }
                  </p>

                  <div className="space-y-3">
                    {/* FBO Selection - Required for FBO pickups */}
                    {isPickupFbo && (
                      <div>
                        <label className="block text-xs font-medium text-ui-blue-fg mb-1">
                          FBO {!selectedPickupFbo && <span className="text-ui-red-fg">*</span>}
                        </label>
                        {/* If FBO is already selected, just show confirmation */}
                        {selectedPickupFbo ? (
                          <div className="p-2 bg-ui-green-bg border border-ui-green-line rounded-md">
                            <p className="text-xs text-ui-green-fg flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Selected: <span className="font-medium">{selectedPickupFbo.name}</span>
                            </p>
                          </div>
                        ) : pickupFboListLoading ? (
                          <div className="flex items-center gap-2 text-xs text-ui-link py-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Loading FBOs at {pickupLocation?.code}...
                          </div>
                        ) : pickupFboListError ? (
                          <div className="p-2 bg-ui-red-bg border border-ui-red-line rounded-md">
                            <p className="text-xs text-ui-red-fg flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              {pickupFboListError}
                            </p>
                          </div>
                        ) : pickupFboList.length > 0 ? (
                          <div className="space-y-1 max-h-40 overflow-y-auto border border-ui-blue-line rounded-md">
                            {pickupFboList.map((fbo) => (
                              <button
                                key={fbo.id}
                                onClick={() => setSelectedPickupFbo(fbo)}
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-ui-blue-bg transition-colors ${
                                  selectedPickupFbo?.id === fbo.id
                                    ? 'bg-ui-blue-line border-l-4 border-ui-primary'
                                    : 'bg-ui-surface'
                                }`}
                              >
                                <div className="font-medium text-ui-ink">{fbo.name}</div>
                                {fbo.details && (
                                  <div className="text-xs text-ui-muted truncate">{fbo.details}</div>
                                )}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-ui-muted py-2">No FBOs available</p>
                        )}
                      </div>
                    )}

                    {/* Arrival Date and Time - Separate Fields */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-ui-blue-fg mb-1">
                          Arrival Date
                        </label>
                        <input
                          type="date"
                          value={arrivalFlightInfo.arrivalDate}
                          onChange={(e) => {
                            setArrivalFlightInfo(prev => ({ ...prev, arrivalDate: e.target.value }));
                            setArrivalFlightValidated(false);
                          }}
                          className="w-full px-3 py-2 border border-ui-blue-line rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-ui-blue-fg mb-1">
                          Arrival Time
                        </label>
                        <input
                          type="time"
                          value={arrivalFlightInfo.arrivalTimeOfDay}
                          onChange={(e) => {
                            setArrivalFlightInfo(prev => ({ ...prev, arrivalTimeOfDay: e.target.value }));
                            setArrivalFlightValidated(false);
                          }}
                          className="w-full px-3 py-2 border border-ui-blue-line rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                        />
                      </div>
                    </div>
                    <p className="text-[10px] text-ui-link">
                      {isPickupFbo
                        ? 'Enter the expected arrival time at the FBO.'
                        : 'This is the scheduled arrival time of the flight. The chauffeur will track the flight and be ready when you land.'
                      }
                    </p>

                    {/* FBO: Tail Number */}
                    {isPickupFbo && (
                      <div>
                        <label className="block text-xs font-medium text-ui-blue-fg mb-1">
                          Tail Number (optional)
                        </label>
                        <input
                          type="text"
                          value={arrivalFlightInfo.tailNumber}
                          onChange={(e) => setArrivalFlightInfo(prev => ({ ...prev, tailNumber: e.target.value.toUpperCase() }))}
                          placeholder="e.g. N12345"
                          maxLength={10}
                          className="w-full px-3 py-2 border border-ui-blue-line rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm uppercase"
                        />
                      </div>
                    )}

                    {/* Airport: Airline Code and Flight Number */}
                    {!isPickupFbo && (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-ui-blue-fg mb-1">
                              Airline Code
                            </label>
                            <input
                              type="text"
                              value={arrivalFlightInfo.airlineCode}
                              onChange={(e) => {
                                setArrivalFlightInfo(prev => ({ ...prev, airlineCode: e.target.value.toUpperCase() }));
                                setArrivalFlightValidated(false);
                              }}
                              placeholder="e.g. AA, UA, DL"
                              maxLength={3}
                              className="w-full px-3 py-2 border border-ui-blue-line rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm uppercase"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-ui-blue-fg mb-1">
                              Flight Number
                            </label>
                            <input
                              type="text"
                              value={arrivalFlightInfo.flightNumber}
                              onChange={(e) => {
                                setArrivalFlightInfo(prev => ({ ...prev, flightNumber: e.target.value }));
                                setArrivalFlightValidated(false);
                              }}
                              placeholder="e.g. 1234"
                              maxLength={6}
                              className="w-full px-3 py-2 border border-ui-blue-line rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                            />
                          </div>
                        </div>

                        {/* Validate Button */}
                        <div className="flex items-center gap-3">
                          <button
                            onClick={handleValidateArrivalFlight}
                            disabled={arrivalFlightValidating || !arrivalFlightInfo.airlineCode || !arrivalFlightInfo.flightNumber || !arrivalFlightInfo.arrivalDate}
                            className="px-4 py-2 bg-ui-primary text-ui-on-primary rounded-md hover:bg-ui-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center gap-2"
                          >
                            {arrivalFlightValidating ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Validating...
                              </>
                            ) : (
                              <>
                                <Search className="w-4 h-4" />
                                Validate Flight
                              </>
                            )}
                          </button>
                          {arrivalFlightValidated && arrivalFlightInfo.originAirport && (
                            <span className="text-xs text-ui-green-fg flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" />
                              Arriving from {arrivalFlightInfo.originAirport}
                            </span>
                          )}
                        </div>

                        {/* Validation Error */}
                        {arrivalFlightValidationError && (
                          <div className="p-2 bg-ui-red-bg border border-ui-red-line rounded-md">
                            <p className="text-xs text-ui-red-fg flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              {arrivalFlightValidationError}
                            </p>
                          </div>
                        )}

                        <p className="text-[10px] text-ui-link">
                          Validating the flight populates the origin airport and verifies the arrival time.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Flight/FBO Info for Manual Airport/FBO Dropoff */}
              {showFlightInfoPrompt && (
                <div className="bg-ui-amber-bg border border-ui-amber-line rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-ui-amber-fg mb-2 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/>
                    </svg>
                    {isDropoffFbo ? 'FBO Departure Information' : 'Flight Information'}
                    {!isDropoffFbo && flightValidated && (
                      <span className="ml-auto flex items-center gap-1 text-ui-green-fg text-xs">
                        <CheckCircle className="w-3 h-3" />
                        Validated
                      </span>
                    )}
                  </h4>
                  <p className="text-xs text-ui-amber-fg mb-3">
                    {isDropoffFbo
                      ? (selectedFbo
                          ? <>Enter departure details for FBO dropoff at <span className="font-semibold">{dropoffLocation?.name || dropoffLocation?.code}</span>.</>
                          : <>Please select an FBO and enter departure details for <span className="font-semibold">{dropoffLocation?.name || dropoffLocation?.code}</span>.</>
                        )
                      : <>Please enter your departure flight details for the airport dropoff at <span className="font-semibold">{dropoffLocation?.name || dropoffLocation?.code}</span>.</>
                    }
                  </p>

                  <div className="space-y-3">
                    {/* FBO Selection - Required for FBO dropoffs */}
                    {isDropoffFbo && (
                      <div>
                        <label className="block text-xs font-medium text-ui-amber-fg mb-1">
                          FBO {!selectedFbo && <span className="text-ui-red-fg">*</span>}
                        </label>
                        {/* If FBO is already selected, just show confirmation */}
                        {selectedFbo ? (
                          <div className="p-2 bg-ui-green-bg border border-ui-green-line rounded-md">
                            <p className="text-xs text-ui-green-fg flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Selected: <span className="font-medium">{selectedFbo.name}</span>
                            </p>
                          </div>
                        ) : fboListLoading ? (
                          <div className="flex items-center gap-2 text-xs text-ui-amber-fg py-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Loading FBOs at {dropoffLocation?.code}...
                          </div>
                        ) : fboListError ? (
                          <div className="p-2 bg-ui-red-bg border border-ui-red-line rounded-md">
                            <p className="text-xs text-ui-red-fg flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              {fboListError}
                            </p>
                          </div>
                        ) : fboList.length > 0 ? (
                          <div className="space-y-1 max-h-40 overflow-y-auto border border-ui-amber-line rounded-md">
                            {fboList.map((fbo) => (
                              <button
                                key={fbo.id}
                                onClick={() => setSelectedFbo(fbo)}
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-ui-amber-bg transition-colors ${
                                  selectedFbo?.id === fbo.id
                                    ? 'bg-ui-amber-line border-l-4 border-ui-amber-solid'
                                    : 'bg-ui-surface'
                                }`}
                              >
                                <div className="font-medium text-ui-ink">{fbo.name}</div>
                                {fbo.details && (
                                  <div className="text-xs text-ui-muted truncate">{fbo.details}</div>
                                )}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-ui-muted py-2">No FBOs available</p>
                        )}
                      </div>
                    )}

                    {/* Departure Date and Time - Separate Fields */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-ui-amber-fg mb-1">
                          Departure Date
                        </label>
                        <input
                          type="date"
                          value={manualAirportFlightInfo.departureDate}
                          onChange={(e) => {
                            setManualAirportFlightInfo(prev => ({ ...prev, departureDate: e.target.value }));
                            setFlightValidated(false);
                          }}
                          className="w-full px-3 py-2 border border-ui-amber-line rounded-md focus:outline-none focus:ring-2 focus:ring-ui-amber-solid text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-ui-amber-fg mb-1">
                          Departure Time
                        </label>
                        <input
                          type="time"
                          value={manualAirportFlightInfo.departureTimeOfDay}
                          onChange={(e) => {
                            setManualAirportFlightInfo(prev => ({ ...prev, departureTimeOfDay: e.target.value }));
                            setFlightValidated(false);
                          }}
                          className="w-full px-3 py-2 border border-ui-amber-line rounded-md focus:outline-none focus:ring-2 focus:ring-ui-amber-solid text-sm"
                        />
                      </div>
                    </div>
                    <p className="text-[10px] text-ui-amber-fg">
                      {isDropoffFbo
                        ? 'If left blank, departure time defaults to pickup time + 2 hours.'
                        : 'This is used to calculate the pickup time for a timely arrival at the airport.'
                      }
                    </p>

                    {/* FBO: Tail Number */}
                    {isDropoffFbo && (
                      <div>
                        <label className="block text-xs font-medium text-ui-amber-fg mb-1">
                          Tail Number (optional)
                        </label>
                        <input
                          type="text"
                          value={manualAirportFlightInfo.tailNumber}
                          onChange={(e) => setManualAirportFlightInfo(prev => ({ ...prev, tailNumber: e.target.value.toUpperCase() }))}
                          placeholder="e.g. N12345"
                          maxLength={10}
                          className="w-full px-3 py-2 border border-ui-amber-line rounded-md focus:outline-none focus:ring-2 focus:ring-ui-amber-solid text-sm uppercase"
                        />
                      </div>
                    )}

                    {/* Airport: Airline Code and Flight Number */}
                    {!isDropoffFbo && (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-ui-amber-fg mb-1">
                              Airline Code
                            </label>
                            <input
                              type="text"
                              value={manualAirportFlightInfo.airlineCode}
                              onChange={(e) => {
                                setManualAirportFlightInfo(prev => ({ ...prev, airlineCode: e.target.value.toUpperCase() }));
                                setFlightValidated(false);
                              }}
                              placeholder="e.g. AA, UA, DL"
                              maxLength={3}
                              className="w-full px-3 py-2 border border-ui-amber-line rounded-md focus:outline-none focus:ring-2 focus:ring-ui-amber-solid text-sm uppercase"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-ui-amber-fg mb-1">
                              Flight Number
                            </label>
                            <input
                              type="text"
                              value={manualAirportFlightInfo.flightNumber}
                              onChange={(e) => {
                                setManualAirportFlightInfo(prev => ({ ...prev, flightNumber: e.target.value }));
                                setFlightValidated(false);
                              }}
                              placeholder="e.g. 1234"
                              maxLength={6}
                              className="w-full px-3 py-2 border border-ui-amber-line rounded-md focus:outline-none focus:ring-2 focus:ring-ui-amber-solid text-sm"
                            />
                          </div>
                        </div>

                        {/* Validate Button */}
                        <div className="flex items-center gap-3">
                          <button
                            onClick={handleValidateFlight}
                            disabled={flightValidating || !manualAirportFlightInfo.airlineCode || !manualAirportFlightInfo.flightNumber || !manualAirportFlightInfo.departureDate}
                            className="px-4 py-2 bg-ui-amber-strong text-ui-on-primary rounded-md hover:bg-ui-amber-strong/90 disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center gap-2"
                          >
                            {flightValidating ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Validating...
                              </>
                            ) : (
                              <>
                                <Search className="w-4 h-4" />
                                Validate Flight
                              </>
                            )}
                          </button>
                          {flightValidated && manualAirportFlightInfo.otherAirport && (
                            <span className="text-xs text-ui-green-fg flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" />
                              Flying to {manualAirportFlightInfo.otherAirport}
                            </span>
                          )}
                        </div>

                        {/* Validation Error */}
                        {flightValidationError && (
                          <div className="p-2 bg-ui-red-bg border border-ui-red-line rounded-md">
                            <p className="text-xs text-ui-red-fg flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              {flightValidationError}
                            </p>
                          </div>
                        )}

                        <p className="text-[10px] text-ui-amber-fg">
                          Validating the flight populates the destination airport and verifies the departure time.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Train Station Departure Information */}
              {isDropoffTrainStation && (
                <div className="bg-ui-amber-bg border border-ui-amber-line rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-ui-amber-fg mb-2 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="4" y="3" width="16" height="16" rx="2"/>
                      <path d="M4 11h16"/>
                      <path d="M12 3v8"/>
                      <path d="m8 19-2 3"/>
                      <path d="m18 22-2-3"/>
                      <path d="M8 15h0"/>
                      <path d="M16 15h0"/>
                    </svg>
                    Train Departure Information
                  </h4>
                  <p className="text-xs text-ui-amber-fg mb-3">
                    Enter departure details for train station dropoff at <span className="font-semibold">{dropoffLocation?.name || 'train station'}</span>.
                  </p>

                  <div className="space-y-3">
                    {/* Railroad Search */}
                    <div className="relative">
                      <label className="block text-xs font-medium text-ui-amber-fg mb-1">
                        Railroad (optional)
                      </label>
                      {trainStationInfo.railroad ? (
                        <div className="flex items-center gap-2 p-2 bg-ui-green-bg border border-ui-green-line rounded-md">
                          <p className="text-xs text-ui-green-fg flex-1 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            <span className="font-medium">{trainStationInfo.railroadName}</span>
                            <span className="text-ui-green-fg">({trainStationInfo.railroad})</span>
                          </p>
                          <button
                            onClick={handleClearRailroad}
                            className="text-ui-green-fg hover:text-ui-green-fg"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="relative">
                            <input
                              type="text"
                              value={railroadSearchQuery}
                              onChange={(e) => handleRailroadSearch(e.target.value)}
                              onFocus={() => railroadSearchQuery && setShowRailroadDropdown(true)}
                              placeholder="Search for railroad (e.g., Amtrak, NJ Transit)"
                              className="w-full px-3 py-2 border border-ui-amber-line rounded-md focus:outline-none focus:ring-2 focus:ring-ui-amber-solid text-sm"
                            />
                            {railroadSearching && (
                              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                <Loader2 className="w-4 h-4 animate-spin text-ui-amber-fg" />
                              </div>
                            )}
                          </div>
                          {showRailroadDropdown && railroadSearchResults.length > 0 && (
                            <div className="absolute z-10 mt-1 w-full bg-ui-surface border border-ui-amber-line rounded-md shadow-lg max-h-40 overflow-y-auto">
                              {railroadSearchResults.map((railroad) => (
                                <button
                                  key={railroad.code}
                                  onClick={() => handleSelectRailroad(railroad)}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-ui-amber-bg transition-colors"
                                >
                                  <span className="font-medium text-ui-ink">{railroad.name}</span>
                                  <span className="text-ui-muted ml-2">({railroad.code})</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Train Number */}
                    <div>
                      <label className="block text-xs font-medium text-ui-amber-fg mb-1">
                        Train Number (optional)
                      </label>
                      <input
                        type="text"
                        value={trainStationInfo.trainNumber}
                        onChange={(e) => setTrainStationInfo(prev => ({ ...prev, trainNumber: e.target.value }))}
                        placeholder="e.g., 123"
                        className="w-full px-3 py-2 border border-ui-amber-line rounded-md focus:outline-none focus:ring-2 focus:ring-ui-amber-solid text-sm"
                      />
                    </div>

                    {/* Departure Date and Time */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-ui-amber-fg mb-1">
                          Departure Date
                        </label>
                        <input
                          type="date"
                          value={trainStationInfo.departureDate}
                          onChange={(e) => setTrainStationInfo(prev => ({ ...prev, departureDate: e.target.value }))}
                          className="w-full px-3 py-2 border border-ui-amber-line rounded-md focus:outline-none focus:ring-2 focus:ring-ui-amber-solid text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-ui-amber-fg mb-1">
                          Departure Time (optional)
                        </label>
                        <input
                          type="time"
                          value={trainStationInfo.departureTime}
                          onChange={(e) => setTrainStationInfo(prev => ({ ...prev, departureTime: e.target.value }))}
                          className="w-full px-3 py-2 border border-ui-amber-line rounded-md focus:outline-none focus:ring-2 focus:ring-ui-amber-solid text-sm"
                        />
                      </div>
                    </div>
                    <p className="text-[10px] text-ui-amber-fg">
                      If departure time is left blank, it defaults to pickup time + 2 hours.
                    </p>
                  </div>
                </div>
              )}

              {/* Pickup Time Selector for Hotel Pickups */}
              {showPickupTimePrompt && (
                <div className="bg-ui-blue-bg border border-ui-blue-line rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-ui-blue-fg mb-2 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Select Pickup Time
                  </h4>

                  {/* Show loading indicator while calculating travel time */}
                  {calculatingTravelTime && (
                    <div className="flex items-center gap-2 text-xs text-ui-link mb-3">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Calculating travel time to destination...
                    </div>
                  )}

                  {/* Show estimated travel time info when calculated */}
                  {estimatedTravelTime && !calculatingTravelTime && (
                    <div className="bg-ui-green-bg border border-ui-green-line rounded-md p-3 mb-3">
                      <div className="text-xs text-ui-green-fg">
                        <div className="font-semibold mb-1 flex items-center gap-2">
                          Suggested pickup time calculated
                          {estimatedTravelTime.usedTrafficData && (
                            <span className="px-1.5 py-0.5 bg-ui-green-line text-ui-green-fg rounded text-[10px] font-medium">
                              Traffic-aware
                            </span>
                          )}
                        </div>
                        <div className="space-y-1">
                          <div>
                            Estimated drive time: <span className="font-medium">{estimatedTravelTime.durationText}</span>
                            {estimatedTravelTime.distanceText && (
                              <span className="text-ui-green-fg ml-1">({estimatedTravelTime.distanceText})</span>
                            )}
                            {estimatedTravelTime.usedTrafficData && (
                              <span className="text-ui-green-fg ml-1">
                                (based on {format(new Date(estimatedTravelTime.suggestedPickupTime), 'EEE')} traffic)
                              </span>
                            )}
                          </div>
                          {/* For non-flights, show rounded drive time */}
                          {!estimatedTravelTime.isAirport && (
                            <div>
                              Rounded up to: <span className="font-medium">{estimatedTravelTime.roundedDurationMinutes} minutes</span>
                            </div>
                          )}
                          {/* Show airport processing time for flights */}
                          {estimatedTravelTime.isAirport && estimatedTravelTime.airportProcessingMinutes > 0 && (
                            <div>
                              Airport check-in buffer: <span className="font-medium">{estimatedTravelTime.airportProcessingMinutes} minutes</span>
                              <span className="text-ui-green-fg ml-1">
                                ({estimatedTravelTime.destinationType === 'flight (private)' ? 'FBO processing' :
                                  estimatedTravelTime.airportProcessingMinutes === 120 ? 'international' : 'domestic'})
                              </span>
                            </div>
                          )}
                          <div>
                            {estimatedTravelTime.isAirport ? (
                              <>
                                Flight departs at:{' '}
                                <span className="font-medium">
                                  {format(new Date(estimatedTravelTime.destinationStartTime), 'h:mm a')}
                                </span>
                                {estimatedTravelTime.flightNumber && (
                                  <span className="text-ui-green-fg ml-1">
                                    ({estimatedTravelTime.airportCode} - {estimatedTravelTime.flightNumber})
                                  </span>
                                )}
                              </>
                            ) : (
                              <>
                                {estimatedTravelTime.destinationType.charAt(0).toUpperCase() + estimatedTravelTime.destinationType.slice(1)} starts at:{' '}
                                <span className="font-medium">
                                  {format(new Date(estimatedTravelTime.destinationStartTime), 'h:mm a')}
                                </span>
                              </>
                            )}
                          </div>
                          {/* For flights, note that pickup time is rounded to nearest 15 minutes */}
                          {estimatedTravelTime.isAirport && (
                            <div className="text-ui-green-fg italic">
                              Pickup time rounded to nearest 15 minutes
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Show error if travel time calculation failed */}
                  {travelTimeError && !calculatingTravelTime && (
                    <div className="bg-ui-amber-bg border border-ui-amber-line rounded-md p-2 mb-3">
                      <p className="text-xs text-ui-amber-fg">
                        Could not calculate travel time: {travelTimeError}. Please select a pickup time manually.
                      </p>
                    </div>
                  )}

                  {/* Show default message if no travel time info */}
                  {!estimatedTravelTime && !calculatingTravelTime && !travelTimeError && (
                    <p className="text-xs text-ui-link mb-3">
                      {pickupLocation?.isHotel
                        ? 'Please specify when you\'d like to be picked up from the hotel. The hotel checkout time is not used as the default pickup time.'
                        : 'Please specify when you\'d like to be picked up from this location.'}
                    </p>
                  )}

                  <input
                    type="datetime-local"
                    value={customPickupTime}
                    onChange={(e) => {
                      setCustomPickupTime(e.target.value);
                      // Clear estimated travel time when user manually changes the time
                      if (estimatedTravelTime) {
                        setEstimatedTravelTime(null);
                      }
                    }}
                    disabled={calculatingTravelTime}
                    className="w-full px-3 py-2 border border-ui-blue-line rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm bg-ui-surface disabled:bg-ui-hover disabled:cursor-not-allowed"
                  />

                  {/* Show note about adjusting the time */}
                  {estimatedTravelTime && (
                    <p className="text-xs text-ui-green-fg mt-2">
                      You can adjust this time if needed.
                    </p>
                  )}
                </div>
              )}

              {/* Passengers */}
              <div className="bg-ui-sunk rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-ui-ink flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Passengers
                  </h4>
                  <button
                    onClick={() => showAddPassengerForm ? resetAddPassengerForm() : setShowAddPassengerForm(true)}
                    className="text-xs text-ui-link hover:text-ui-link font-medium flex items-center gap-1"
                  >
                    {showAddPassengerForm ? (
                      <>
                        <X className="w-3 h-3" />
                        Cancel
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-3 h-3" />
                        Add Passenger
                      </>
                    )}
                  </button>
                </div>

                {/* Add Passenger Form with Smart Lookup */}
                {showAddPassengerForm && (
                  <div className="bg-ui-surface border border-ui-blue-line rounded-lg p-3 mb-3">
                    {/* Name Fields with Search Button */}
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        placeholder="First Name *"
                        value={newPassenger.firstName}
                        onChange={(e) => setNewPassenger({ ...newPassenger, firstName: e.target.value })}
                        className="flex-1 px-2 py-1.5 text-sm border border-ui-control rounded focus:outline-none focus:ring-1 focus:ring-ui-primary"
                      />
                      <input
                        type="text"
                        placeholder="Last Name *"
                        value={newPassenger.lastName}
                        onChange={(e) => setNewPassenger({ ...newPassenger, lastName: e.target.value })}
                        className="flex-1 px-2 py-1.5 text-sm border border-ui-control rounded focus:outline-none focus:ring-1 focus:ring-ui-primary"
                      />
                      <button
                        onClick={() => lookupPassenger(newPassenger.firstName, newPassenger.lastName)}
                        disabled={!newPassenger.firstName.trim() || !newPassenger.lastName.trim() || passengerLookupState === 'searching'}
                        className="px-3 py-1.5 bg-ui-hover text-ui-ink-2 text-xs rounded hover:bg-ui-line disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        {passengerLookupState === 'searching' ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Search className="w-3 h-3" />
                        )}
                        Lookup
                      </button>
                    </div>

                    {/* Lookup Status */}
                    {passengerLookupState === 'searching' && (
                      <div className="flex items-center gap-2 text-xs text-ui-muted mb-2 p-2 bg-ui-sunk rounded">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Searching for existing profile...
                      </div>
                    )}

                    {/* Executive with Urbanride Profile Found */}
                    {passengerLookupState === 'found_executive' && selectedUrbanrideProfile && (
                      <div className="p-2 bg-ui-green-bg border border-ui-green-line rounded mb-2">
                        <div className="flex items-center gap-2 text-xs text-ui-green-fg">
                          <Check className="w-4 h-4 text-ui-green-fg" />
                          <div>
                            <span className="font-medium">Executive profile found with Urbanride ID</span>
                            <span className="ml-2 px-1.5 py-0.5 bg-ui-green-bg rounded text-ui-green-fg">
                              #{selectedUrbanrideProfile.id}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Executive Found but No Urbanride Profile - Show Urbanride Search Results */}
                    {passengerLookupState === 'found_urbanride' && matchedExecutive && (
                      <div className="mb-2">
                        <div className="p-2 bg-ui-blue-bg border border-ui-blue-line rounded mb-2">
                          <div className="flex items-center gap-2 text-xs text-ui-link">
                            <Check className="w-4 h-4 text-ui-link" />
                            <span className="font-medium">Executive found: {matchedExecutive.first_name} {matchedExecutive.last_name}</span>
                            <span className="text-ui-link">(no Urbanride profile)</span>
                          </div>
                        </div>
                        <div className="text-xs text-ui-ink-3 mb-1">Select an Urbanride profile to link:</div>
                        <div className="max-h-32 overflow-y-auto border border-ui-line rounded">
                          {urbanrideSearchResults.map((profile) => (
                            <button
                              key={profile.id}
                              onClick={() => selectUrbanrideProfile(profile)}
                              className={`w-full px-2 py-1.5 text-left text-xs border-b border-ui-divider last:border-b-0 hover:bg-ui-hover ${
                                selectedUrbanrideProfile?.id === profile.id ? 'bg-ui-blue-bg border-ui-blue-line' : ''
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-medium">
                                  {profile.firstName} {profile.lastName}
                                  {profile.email && <span className="text-ui-muted ml-1">({profile.email})</span>}
                                </span>
                                <span className="text-ui-muted">#{profile.id}</span>
                              </div>
                            </button>
                          ))}
                        </div>
                        <button
                          onClick={() => setSelectedUrbanrideProfile(null)}
                          className={`w-full mt-1 px-2 py-1.5 text-left text-xs border border-ui-line rounded hover:bg-ui-hover ${
                            selectedUrbanrideProfile === null && passengerLookupState !== 'idle' ? 'bg-ui-amber-bg border-ui-amber-line' : ''
                          }`}
                        >
                          <span className="text-ui-ink-3">Create new Urbanride profile</span>
                        </button>
                      </div>
                    )}

                    {/* No Executive - Show Urbanride Search Results */}
                    {passengerLookupState === 'found_urbanride' && !matchedExecutive && (
                      <div className="mb-2">
                        <div className="text-xs text-ui-ink-3 mb-1">Select an existing Urbanride profile or create new:</div>
                        <div className="max-h-32 overflow-y-auto border border-ui-line rounded">
                          {urbanrideSearchResults.map((profile) => (
                            <button
                              key={profile.id}
                              onClick={() => selectUrbanrideProfile(profile)}
                              className={`w-full px-2 py-1.5 text-left text-xs border-b border-ui-divider last:border-b-0 hover:bg-ui-hover ${
                                selectedUrbanrideProfile?.id === profile.id ? 'bg-ui-blue-bg border-ui-blue-line' : ''
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-medium">
                                  {profile.firstName} {profile.lastName}
                                  {profile.email && <span className="text-ui-muted ml-1">({profile.email})</span>}
                                </span>
                                <span className="text-ui-muted">#{profile.id}</span>
                              </div>
                            </button>
                          ))}
                        </div>
                        <button
                          onClick={() => setSelectedUrbanrideProfile(null)}
                          className={`w-full mt-1 px-2 py-1.5 text-left text-xs border border-ui-line rounded hover:bg-ui-hover ${
                            selectedUrbanrideProfile === null && passengerLookupState !== 'idle' ? 'bg-ui-amber-bg border-ui-amber-line' : ''
                          }`}
                        >
                          <span className="text-ui-ink-3">Create new Urbanride profile</span>
                        </button>
                      </div>
                    )}

                    {/* Not Found - Will Create New */}
                    {passengerLookupState === 'not_found' && (
                      <div className="p-2 bg-ui-amber-bg border border-ui-amber-line rounded mb-2">
                        <div className="flex items-center gap-2 text-xs text-ui-amber-fg">
                          <AlertCircle className="w-4 h-4 text-ui-amber-fg" />
                          <span>No existing Urbanride profile found. A new profile will be created.</span>
                        </div>
                      </div>
                    )}

                    {/* Optional Email/Mobile Fields */}
                    {(passengerLookupState === 'not_found' || (passengerLookupState !== 'idle' && passengerLookupState !== 'searching' && !selectedUrbanrideProfile)) && (
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <input
                          type="email"
                          placeholder="Email (optional)"
                          value={newPassenger.email}
                          onChange={(e) => setNewPassenger({ ...newPassenger, email: e.target.value })}
                          className="px-2 py-1.5 text-sm border border-ui-control rounded focus:outline-none focus:ring-1 focus:ring-ui-primary"
                        />
                        <input
                          type="tel"
                          placeholder="Mobile (optional)"
                          value={newPassenger.mobile}
                          onChange={(e) => setNewPassenger({ ...newPassenger, mobile: e.target.value })}
                          className="px-2 py-1.5 text-sm border border-ui-control rounded focus:outline-none focus:ring-1 focus:ring-ui-primary"
                        />
                      </div>
                    )}

                    {/* Extra Guests and Add Button */}
                    <div className="flex items-center justify-between pt-2 border-t border-ui-divider">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-ui-ink-3">Extra guests:</span>
                        <input
                          type="number"
                          min="0"
                          value={newPassenger.extra}
                          onChange={(e) => setNewPassenger({ ...newPassenger, extra: Math.max(0, parseInt(e.target.value) || 0) })}
                          className="w-14 px-2 py-1 text-sm border border-ui-control rounded focus:outline-none focus:ring-1 focus:ring-ui-primary text-center"
                        />
                      </div>
                      <button
                        onClick={addNewPassenger}
                        disabled={!newPassenger.firstName.trim() || !newPassenger.lastName.trim() || passengerLookupState === 'searching' || savingToExecutive}
                        className="px-3 py-1.5 bg-ui-primary text-ui-on-primary text-xs rounded hover:bg-ui-primary-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        {savingToExecutive ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          'Add Passenger'
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {passengers.length > 0 ? (
                  <div className="space-y-2">
                    {passengers.map((p, idx) => (
                      <div key={idx} className="bg-ui-surface p-2 rounded border border-ui-line">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-ui-blue-bg rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-ui-link text-xs font-semibold">
                                {(p.firstName || p.name)?.[0]?.toUpperCase() || '?'}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm text-ui-ink">
                                {p.name || `${p.firstName} ${p.lastName}`}
                                {p.isExecutive && (
                                  <span className="ml-2 px-1.5 py-0.5 bg-ui-blue-bg text-ui-link text-xs rounded">Executive</span>
                                )}
                                {p.urbanride_pax_id && (
                                  <span className="ml-1 px-1.5 py-0.5 bg-ui-green-bg text-ui-green-fg text-xs rounded">UR</span>
                                )}
                              </div>
                              {p.email && <div className="text-xs text-ui-muted truncate">{p.email}</div>}
                            </div>
                          </div>
                          <button
                            onClick={() => removePassenger(idx)}
                            className="text-ui-red-fg hover:text-ui-red-fg p-1 flex-shrink-0"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        {/* Extra Guests Row */}
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-ui-divider">
                          <span className="text-xs text-ui-ink-3">Additional guests:</span>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => updatePassengerExtra(idx, (p.extra || 0) - 1)}
                              disabled={(p.extra || 0) <= 0}
                              className="w-6 h-6 flex items-center justify-center rounded bg-ui-line hover:bg-ui-control disabled:opacity-50 disabled:cursor-not-allowed text-ui-ink-2"
                            >
                              <span className="text-sm font-bold">−</span>
                            </button>
                            <span className="w-8 text-center text-sm font-medium text-ui-ink">
                              {p.extra || 0}
                            </span>
                            <button
                              onClick={() => updatePassengerExtra(idx, (p.extra || 0) + 1)}
                              className="w-6 h-6 flex items-center justify-center rounded bg-ui-line hover:bg-ui-control text-ui-ink-2"
                            >
                              <span className="text-sm font-bold">+</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-ui-muted text-center py-2">
                    No passengers added yet. Click "Add Passenger" to add travelers.
                  </div>
                )}

                {/* Total passenger count */}
                {passengers.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-ui-line text-xs text-ui-ink-3 text-right">
                    Total: {passengers.length} named passenger{passengers.length !== 1 ? 's' : ''}
                    {passengers.reduce((sum, p) => sum + (p.extra || 0), 0) > 0 && (
                      <> + {passengers.reduce((sum, p) => sum + (p.extra || 0), 0)} additional guest{passengers.reduce((sum, p) => sum + (p.extra || 0), 0) !== 1 ? 's' : ''}</>
                    )}
                  </div>
                )}
              </div>

              {/* Comments */}
              <div className="bg-ui-sunk rounded-lg p-4">
                <label className="block text-sm font-semibold text-ui-ink mb-2">
                  Special Instructions (optional)
                </label>
                <textarea
                  value={clientComments}
                  onChange={(e) => setClientComments(e.target.value)}
                  rows={2}
                  placeholder="Any special requests for the driver..."
                  className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                />
              </div>
            </div>
          )}

          {/* Step 2: Select Vehicle */}
          {step === 2 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-md font-semibold text-ui-ink">Select Vehicle</h4>
                <span className="text-xs text-ui-muted">
                  {totalPassengerCount} passenger{totalPassengerCount !== 1 ? 's' : ''} total
                </span>
              </div>

              {filteredOutCount > 0 && (
                <div className="mb-3 p-2 bg-ui-amber-bg border border-ui-amber-line rounded-lg">
                  <div className="flex items-center gap-2 text-xs text-ui-amber-fg">
                    <AlertCircle className="w-4 h-4 text-ui-amber-fg" />
                    <span>
                      {filteredOutCount} vehicle{filteredOutCount !== 1 ? 's' : ''} hidden due to insufficient capacity for {totalPassengerCount} passengers.
                    </span>
                  </div>
                </div>
              )}

              {availableRates.length === 0 ? (
                <div className="text-center py-8 text-ui-muted">
                  {rates.length === 0
                    ? 'No vehicles available for this route.'
                    : `No vehicles available with capacity for ${totalPassengerCount} passengers.`}
                </div>
              ) : (
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {availableRates.map((rate, idx) => (
                    <div
                      key={idx}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedRate?.vehicleId === rate.vehicleId
                          ? 'border-ui-primary bg-ui-blue-bg'
                          : 'border-ui-line hover:border-ui-control'
                      }`}
                      onClick={() => setSelectedRate(rate)}
                    >
                      <div className="flex items-start gap-3">
                        {rate.vehicleImageUrl && (
                          <img
                            src={rate.vehicleImageUrl}
                            alt={rate.vehicle}
                            className="w-20 h-14 object-contain"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h5 className="font-semibold text-ui-ink text-sm">{rate.vehicle}</h5>
                            <div className="text-lg font-bold text-ui-link">{rate.rate}</div>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-ui-ink-3 mt-1">
                            <span>Capacity: {rate.capacity}</span>
                            <span>Luggage: {rate.luggage}</span>
                          </div>
                          {rate.cancellationPolicy && (
                            <div className="text-xs text-ui-muted mt-1 truncate">
                              {rate.cancellationPolicy}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && bookingResult && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-ui-green-bg rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-ui-green-fg" />
              </div>

              <h3 className="text-xl font-semibold text-ui-ink mb-2">{editMode ? 'Booking Updated!' : 'Booking Confirmed!'}</h3>
              <p className="text-ui-ink-3 mb-4">
                {editMode
                  ? 'Your chauffeur service reservation has been updated successfully.'
                  : 'Your chauffeur service has been booked and linked to the selected itinerary items.'}
              </p>

              <div className="bg-ui-sunk rounded-lg p-4 inline-block mb-4">
                <div className="text-sm text-ui-muted">Confirmation Number</div>
                <div className="text-2xl font-bold text-ui-ink">{bookingResult.urcn}</div>
              </div>

              {apiMode === 'test' && (
                <div className="mb-4 p-3 bg-ui-amber-bg border border-ui-amber-line text-ui-amber-fg rounded-md text-sm">
                  This is a TEST booking and will not result in an actual reservation.
                </div>
              )}

              {addingToTrip && (
                <div className="mb-4 p-3 bg-ui-blue-bg border border-ui-blue-line text-ui-link rounded-md text-sm flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {editMode ? 'Saving changes to trip...' : 'Updating trip components...'}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`${sidePanel ? 'px-4 py-3' : 'px-6 py-4'} border-t border-ui-line flex justify-between bg-ui-sunk`}>
          {step === 1 && (
            <>
              <button
                onClick={onCancel}
                className="px-4 py-2 border border-ui-control rounded-md hover:bg-ui-hover text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleGetRates}
                disabled={ratesLoading || !pickupLocation || !dropoffLocation}
                className="px-6 py-2 bg-ui-primary text-ui-on-primary rounded-md hover:bg-ui-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center gap-2"
              >
                {ratesLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Getting Rates...
                  </>
                ) : (
                  <>
                    Get Rates
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2 border border-ui-control rounded-md hover:bg-ui-hover text-sm"
              >
                Back
              </button>
              <button
                onClick={editMode ? handleUpdateTripSubmit : handleBookTrip}
                disabled={!selectedRate || bookingLoading}
                className="px-6 py-2 bg-ui-primary text-ui-on-primary rounded-md hover:bg-ui-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center gap-2"
              >
                {bookingLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {editMode ? 'Updating...' : 'Booking...'}
                  </>
                ) : (
                  editMode ? 'Confirm Update' : 'Confirm Booking'
                )}
              </button>
            </>
          )}

          {step === 3 && (
            <div className="w-full flex justify-center">
              <button
                onClick={handleDone}
                className="px-6 py-2 bg-ui-primary text-ui-on-primary rounded-md hover:bg-ui-primary-hover"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UrbanrideQuickBooking;
